import { useState, useRef, useEffect, useCallback, useMemo } from 'react'
// (Enter avança campos via enterAdvance; observação MAIÚSCULA, e-mail minúsculo no cadastro rápido)
// MERGE: base é o PDV do Nexus (multi-pagamento, espera, cliente, tema, etc.) +
// modal industrial de grade/unidade/QUALIDADE da Velore (IBK) reinserido.
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  Search, Trash2, Plus, Minus, ShoppingCart, CreditCard, Banknote,
  Smartphone, QrCode, X, ArrowLeft, Receipt, Loader2, Check, Printer,
  FileText, RotateCcw, Warehouse, Lock, Unlock, ArrowDownToLine, ArrowUpFromLine,
  DollarSign, LayoutGrid, List, Image as ImageIcon, User, Clock, Package,
  AlertTriangle, MoreVertical, UserPlus, Sun, Moon, Bike, Wallet, HandCoins, RefreshCw, Wrench,
  HelpCircle, Keyboard, ChevronUp, ChevronDown, Ban, Store, Link2, Pencil, History, Eye,
} from 'lucide-react'
async function forcarAtualizacaoApp() {
  try {
    if ('serviceWorker' in navigator) {
      const rs = await navigator.serviceWorker.getRegistrations()
      await Promise.all(rs.map(r => r.unregister()))
    }
    if (window.caches) {
      const ks = await caches.keys()
      await Promise.all(ks.map(k => caches.delete(k)))
    }
  } catch { /* recarrega mesmo assim */ }
  window.location.reload()
}
import toast from 'react-hot-toast'
import { api, formatMoney } from '@/lib/api'
import { usePdfPreview } from '@/components/PdfPreviewModal'
import { formaVisual } from '@/lib/formaVisual'
// Modal de cadastro de cliente agora é compartilhado (Rápido/Completo); antes era
// uma cópia local nesta página (removida 30/06 p/ dedup + ganhar os 2 modos).
import ModalNovoCliente from '@/components/ModalNovoCliente'
import EditarVendaModal from '@/components/EditarVendaModal'
import { imprimirPdfBlob } from '@/lib/print'
import { pedirExclusao } from '@/lib/confirmarExclusao'
import { enterAdvance } from '@/hooks/useEnterAdvance'
import { useFilial } from '@/lib/filial'
import { useAuth } from '@/lib/auth'

interface ProdutoPDV {
  id: number; codigo: string; ean: string | null; descricao: string;
  preco_venda: number; estoque_atual: number; estoque_consolidado?: number; unidade: string;
  foto_thumb?: string | null; categoria_id?: number | null;
  is_industrial?: boolean; tipo?: string;
}

// FIX (09/09, pedido do cliente): mostrar DE QUAL LOJA é o estoque exibido — antes
// aparecia só o número ("Est: 0 UN"), sem dizer se é da loja atual ou o total.
function textoEstoque(p: ProdutoPDV, nomeFilial?: string): string {
  const local = Number(p.estoque_atual ?? 0)
  const total = Number(p.estoque_consolidado ?? local)
  const base = nomeFilial
    ? `${local.toFixed(0)} ${p.unidade} em ${nomeFilial}`
    : `${local.toFixed(0)} ${p.unidade}`
  return total > local ? `${base} · ${total.toFixed(0)} no total (todas as lojas)` : base
}

/** Cor do saldo no card: negativo grita, zero avisa, positivo fica discreto.
 *  14/10 (pedido do cliente): antes o saldo saía sempre na mesma cor, então
 *  vender item zerado ou negativo não dava nenhum sinal visual. */
function corEstoque(p: ProdutoPDV): string {
  const est = Number(p.estoque_atual ?? 0)
  if (est < 0) return 'text-rose-600 font-semibold'
  if (est === 0) return 'text-amber-600 font-medium'
  return ''
}

interface GradePDVOption {
  id: number
  tipo_id: number
  tipo_codigo: string
  tipo_label: string
  codigo: string
  label: string
  hex_cor?: string | null
}
interface UnidadePDVOption {
  id: number
  unidade: string
  fator: number
  preco_venda: number | null
  descricao_alt?: string | null
}
interface ItemCarrinho {
  produto: ProdutoPDV; quantidade: number; desconto: number;
  grade_valor_ids?: number[];
  grade_descritor?: string;  // "Vermelho · M"
  conversao_id?: number | null;
  unidade_label?: string;   // "PLACA" se conversão, senão produto.unidade
  preco_unitario?: number;  // se conversão tem preço próprio, sobrescreve produto.preco_venda
  qualidade_exigida?: 'PRIMEIRA' | 'SEGUNDA' | 'TERCEIRA';
}

// Preço efetivo de uma linha (conversão sobrescreve o preço base do produto)
const precoLinha = (i: ItemCarrinho) => i.preco_unitario != null ? i.preco_unitario : i.produto.preco_venda

// Metadados (ícone/cor/comportamento) por forma. Label, ordem e ativo vêm da
// config por empresa (/caixa/config-formas), compartilhada com o Novo Pedido —
// reordenável via drag-and-drop.
const FORMA_META: Record<string, { icon: any; color: string; parcelas: boolean; vencimento: boolean; bike: boolean }> = {
  dinheiro:         { icon: Banknote,   color: 'bg-emerald-600 hover:bg-emerald-700', parcelas: false, vencimento: false, bike: false },
  pix:              { icon: QrCode,     color: 'bg-teal-600 hover:bg-teal-700',       parcelas: false, vencimento: false, bike: false },
  cartao_debito:    { icon: CreditCard, color: 'bg-blue-600 hover:bg-blue-700',       parcelas: false, vencimento: false, bike: false },
  cartao_credito:   { icon: CreditCard, color: 'bg-violet-600 hover:bg-violet-700',   parcelas: true,  vencimento: false, bike: false },
  // FEATURE 03/07: Link de pagamento (FixPay). Parcelas/modalidade tratadas dinamicamente no painel.
  link:             { icon: Link2,      color: 'bg-indigo-600 hover:bg-indigo-700',   parcelas: false, vencimento: false, bike: false },
  a_vista:          { icon: Wallet,     color: 'bg-green-700 hover:bg-green-800',      parcelas: false, vencimento: false, bike: false },
  prazo:            { icon: FileText,   color: 'bg-stone-600 hover:bg-stone-700',      parcelas: true,  vencimento: true,  bike: false },
  crediario:        { icon: Clock,      color: 'bg-amber-600 hover:bg-amber-700',      parcelas: true,  vencimento: true,  bike: false },
  boleto:           { icon: FileText,   color: 'bg-slate-600 hover:bg-slate-700',      parcelas: true,  vencimento: true,  bike: false },
  recebimento_bike: { icon: Bike,       color: 'bg-fuchsia-600 hover:bg-fuchsia-700',  parcelas: false, vencimento: false, bike: true },
  credito_cliente:  { icon: HandCoins,  color: 'bg-cyan-600 hover:bg-cyan-700',        parcelas: false, vencimento: false, bike: false },
}
const META_DEFAULT = { icon: CreditCard, color: 'bg-ink-600 hover:bg-ink-700', parcelas: true, vencimento: true, bike: false }
const metaFor = (id: string) => FORMA_META[id] || META_DEFAULT
const FORMA_LABEL_FALLBACK: Record<string, string> = {
  dinheiro: 'Dinheiro', pix: 'PIX', cartao_debito: 'Débito', cartao_credito: 'Crédito',
  a_vista: 'À Vista', prazo: 'A Prazo', crediario: 'Crediário', boleto: 'Boleto', recebimento_bike: 'Receb. Bike',
  credito_cliente: 'Crédito cliente', link: 'Link',
}
// FEATURE 03/07: cor por operadora (bandeira/máquina) — chip colorido no seletor do PDV.
// Estável por id; inline (hex) p/ não sofrer purge do Tailwind.
const OPERADORA_CORES = ['#f59e0b', '#3b82f6', '#10b981', '#8b5cf6', '#ec4899', '#06b6d4', '#f97316', '#84cc16']
const corOperadora = (id: number) => OPERADORA_CORES[Math.abs(id) % OPERADORA_CORES.length]
// Fallback enquanto a config não carregou.
const FORMAS_FALLBACK = [
  { forma: 'dinheiro', label: 'Dinheiro', ativo: true, ordem: 1 },
  { forma: 'pix', label: 'PIX', ativo: true, ordem: 2 },
  { forma: 'cartao_debito', label: 'Débito', ativo: true, ordem: 3 },
  { forma: 'cartao_credito', label: 'Crédito', ativo: true, ordem: 4 },
  { forma: 'a_vista', label: 'À Vista', ativo: true, ordem: 5 },
  { forma: 'prazo', label: 'A Prazo', ativo: true, ordem: 6 },
  { forma: 'crediario', label: 'Crediário', ativo: true, ordem: 7 },
  { forma: 'boleto', label: 'Boleto', ativo: true, ordem: 8 },
]

interface BikeRecebida {
  marca?: string
  modelo?: string
  ano?: string
  tamanho?: string
  numero_serie?: string
  cor?: string
  observacoes?: string
}

interface LinhaPag {
  forma: string
  valor: number
  parcelas: number
  primeiro_venc?: string  // ISO date
  bike?: BikeRecebida     // preenchido quando forma === 'recebimento_bike'
  // FEATURE 03/07: operadora (cartão/link, ex.: FixPay) escolhida no PDV + taxa aplicada.
  // Só REGISTRO (não lança financeiro): operadora_nome/taxa_pct aparecem na venda/relatório.
  operadora_id?: number
  operadora_nome?: string
  taxa_pct?: number
  // Para forma 'link': modalidade escolhida (debito | credito | pix).
  modalidade?: string
  // Nome cadastrado da forma (ex.: "PIX MAQUINA" × "PIX CHAVE"). Vem do backend
  // em _venda_to_dict; nas linhas montadas aqui no PDV não existe, por isso
  // opcional — ali o formaLabel cai no fallback.
  forma_label?: string | null
}

// ============ IMPRESSÃO DE CUPOM ============
function formaLabel(id: string, label?: string | null) {
  // Label cadastrado manda — é o que diferencia "PIX MAQUINA" de "PIX CHAVE".
  if (label && label.trim()) return label.trim()
  return FORMA_LABEL_FALLBACK[id] || id.replace(/_/g, ' ').toUpperCase()
}

function gerarCupomHTML(venda: any, itens: ItemCarrinho[], tipo: 'cupom' | 'recibo') {
  const agora = venda.criado_em
    ? new Date(venda.criado_em).toLocaleString('pt-BR')
    : new Date().toLocaleString('pt-BR')
  // Número SEM "R$" (implícito nas colunas Unit./Total) e com fonte menor — valores
  // grandes (ex.: 10.000,00) estouravam a coluna e desalinhavam o cupom de 80mm.
  const num = (v: number) => Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  const linhas = itens.map(i => {
    const preco = precoLinha(i)
    const total = i.quantidade * preco - i.desconto
    const grade = i.grade_descritor ? ` — ${i.grade_descritor}` : ''
    const unidade = i.unidade_label || i.produto.unidade
    const codProd = i.produto.codigo ? `<div class="cod">Cód: ${i.produto.codigo}</div>` : ''
    return `
      <tr>
        <td class="c-prod">${i.produto.descricao}${grade}${codProd}</td>
        <td class="c-qtd">${i.quantidade} ${unidade}</td>
        <td class="c-num">${num(preco)}</td>
        <td class="c-num">${num(total)}</td>
      </tr>`
  }).join('')

  // Lista de pagamentos (múltiplos). A bike de troca (forma=recebimento_bike)
  // NÃO é listada como pagamento: o valor dela é somado ao Desconto (abate o
  // total — ver bloco de totais) e a bike aparece num bloco informativo
  // "BIKE RECEBIDA NA TROCA" no rodapé.
  const pagamentosRaw: LinhaPag[] | null = Array.isArray(venda.pagamentos) && venda.pagamentos.length > 0
    ? venda.pagamentos
    : null
  const bikesRecebidas = pagamentosRaw ? pagamentosRaw.filter(p => p.forma === 'recebimento_bike') : []
  const bikeTotal = bikesRecebidas.reduce((s, p) => s + (Number(p.valor) || 0), 0)
  const pagamentosReais = pagamentosRaw ? pagamentosRaw.filter(p => p.forma !== 'recebimento_bike') : null
  const pagamentosHtml = pagamentosReais && pagamentosReais.length > 0
    ? `<div class="line"></div>
       <div style="font-size:11px;font-weight:bold;margin:2px 0;">PAGAMENTO${pagamentosReais.length > 1 ? 'S' : ''}:</div>
       ${pagamentosReais.map(p => {
         const det = p.parcelas > 1 ? ` ${p.parcelas}x` : ''
         const venc = p.primeiro_venc ? ` (1º ${new Date(p.primeiro_venc).toLocaleDateString('pt-BR')})` : ''
         return `<div style="display:flex;justify-content:space-between;font-size:11px;">
           <span>${formaLabel(p.forma, p.forma_label)}${det}${venc}</span>
           <span>${formatMoney(p.valor)}</span>
         </div>`
       }).join('')}`
    : (pagamentosRaw ? '' : `<div class="center" style="font-size:11px;">Pagamento: ${formaLabel(venda.forma_pagamento)}</div>`)
  const bikeInfoHtml = bikesRecebidas.length > 0
    ? `<div class="line"></div>
       <div style="font-size:11px;font-weight:bold;margin:2px 0;">BIKE${bikesRecebidas.length > 1 ? 'S' : ''} RECEBIDA${bikesRecebidas.length > 1 ? 'S' : ''} NA TROCA:</div>
       ${bikesRecebidas.map(p => {
         const b = p.bike || {}
         const titulo = [b.marca, b.modelo, b.ano].filter(Boolean).join(' ')
         return `<div style="display:flex;justify-content:space-between;font-size:11px;">
             <span>${titulo || 'Bike'}${b.cor ? ` · ${b.cor}` : ''}${b.tamanho ? ` · ${b.tamanho}` : ''}</span>
             <span>${formatMoney(p.valor)}</span>
           </div>
           ${b.numero_serie ? `<div style="font-size:10px;margin-left:8px;">Nº série: ${b.numero_serie}</div>` : ''}
           ${b.observacoes ? `<div style="font-size:10px;margin-left:8px;">Obs: ${String(b.observacoes).replace(/\n/g, '<br/>')}</div>` : ''}`
       }).join('')}`
    : ''

  return `<!DOCTYPE html>
<html><head><meta charset="utf-8">
<title>${tipo === 'cupom' ? 'Cupom' : 'Recibo'} #${venda.numero}</title>
<style>
  @media print { @page { margin: 0; size: 80mm auto; } body { margin: 0; } }
  body { font-family: 'Courier New', monospace; font-size: 13px; font-weight: bold; width: 280px; margin: 0 auto; color: #000; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  * { color: #000 !important; }
  .center { text-align: center; }
  .line { border-top: 1px dashed #000; margin: 6px 0; }
  table { width: 100%; border-collapse: collapse; table-layout: fixed; }
  th, td { padding: 2px 0; }
  th { font-size: 10px; border-bottom: 1px solid #000; }
  td { font-size: 11px; vertical-align: top; }
  .c-prod { width: 42%; text-align: left; word-wrap: break-word; overflow-wrap: anywhere; line-height: 1.2; }
  .c-qtd  { width: 16%; text-align: center; white-space: nowrap; }
  .c-num  { width: 21%; text-align: right; white-space: nowrap; font-size: 10px; }
  .cod    { font-size: 9px; font-weight: bold; color: #000; }
  .big { font-size: 18px; font-weight: bold; }
  .footer { font-size: 10px; text-align: center; margin-top: 8px; }
</style></head><body>
<div class="center" style="line-height:1.35;">
  ${venda.emissor?.logo_url ? `<img src="${venda.emissor.logo_url}" style="width:64px;height:auto;margin:4px auto 2px;display:block;" />` : ''}
  <div style="font-size:15px;font-weight:bold;margin:8px 0 1px;">${venda.emissor?.nome || 'Velore ERP'}</div>
  ${venda.emissor?.razao && venda.emissor.razao !== venda.emissor.nome ? `<div style="font-size:9px;color:#000;">${venda.emissor.razao}</div>` : ''}
  ${venda.emissor?.cnpj ? `<div style="font-size:9px;">CNPJ: ${venda.emissor.cnpj}</div>` : ''}
  ${(venda.emissor?.ie || venda.emissor?.im) ? `<div style="font-size:9px;">${[
    venda.emissor.ie ? `IE: ${venda.emissor.ie}` : '',
    venda.emissor.im ? `IM: ${venda.emissor.im}` : '',
  ].filter(Boolean).join(' · ')}</div>` : ''}
  ${venda.emissor?.endereco ? `<div style="font-size:9px;">${venda.emissor.endereco}</div>` : ''}
  ${(venda.emissor?.cidade || venda.emissor?.cep) ? `<div style="font-size:9px;">${[
    venda.emissor.cep ? `CEP ${venda.emissor.cep}` : '',
    venda.emissor.cidade,
  ].filter(Boolean).join(' — ')}</div>` : ''}
  ${(venda.emissor?.telefone || venda.emissor?.email) ? `<div style="font-size:9px;margin-bottom:4px;">${[
    venda.emissor.telefone ? `Tel: ${venda.emissor.telefone}` : '',
    venda.emissor.email,
  ].filter(Boolean).join(' · ')}</div>` : '<div style="margin-bottom:4px;"></div>'}
</div>
<div class="line"></div>
<div class="center" style="font-size:14px;font-weight:bold;margin:4px 0;">
  ${venda.os_numero ? `ORDEM DE SERVIÇO #${venda.os_numero}` : (tipo === 'cupom' ? 'CUPOM NÃO FISCAL' : 'RECIBO DE VENDA')}
</div>
${venda.os_numero ? '<div class="center" style="font-size:10px;font-weight:bold;">SERVIÇOS REALIZADOS</div>' : ''}
<div style="font-size:10px;">
  Nº: ${venda.numero} | Data: ${agora}
</div>
${venda.incluir_cliente !== false && venda.cliente_nome && venda.cliente_nome !== 'Consumidor Final' ? `
<div style="font-size:11px;margin-top:3px;line-height:1.35;">
  <strong>Cliente:</strong> ${venda.cliente_nome}
  ${venda.cliente_codigo ? `<br/><strong>Cód.:</strong> ${venda.cliente_codigo}` : ''}
  ${venda.cliente_documento ? `<br/><strong>Doc:</strong> ${venda.cliente_documento}` : ''}
  ${venda.cliente_telefone ? `<br/><strong>Tel:</strong> ${venda.cliente_telefone}` : ''}
  ${(venda.cliente_endereco || venda.cliente_cidade) ? `<br/><strong>End:</strong> ${
    [
      venda.cliente_endereco,
      venda.cliente_numero ? `nº ${venda.cliente_numero}` : '',
      venda.cliente_complemento,
      venda.cliente_bairro,
    ].filter(Boolean).join(', ')
  }${venda.cliente_cidade ? `${venda.cliente_endereco || venda.cliente_bairro ? ' — ' : ''}${venda.cliente_cidade}${venda.cliente_uf ? `/${venda.cliente_uf}` : ''}` : ''}` : ''}
</div>` : ''}
<div class="line"></div>
<table>
  <thead><tr>
    <th class="c-prod">Produto</th><th class="c-qtd">Qtd</th>
    <th class="c-num">Unit.</th><th class="c-num">Total</th>
  </tr></thead>
  <tbody>${linhas}</tbody>
</table>
<div class="line"></div>
${(() => {
  const subtotalBruto = itens.reduce((s, i) => s + i.quantidade * precoLinha(i), 0)
  // A entrada da bike abate o total a pagar e é somada ao Desconto.
  const totalAPagar = (Number(venda.total) || 0) - bikeTotal
  const descomercial = subtotalBruto - (Number(venda.total) || 0)  // desconto comercial puro
  const descontoTotal = subtotalBruto - totalAPagar                // comercial + bike
  return `<div style="display:flex;justify-content:space-between;margin:4px 0;">
  <span>Subtotal:</span><span>${formatMoney(subtotalBruto)}</span>
</div>${descontoTotal > 0.005 ? `
<div style="display:flex;justify-content:space-between;">
  <span>Desconto:</span><span>- ${formatMoney(descontoTotal)}</span>
</div>${bikeTotal > 0.005 ? `
<div style="font-size:10px;margin:0 0 2px 8px;">(comercial ${formatMoney(descomercial)} + troca/bike ${formatMoney(bikeTotal)})</div>` : ''}` : ''}`
})()}
${venda.troco > 0 ? `
<div style="display:flex;justify-content:space-between;">
  <span>Valor recebido:</span><span>${formatMoney((Number(venda.total) || 0) - bikeTotal + venda.troco)}</span>
</div>
<div style="display:flex;justify-content:space-between;font-weight:bold;">
  <span>Troco:</span><span>${formatMoney(venda.troco)}</span>
</div>` : ''}
<div class="line"></div>
<div class="center big" style="margin:6px 0;">TOTAL: ${formatMoney((Number(venda.total) || 0) - bikeTotal)}</div>
${pagamentosHtml}
${bikeInfoHtml}
${venda.observacao ? `
<div class="line"></div>
<div style="font-size:11px;margin:2px 0;">
  <strong>Observação:</strong><br/>${String(venda.observacao).replace(/\n/g, '<br/>')}
</div>` : ''}
<div class="line"></div>
<div class="footer">
  ${tipo === 'recibo' ? 'RECIBO DE VENDA - DOCUMENTO SEM VALOR FISCAL' : 'CUPOM NÃO FISCAL'}
  <br/>Obrigado pela preferência!
</div>
<script>window.onload = function(){ window.print(); }</script>
</body></html>`
}

export function imprimirCupom(venda: any, itens: ItemCarrinho[], tipo: 'cupom' | 'recibo') {
  // Imprime via iframe escondido — sem janela popup extra. Só aparece o
  // dialog de impressão do navegador (uma "tela" só).
  const html = gerarCupomHTML(venda, itens, tipo)
    // Remove o auto-print que estava no HTML (vamos disparar manualmente
    // após o iframe carregar)
    .replace(/<script>window\.onload\s*=\s*function\(\)\{\s*window\.print\(\);\s*\}<\/script>/g, '')

  const iframe = document.createElement('iframe')
  iframe.style.position = 'fixed'
  iframe.style.right = '0'
  iframe.style.bottom = '0'
  iframe.style.width = '0'
  iframe.style.height = '0'
  iframe.style.border = '0'
  document.body.appendChild(iframe)

  const doc = iframe.contentDocument || iframe.contentWindow?.document
  if (!doc) {
    document.body.removeChild(iframe)
    return
  }
  doc.open()
  doc.write(html)
  doc.close()

  const dispararPrint = () => {
    try {
      iframe.contentWindow?.focus()
      iframe.contentWindow?.print()
    } catch (e) { /* ignore */ }
    // Remove iframe após o usuário fechar/imprimir o dialog
    setTimeout(() => {
      if (iframe.parentNode) iframe.parentNode.removeChild(iframe)
    }, 1000)
  }

  // Só dispara o print depois que TODAS as imagens (logo da loja) terminarem
  // de carregar — senão, quando a logo é uma URL remota (ex.: fallback
  // /logo-empresa.jpeg), o print abre antes do download e o cupom sai SEM
  // logo ("às vezes não sai a logo"). Data URIs já vêm prontas. Timeout de
  // segurança garante que nunca trava (ex.: logo 404 / rede lenta).
  const imprimirQuandoPronto = () => {
    const imgs = Array.from((doc.images || []) as any as HTMLImageElement[])
    const pendentes = imgs.filter(img => !img.complete || img.naturalHeight === 0)
    if (pendentes.length === 0) {
      setTimeout(dispararPrint, 30)
      return
    }
    let disparou = false
    const go = () => { if (!disparou) { disparou = true; dispararPrint() } }
    let faltam = pendentes.length
    const umMenos = () => { faltam -= 1; if (faltam <= 0) go() }
    pendentes.forEach(img => {
      img.addEventListener('load', umMenos, { once: true })
      img.addEventListener('error', umMenos, { once: true })
    })
    setTimeout(go, 2500)  // rede lenta / imagem que nunca resolve → imprime assim mesmo
  }

  if (iframe.contentWindow?.document.readyState === 'complete') {
    imprimirQuandoPronto()
  } else {
    iframe.onload = imprimirQuandoPronto
  }
}

// FIX 30/06: o cupom/recibo do PDV ao finalizar saía em A4 — usava o gerador HTML
// (gerarCupomHTML/imprimirCupom), cujo @page 80mm é inconsistente entre navegador/
// impressora. Agora usa o MESMO PDF 80mm (reportlab) do backend que a reimpressão da
// tela Vendas e a OS já usam (GET /pdv/venda/{pedido_id}/cupom-pdf), via imprimirPdfBlob.
async function imprimirCupomPdf(pedidoId: number, tipo: 'cupom' | 'recibo') {
  const t = toast.loading('Gerando cupom...')
  try {
    const r = await api.get(`/pdv/venda/${pedidoId}/cupom-pdf`, { params: { tipo }, responseType: 'blob' })
    toast.dismiss(t)
    imprimirPdfBlob(r.data)
  } catch (e: any) {
    toast.dismiss(t)
    toast.error(e?.response?.data?.detail || 'Erro ao gerar cupom')
  }
}

// ============ MODAL: RECEBER TÍTULO A PRAZO PELO CAIXA (FEATURE 01/07) ============
// Cliente vem no balcão pagar uma conta a receber em aberto. Fluxo: 1) acha o cliente,
// 2) lista os títulos em aberto dele, 3) escolhe o título + valor + forma e confirma.
// O backend (POST /caixa/sessoes/{id}/receber-titulo) dá baixa no título E cria o
// movimento de entrada na gaveta (dinheiro entra no numerário; pix/cartão = receita fora
// da gaveta). SEM SENHA (rotina de caixa); estorno é pelo fluxo de estorno de movimento.
function ReceberTituloModal({ sessaoId, clienteInicial, onClose, onSuccess }: {
  sessaoId: number
  clienteInicial: any | null
  onClose: () => void
  onSuccess: () => void
}) {
  const [busca, setBusca] = useState('')
  const [cliente, setCliente] = useState<any | null>(clienteInicial || null)
  const [tituloSel, setTituloSel] = useState<any | null>(null)
  const [valor, setValor] = useState('')
  const [desconto, setDesconto] = useState('')
  // Quitar sem recebimento (dívida que não existe de verdade): motivo + senha de cargo.
  const [quitarAdm, setQuitarAdm] = useState(false)
  const [motivoAdm, setMotivoAdm] = useState('')
  const [senhaAdm, setSenhaAdm] = useState('')
  const qcRt = useQueryClient()
  const [forma, setForma] = useState('dinheiro')
  const [obs, setObs] = useState('')

  // Formas vêm da CONFIG da empresa (mesma do PDV de venda): só as que recebem
  // AGORA — fora a prazo e as que não geram receita (bike na troca, crédito do
  // cliente). Assim "à vista" aparece, e forma nova cadastrada entra sozinha.
  const { data: formasConfig = [] } = useQuery<any[]>({
    queryKey: ['config-formas'],
    queryFn: async () => (await api.get('/caixa/config-formas')).data,
  })
  const formasReceber = useMemo(
    () => (formasConfig as any[])
      .filter(f => f.ativo !== false && !f.a_prazo && f.gera_receber !== false)
      .sort((a, b) => (a.ordem ?? 0) - (b.ordem ?? 0)),
    [formasConfig],
  )

  const { data: pessoas = [], isFetching: buscandoCli } = useQuery<any[]>({
    queryKey: ['rt-clientes', busca],
    queryFn: async () => (await api.get('/cadastros/pessoas', { params: { q: busca, tipo: 'cliente', limit: 15 } })).data,
    enabled: !cliente && busca.trim().length >= 2,
  })

  const { data: titulos = [], isFetching: buscandoTit } = useQuery<any[]>({
    queryKey: ['rt-titulos', cliente?.id],
    queryFn: async () => (await api.get('/financeiro/com-cliente', {
      params: { tipo: 'receber', status: 'em_aberto', pessoa_id: cliente.id },
    })).data,
    enabled: !!cliente?.id,
  })
  // Saldo real = valor − já pago − descontos já concedidos em recebimentos anteriores.
  const saldoDe = (t: any) =>
    Number(t.valor || 0) - Number(t.valor_pago || 0) - Number(t.desconto_total || 0)
  const dataBr = (v: string | null) => (v ? String(v).slice(0, 10).split('-').reverse().join('/') : '')

  const receber = useMutation({
    mutationFn: async () => (await api.post(`/caixa/sessoes/${sessaoId}/receber-titulo`, {
      conta_financeira_id: tituloSel.id,
      valor: parseFloat(valor || '0'),
      forma,
      observacao: obs || undefined,
      desconto_valor: dNum > 0 ? dNum : 0,
    })).data,
    onSuccess: () => { toast.success('Recebimento registrado no caixa'); onSuccess() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao receber título'),
  })

  const quitar = useMutation({
    mutationFn: async () => (await api.post(`/financeiro/${tituloSel.id}/quitar-administrativo`, {
      motivo: motivoAdm, senha: senhaAdm,
    })).data,
    onSuccess: (d: any) => {
      toast.success(`Dívida de ${formatMoney(d.saldo_quitado)} quitada — sem entrada no caixa`)
      setQuitarAdm(false); setMotivoAdm(''); setSenhaAdm('')
      setTituloSel(null); setValor(''); setDesconto('')
      qcRt.invalidateQueries({ queryKey: ['rt-titulos'] })
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Não foi possível quitar'),
  })

  const escolherTitulo = (t: any) => {
    setTituloSel(t); setValor(saldoDe(t).toFixed(2)); setDesconto('')
    setQuitarAdm(false); setMotivoAdm(''); setSenhaAdm('')
  }
  const vNum = parseFloat(valor || '0') || 0
  const dNum = parseFloat(desconto || '0') || 0
  const saldoSel = tituloSel ? saldoDe(tituloSel) : 0
  const restante = Math.max(0, saldoSel - vNum - dNum)
  const excede = !!tituloSel && vNum + dNum > saldoSel + 0.005
  const podeConfirmar = !!tituloSel && vNum > 0 && dNum >= 0 && !excede && !receber.isPending

  return (
    <div className="fixed inset-0 bg-ink-950/85 z-[60] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6 max-h-[90vh] overflow-auto">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center"><HandCoins size={20} className="text-teal-700" /></div>
          <div>
            <h3 className="text-lg font-bold text-ink-900">Receber a prazo</h3>
            <p className="text-xs text-ink-500">Baixa de conta a receber do cliente, direto no caixa.</p>
          </div>
        </div>

        {!cliente ? (
          <div>
            <label className="label">Cliente</label>
            <input className="input" autoFocus value={busca} onChange={e => setBusca(e.target.value)}
              placeholder="Nome, documento ou código (mín. 2 letras)" />
            {busca.trim().length >= 2 && (
              <div className="mt-2 border rounded-lg divide-y max-h-52 overflow-auto">
                {buscandoCli && <div className="p-3 text-sm text-ink-400">Buscando...</div>}
                {!buscandoCli && pessoas.length === 0 && <div className="p-3 text-sm text-ink-400">Nenhum cliente encontrado.</div>}
                {pessoas.map(p => (
                  <button key={p.id} onClick={() => { setCliente(p); setBusca('') }}
                    className="w-full text-left px-3 py-2 hover:bg-ink-50 text-sm">
                    <div className="font-medium text-ink-900">{p.nome}</div>
                    <div className="text-[11px] text-ink-500">{[p.codigo && `Cód ${p.codigo}`, p.documento].filter(Boolean).join(' · ')}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 px-3 py-2 rounded bg-ink-100 mb-3">
              <User size={14} className="text-ink-500" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-ink-900 truncate">{cliente.nome}</div>
                {cliente.documento && <div className="text-[11px] text-ink-500">{cliente.documento}</div>}
              </div>
              <button onClick={() => { setCliente(null); setTituloSel(null); setValor('') }}
                className="text-ink-400 hover:text-rose-500 p-1" title="Trocar cliente"><X size={14} /></button>
            </div>

            {!tituloSel ? (
              <div>
                <label className="label">Títulos em aberto</label>
                {buscandoTit && <div className="p-3 text-sm text-ink-400">Carregando...</div>}
                {!buscandoTit && titulos.length === 0 && (
                  <div className="p-3 text-sm text-ink-400 border rounded-lg">Nenhum título em aberto pra este cliente.</div>
                )}
                {titulos.length > 0 && (
                  <div className="border rounded-lg divide-y max-h-64 overflow-auto">
                    {titulos.map(t => (
                      <button key={t.id} onClick={() => escolherTitulo(t)}
                        className="w-full text-left px-3 py-2 hover:bg-teal-50 flex items-center gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-ink-900 truncate">{t.descricao}</div>
                          <div className="text-[11px] text-ink-500">
                            Venc {dataBr(t.vencimento)}
                            {t.total_parcelas > 1 ? ` · parc ${t.parcela}/${t.total_parcelas}` : ''}
                            {t.status === 'parcial' ? ' · parcial' : ''}
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="text-sm font-bold text-teal-700">{formatMoney(saldoDe(t))}</div>
                          {saldoDe(t) !== Number(t.valor) && <div className="text-[10px] text-ink-400">de {formatMoney(t.valor)}</div>}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                <div className="px-3 py-2 rounded bg-teal-50 border border-teal-200">
                  <div className="text-sm font-medium text-ink-900">{tituloSel.descricao}</div>
                  <div className="text-[11px] text-ink-600">Saldo devedor: <strong>{formatMoney(saldoDe(tituloSel))}</strong></div>
                  <button onClick={() => { setTituloSel(null); setValor(''); setDesconto('') }} className="text-[11px] text-brand-600 hover:underline mt-0.5">← escolher outro título</button>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="label">Desconto (R$)</label>
                    <input className="input" type="number" step="0.01" min="0" placeholder="0,00"
                      value={desconto}
                      onChange={e => {
                        const d = e.target.value
                        setDesconto(d)
                        // Desconto costuma ser pra QUITAR: ajusta o valor pro que falta.
                        // Continua editável se o cliente for pagar só uma parte.
                        const resto = Math.max(0, saldoSel - (parseFloat(d || '0') || 0))
                        setValor(resto.toFixed(2))
                      }} />
                    {dNum > 0 && saldoSel > 0 && (
                      <p className="text-[11px] text-ink-500 mt-1">
                        ≈ {((dNum / saldoSel) * 100).toFixed(1).replace('.', ',')}% do saldo
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="label">Valor a receber (R$)</label>
                    <input className="input text-lg font-bold" type="number" step="0.01" min="0.01" autoFocus
                      value={valor} onChange={e => setValor(e.target.value)} />
                  </div>
                </div>

                {excede ? (
                  <p className="text-[11px] text-rose-600">Valor + desconto maior que o saldo devedor.</p>
                ) : (vNum > 0 || dNum > 0) && (
                  <div className={`text-xs px-3 py-2 rounded border ${
                    restante <= 0.005 ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                                      : 'bg-amber-50 border-amber-200 text-amber-800'}`}>
                    {restante <= 0.005
                      ? <>Título será <strong>QUITADO</strong>{dNum > 0 && <> com desconto de <strong>{formatMoney(dNum)}</strong></>}.</>
                      : <>Pagamento parcial — ficará em aberto <strong>{formatMoney(restante)}</strong>.</>}
                  </div>
                )}

                <div>
                  <label className="label">Forma</label>
                  <select className="input" value={forma} onChange={e => setForma(e.target.value)}>
                    {formasReceber.length === 0 && <option value="dinheiro">Dinheiro</option>}
                    {formasReceber.map((f: any) => (
                      <option key={f.id ?? f.forma} value={f.forma}>{f.label || f.forma}</option>
                    ))}
                  </select>
                  <p className="text-[11px] text-ink-400 mt-1">
                    {forma === 'dinheiro' ? 'Dinheiro entra na gaveta do caixa.' : 'PIX/cartão viram receita, mas não entram no numerário da gaveta.'}
                  </p>
                </div>
                <div>
                  <label className="label">Observação (opcional)</label>
                  <input className="input" value={obs} onChange={e => setObs(e.target.value)} placeholder="Ex: pago no balcão" />
                </div>

                {/* Dívida que existe no sistema mas não é real: sai sem entrada no caixa */}
                {!quitarAdm ? (
                  <button type="button" onClick={() => setQuitarAdm(true)}
                    className="text-xs text-rose-700 hover:underline">
                    Cliente não deve este valor? Quitar sem recebimento
                  </button>
                ) : (
                  <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 space-y-2">
                    <div className="text-sm font-semibold text-rose-900">
                      Quitar {formatMoney(saldoSel)} sem recebimento
                    </div>
                    <p className="text-[11px] text-rose-800">
                      O título sai da dívida do cliente <strong>sem entrada no caixa, no banco ou no DRE</strong>.
                      Use só quando a dívida não existe de verdade (lançada errado, em duplicidade,
                      já acertada fora do sistema). Fica registrado quem autorizou e o motivo.
                    </p>
                    <textarea className="input text-sm" rows={2} value={motivoAdm}
                      onChange={e => setMotivoAdm(e.target.value)}
                      placeholder="Motivo (ex.: venda lançada em duplicidade no dia 10/09)" />
                    <input type="password" className="input text-sm" value={senhaAdm} autoComplete="new-password"
                      onChange={e => setSenhaAdm(e.target.value)}
                      placeholder="Senha de gerente / admin" />
                    <div className="flex gap-2">
                      <button type="button" className="btn-secondary flex-1 text-sm"
                        onClick={() => { setQuitarAdm(false); setMotivoAdm(''); setSenhaAdm('') }}>
                        Voltar
                      </button>
                      <button type="button"
                        disabled={motivoAdm.trim().length < 10 || !senhaAdm || quitar.isPending}
                        onClick={() => {
                          if (window.confirm(`Quitar ${formatMoney(saldoSel)} SEM recebimento? Não entra nada no caixa.`)) {
                            quitar.mutate()
                          }
                        }}
                        className="flex-1 text-sm px-3 py-2 rounded-lg bg-rose-600 hover:bg-rose-700 text-white font-semibold disabled:opacity-50">
                        {quitar.isPending ? <Loader2 size={14} className="animate-spin inline" /> : 'Quitar'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        <div className="flex gap-2 mt-5">
          <button className="btn-secondary flex-1" onClick={onClose}>Cancelar</button>
          {tituloSel && (
            <button className="btn-primary flex-1 !bg-teal-600 hover:!bg-teal-700"
              disabled={!podeConfirmar} onClick={() => receber.mutate()}>
              {receber.isPending ? <Loader2 size={16} className="animate-spin" />
                : `Receber ${formatMoney(vNum)}${dNum > 0 ? ` (desc. ${formatMoney(dNum)})` : ''}`}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

// ============ COMPONENTE PDV ============
// FEATURE 30/06: mesmo PDV, parametrizado por CANAL de caixa. canal='online' = PDV-online
// (caixa separado do PDV normal 'balcao'). Rotas: /pdv (balcao) e /pdv-online (online).
export default function PDVPage({ canal = 'balcao' }: { canal?: 'balcao' | 'online' } = {}) {
  const nav = useNavigate()
  const location = useLocation()
  const inputRef = useRef<HTMLInputElement>(null)
  const [itens, setItens] = useState<ItemCarrinho[]>([])
  const [busca, setBusca] = useState('')
  const [buscaIdx, setBuscaIdx] = useState(0)  // item destacado na lista de resultados (teclado)
  const [showTutorial, setShowTutorial] = useState(false)
  const resultsRef = useRef<HTMLDivElement>(null)
  const pdvRootRef = useRef<HTMLDivElement>(null)
  // Foco pendente numa linha do carrinho (fluxo de teclado). Quando um item é
  // recém-adicionado ele ainda NÃO existe no DOM na hora da chamada; guardamos o
  // alvo aqui e o effect abaixo foca assim que a lista re-renderiza (robusto — o
  // setTimeout(0) antigo corria em race com re-renders da busca e "nem sempre" ia).
  const focoPendenteRef = useRef<{ idx: number; campo: 'qtd' | 'preco' | 'desc' } | null>(null)
  const [showPagamento, setShowPagamento] = useState(false)
  const [formaPag, setFormaPag] = useState('')
  const [valorRecebido, setValorRecebido] = useState('')
  const [vendaConcluida, setVendaConcluida] = useState<any>(null)
  const [itensVenda, setItensVenda] = useState<ItemCarrinho[]>([])
  const [pagamentos, setPagamentos] = useState<LinhaPag[]>([])
  const [observacao, setObservacao] = useState('')
  const [cliente, setCliente] = useState<{
    id: number; nome: string; codigo?: string | null; documento?: string | null;
    telefone?: string | null; endereco?: string | null; numero?: string | null;
    complemento?: string | null; bairro?: string | null; cidade?: string | null; uf?: string | null;
  } | null>(null)
  const [buscaCliente, setBuscaCliente] = useState('')
  const [incluirClienteCupom, setIncluirClienteCupom] = useState(true)
  const [vendedorCadId, setVendedorCadId] = useState<number | ''>('')
  // FEATURE (06/07): quando o vendedor é a OFICINA, captura QUAL mecânico executou.
  const [mecanicoCadId, setMecanicoCadId] = useState<number | ''>('')
  const [buscaVendedor, setBuscaVendedor] = useState('')
  const [abrirVendedor, setAbrirVendedor] = useState(false)
  const [vendHi, setVendHi] = useState(0)  // item destacado no dropdown de vendedor (teclado)
  // FEATURE (03/07): não deixa fechar a venda sem vendedor — gate de escolha rápida
  // (digita nome/número + setas + Enter) que aparece ao tentar ir pro pagamento.
  const [showVendedorGate, setShowVendedorGate] = useState(false)
  // PEDIDO DO CLIENTE (19/10): gate de cliente — venda não fecha sem cadastro completo.
  const [showClienteGate, setShowClienteGate] = useState(false)
  const [gateHi, setGateHi] = useState(0)
  const vendedorRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const onClick = (e: MouseEvent) => { if (vendedorRef.current && !vendedorRef.current.contains(e.target as Node)) setAbrirVendedor(false) }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [])
  const filialAtualId = useFilial(s => s.filialAtual?.id ?? null)
  // PDV opera sobre UMA loja (caixa, cupom e estoque são por filial). Em modo
  // consolidado / sem filial no topo, o backend bloqueia abrir caixa e finalizar
  // ("Selecione a loja..."). Pré-check de UX: em vez de abrir o caixa e bater no
  // 400, mostra um gate pedindo pra escolher a loja antes de operar o PDV.
  const modoConsolidado = useFilial(s => s.modoConsolidado)
  const setFilialAtual = useFilial(s => s.setFilialAtual)
  const setModoConsolidado = useFilial(s => s.setModoConsolidado)
  const semFilialLoja = modoConsolidado || !filialAtualId
  const authUser = useAuth(s => s.user)
  const [showVendasDoDia, setShowVendasDoDia] = useState(false)
  const [showAddCredito, setShowAddCredito] = useState(false)
  const [showServicosOS, setShowServicosOS] = useState(false)
  // FIX (item #16 da lista, "juntar mais de uma OS do mesmo cliente no PDV pra passar
  // junto"): agora é uma LISTA — sem OS: []; uma OS: [os]; várias: [os1, os2, ...].
  const [osVinculadas, setOsVinculadas] = useState<any[]>([])
  const [finalizandoMultiplas, setFinalizandoMultiplas] = useState(false)
  const [depositoId, setDepositoId] = useState<number | ''>(() => {
    const saved = typeof localStorage !== 'undefined' ? localStorage.getItem('pdv_deposito_id') : null
    return saved ? Number(saved) : ''
  })
  const [filialId, setFilialId] = useState<number | ''>(() => {
    const saved = typeof localStorage !== 'undefined' ? localStorage.getItem('pdv_filial_id') : null
    return saved ? Number(saved) : ''
  })
  const [showSeletorFilial, setShowSeletorFilial] = useState(false)
  useEffect(() => {
    if (depositoId !== '') localStorage.setItem('pdv_deposito_id', String(depositoId))
  }, [depositoId])
  useEffect(() => {
    if (filialId !== '') localStorage.setItem('pdv_filial_id', String(filialId))
  }, [filialId])
  // A filial da venda segue automaticamente a filial atual (usuário logado ou
  // impersonado/visualização) — não puxa a última do localStorage. Operador ainda
  // pode trocar por venda no seletor; trocar a filial no topo re-sincroniza aqui.
  useEffect(() => {
    if (filialAtualId) setFilialId(filialAtualId)
  }, [filialAtualId])
  // Vendedores seguem a filial DA VENDA (filialId) — passamos filial_id explícito
  // pro backend, que ignora o header X-Filial-Id (a filial da venda pode diferir
  // da filial do topo). Fallback p/ a filial do topo enquanto a da venda não resolve.
  const filialVendedores = filialId !== '' ? filialId : filialAtualId
  const { data: vendedoresPDV = [] } = useQuery<any[]>({
    queryKey: ['vendedores-pdv', filialVendedores],
    queryFn: async () => (await api.get('/cadastros/colaboradores', {
      params: { tipo: 'vendedor', ativos: true, filial: true, filial_id: filialVendedores ?? undefined },
    }).catch(() => ({ data: [] }))).data || [],
  })
  // Mecânicos (colaboradores tipo=tecnico) da filial da venda — só usados quando o
  // vendedor escolhido é a OFICINA (06/07). Ficam FORA da lista de vendedor.
  const { data: mecanicosPDV = [] } = useQuery<any[]>({
    queryKey: ['mecanicos-pdv', filialVendedores],
    queryFn: async () => (await api.get('/cadastros/colaboradores', {
      params: { tipo: 'tecnico', ativos: true, filial: true, filial_id: filialVendedores ?? undefined },
    }).catch(() => ({ data: [] }))).data || [],
  })
  // Vendedor selecionado é a OFICINA? (colaborador cujo nome começa com "oficina").
  // Quando sim, exige/mostra o campo Mecânico.
  const vendedorEhOficina = (() => {
    const v = vendedoresPDV.find((x: any) => x.id === vendedorCadId)
    return !!v && String(v.nome || '').trim().toLowerCase().startsWith('oficina')
  })()
  // Trocou o vendedor pra alguém que não é a OFICINA → limpa o mecânico.
  useEffect(() => { if (!vendedorEhOficina && mecanicoCadId !== '') setMecanicoCadId('') }, [vendedorEhOficina])  // eslint-disable-line react-hooks/exhaustive-deps
  // 06/07 (pedido do usuário): a OFICINA deve ser SEMPRE o 1º item da lista de vendedor,
  // com ou sem busca. ofRank = 0 p/ oficina, 1 p/ demais → chave primária da ordenação.
  const ehOficinaVend = (v: any) => String(v.nome || '').trim().toLowerCase().startsWith('oficina')
  const vendedoresFiltrados = (() => {
    const ofRank = (v: any) => (ehOficinaVend(v) ? 0 : 1)
    const q = buscaVendedor.trim().toLowerCase()
    if (!q) {
      // Sem busca: só fixa a OFICINA no topo, mantendo o resto na ordem original.
      return [...vendedoresPDV].sort((a: any, b: any) => ofRank(a) - ofRank(b))
    }
    const qn = q.replace(/^0+/, '')  // sem zeros à esquerda
    const ehNumero = /^\d+$/.test(q)
    // rank: 0=código exato, 1=código começa, 2=nome começa, 3=contém. menor = mais relevante
    const rank = (v: any): number => {
      const cod = String(v.codigo || '').toLowerCase()
      const nome = String(v.nome || '').toLowerCase()
      if (cod === q || cod.replace(/^0+/, '') === qn) return 0
      if (cod.startsWith(q) || cod.replace(/^0+/, '').startsWith(qn)) return 1
      if (nome.startsWith(q)) return 2
      if (cod.includes(q) || nome.includes(q)) return 3
      return 99
    }
    const codNum = (v: any) => Number(v.codigo) || 1e9
    const achados = vendedoresPDV.filter((v: any) => rank(v) < 99)
    // OFICINA sempre no topo; depois: busca por NÚMERO → código crescente; por texto → relevância.
    if (ehNumero) {
      return achados.sort((a: any, b: any) => ofRank(a) - ofRank(b)
        || codNum(a) - codNum(b)
        || String(a.nome).localeCompare(String(b.nome)))
    }
    return achados.sort((a: any, b: any) => ofRank(a) - ofRank(b)
      || rank(a) - rank(b)
      || codNum(a) - codNum(b)
      || String(a.nome).localeCompare(String(b.nome)))
  })()
  const [confirmSair, setConfirmSair] = useState<{ destino: string } | null>(null)
  const [showMenu, setShowMenu] = useState(false)
  // Tema sincronizado com o resto do app via chave 'velore_tema' (default escuro)
  const [tema, setTema] = useState<'escuro' | 'claro'>(() => {
    if (typeof localStorage === 'undefined') return 'escuro'
    const t = localStorage.getItem('velore_tema') || localStorage.getItem('pdv_tema')
    return (t as 'escuro' | 'claro') || 'escuro'
  })
  useEffect(() => { try { localStorage.setItem('velore_tema', tema) } catch {} }, [tema])
  const escuro = tema === 'escuro'
  // Classes condicionais reutilizadas pra trocar tema sem refatorar todas as classes
  const T = {
    outer: escuro ? 'bg-ink-950' : 'bg-ink-100',
    header: escuro ? 'bg-ink-950 text-white' : 'bg-white text-ink-900 border-b border-ink-200',
    headerIcon: escuro ? 'text-ink-400 hover:bg-ink-800' : 'text-ink-500 hover:bg-ink-100',
    headerMutedText: escuro ? 'text-ink-300' : 'text-ink-600',
    rightPanel: escuro ? 'bg-ink-950 text-white border-l border-ink-800' : 'bg-white text-ink-900 border-l border-ink-200',
    rightPanelDivider: escuro ? 'border-ink-800' : 'border-ink-200',
    card: escuro ? 'bg-ink-900 border-ink-700' : 'bg-ink-50 border-ink-200',
    cardSubtle: escuro ? 'bg-ink-900' : 'bg-ink-50',
    cardText: escuro ? 'text-white' : 'text-ink-900',
    cardMuted: escuro ? 'text-ink-400' : 'text-ink-500',
    chip: escuro ? 'bg-ink-800' : 'bg-ink-100',
  }
  const qc = useQueryClient()

  // ============ CAIXA (sessão de turno) ============
  const [showAbrirCaixa, setShowAbrirCaixa] = useState(false)
  const [showSangria, setShowSangria] = useState(false)
  const [showSuprimento, setShowSuprimento] = useState(false)
  const [showSaida, setShowSaida] = useState(false)            // saída de valor (despesa) do caixa
  const [saidaCgId, setSaidaCgId] = useState<number | ''>('')  // categoria (conta gerencial) da saída
  const [showEntrada, setShowEntrada] = useState(false)        // entrada de valor (receita) no caixa
  const [entradaCgId, setEntradaCgId] = useState<number | ''>('')  // categoria (conta gerencial) da entrada
  const [entradaForma, setEntradaForma] = useState('dinheiro')      // 01/07: forma de recebimento da entrada (dinheiro/pix/cartão...)
  // FEATURE 06/07: quando a entrada é em cartão/link, o operador escolhe operadora/
  // modalidade/parcelas igual à venda (BUG: antes o modal não abria esses detalhes).
  const [entradaOperadoraId, setEntradaOperadoraId] = useState<number | null>(null)
  const [entradaModalidade, setEntradaModalidade] = useState('')   // link: debito | credito | pix
  const [entradaParcelas, setEntradaParcelas] = useState<number>(1)
  const [showResumoCaixa, setShowResumoCaixa] = useState(false)
  const [showFecharCaixa, setShowFecharCaixa] = useState(false)
  const [showReceberTitulo, setShowReceberTitulo] = useState(false)  // FEATURE 01/07: receber título a prazo no caixa
  const [caixaContaId, setCaixaContaId] = useState<number | ''>('')
  const [valorAbertura, setValorAbertura] = useState('')
  const [obsAbertura, setObsAbertura] = useState('')
  const [movValor, setMovValor] = useState('')
  const [movDescricao, setMovDescricao] = useState('')
  const [movSenha, setMovSenha] = useState('')
  const [movResponsavelId, setMovResponsavelId] = useState<number | ''>('')
  const [valorContado, setValorContado] = useState('')
  const [obsFechamento, setObsFechamento] = useState('')
  // FEATURE 08/07: virada de dia (caixa aberto ontem e não fechado) — separa ontem/hoje.
  const [showVirarDia, setShowVirarDia] = useState(false)
  const [valorContadoOntem, setValorContadoOntem] = useState('')

  const { data: contasBancarias = [] } = useQuery<any[]>({
    queryKey: ['contas-bancarias'],
    queryFn: async () => (await api.get('/financeiro/contas-bancarias')).data,
  })

  const { data: sessaoCaixa, refetch: refetchSessao } = useQuery<any>({
    queryKey: ['caixa-sessao-atual', canal],
    queryFn: async () => (await api.get('/caixa/sessao-atual', { params: { canal } })).data,
    refetchInterval: 30000,
  })

  // Mesmo detalhamento da tela de fechamento (/caixa/sessoes/{id}) — é de onde sai
  // o total por forma de pagamento. Só busca com o modal aberto, pra não pesar o PDV.
  // PRECISA vir depois de sessaoCaixa (usa o id dela).
  const { data: detalheSessao } = useQuery<any>({
    queryKey: ['caixa-sessao-detalhe', sessaoCaixa?.id],
    queryFn: async () => (await api.get(`/caixa/sessoes/${sessaoCaixa!.id}`)).data,
    enabled: !!sessaoCaixa?.id && showResumoCaixa,
  })

  // Vendas + recebimentos da MESMA forma somam na mesma linha (igual ao fechamento).
  // Diferença: aqui o caixa ainda está aberto, então o dinheiro usa o ESPERADO na
  // gaveta (abertura + vendas + suprimentos − sangrias), não o valor contado.
  const resumoPorForma = useMemo(() => {
    if (!detalheSessao) return []
    const mapa: Record<string, { vendas: number; receb: number }> = {}
    Object.entries(detalheSessao.receitas_por_forma || {}).forEach(([forma, valor]: [string, any]) => {
      if (!mapa[forma]) mapa[forma] = { vendas: 0, receb: 0 }
      mapa[forma].vendas += Number(valor || 0)
    })
    ;(detalheSessao.movimentos || []).forEach((m: any) => {
      if (m.estornado_em || m.tipo !== 'entrada') return
      const forma = m.forma || 'dinheiro'
      if (!mapa[forma]) mapa[forma] = { vendas: 0, receb: 0 }
      mapa[forma].receb += Number(m.valor || 0)
    })
    if (!mapa['dinheiro']) mapa['dinheiro'] = { vendas: 0, receb: 0 }
    return Object.entries(mapa)
      .map(([forma, v]) => ({
        forma, vendas: v.vendas, receb: v.receb,
        total: forma === 'dinheiro'
          ? Number(detalheSessao.valor_sistema_atual ?? (v.vendas + v.receb))
          : v.vendas + v.receb,
      }))
      .sort((a, b) => b.total - a.total)
  }, [detalheSessao])
  const resumoTotalGeral = resumoPorForma.reduce((acc, f) => acc + f.total, 0)

  // Modal abertura aparece automaticamente se não tem sessao aberta — MAS só
  // quando há uma loja selecionada (senão o /caixa/abrir bloqueia com 400). Em
  // consolidado, o gate de filial (abaixo) cuida de pedir a loja primeiro.
  useEffect(() => {
    if (sessaoCaixa === null && !vendaConcluida && !semFilialLoja) setShowAbrirCaixa(true)
  }, [sessaoCaixa, vendaConcluida, semFilialLoja])

  // O vendedor é escolhido no painel direito (campo de busca por nome ou número),
  // não num modal ao abrir o PDV.

  const abrirCaixa = useMutation({
    mutationFn: async () => (await api.post('/caixa/abrir', {
      conta_id: caixaContaId,
      valor_abertura: parseFloat(valorAbertura || '0'),
      observacao: obsAbertura || undefined,
      canal,
    })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['caixa-sessao-atual'] })
      setShowAbrirCaixa(false); setValorAbertura(''); setObsAbertura(''); setCaixaContaId('')
      toast.success('Caixa aberto')
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao abrir caixa'),
  })

  const sangriaMut = useMutation({
    mutationFn: async () => (await api.post(`/caixa/sessoes/${sessaoCaixa.id}/sangria`, {
      valor: parseFloat(movValor || '0'),
      descricao: movDescricao || undefined,
      senha: movSenha,
      responsavel_id: movResponsavelId || undefined,
    })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['caixa-sessao-atual'] })
      setShowSangria(false); setMovValor(''); setMovDescricao(''); setMovSenha(''); setMovResponsavelId('')
      toast.success('Sangria registrada')
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao registrar sangria'),
  })

  const suprimentoMut = useMutation({
    mutationFn: async () => (await api.post(`/caixa/sessoes/${sessaoCaixa.id}/suprimento`, {
      valor: parseFloat(movValor || '0'),
      descricao: movDescricao || undefined,
    })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['caixa-sessao-atual'] })
      setShowSuprimento(false); setMovValor(''); setMovDescricao('')
      toast.success('Suprimento registrado')
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao registrar suprimento'),
  })

  // Categorias (plano de contas) de despesa, p/ classificar a "saída de valor".
  // O plano usa despesa_op/despesa_fin (não existe tipo "despesa") — daí o dropdown vinha vazio.
  const { data: categoriasSaida = [] } = useQuery<any[]>({
    queryKey: ['plano-contas-despesa'],
    queryFn: async () => (await api.get('/plano-contas/analiticas', { params: { tipo: 'despesa_op,despesa_fin' } })).data,
  })
  // Pré-seleciona "Saída - Outros" (ou a 1ª categoria) como padrão; o operador pode trocar.
  useEffect(() => {
    if (saidaCgId === '' && categoriasSaida.length) {
      const padrao = categoriasSaida.find((c: any) => /outros/i.test(c.nome)) || categoriasSaida[0]
      if (padrao) setSaidaCgId(padrao.id)
    }
  }, [categoriasSaida, saidaCgId])

  // Categorias (plano de contas) de receita, p/ classificar a "entrada de valor".
  const { data: categoriasEntrada = [] } = useQuery<any[]>({
    queryKey: ['plano-contas-receita'],
    queryFn: async () => (await api.get('/plano-contas/analiticas', { params: { tipo: 'receita,receita_fin' } })).data,
  })
  useEffect(() => {
    if (entradaCgId === '' && categoriasEntrada.length) {
      const padrao = categoriasEntrada.find((c: any) => /outras/i.test(c.nome)) || categoriasEntrada[0]
      if (padrao) setEntradaCgId(padrao.id)
    }
  }, [categoriasEntrada, entradaCgId])

  // 01/07: formas de recebimento p/ a "entrada de valor" — só as que entram dinheiro AGORA
  // (exclui a prazo/boleto/crediário/carteira, que não fazem sentido numa entrada avulsa de caixa).
  const { data: formasConfig = [] } = useQuery<any[]>({
    queryKey: ['config-formas'],
    queryFn: async () => (await api.get('/caixa/config-formas')).data,
  })
  const formasEntrada = useMemo(
    () => (formasConfig as any[]).filter(f => f.ativo !== false && !f.a_prazo),
    [formasConfig],
  )

  // FEATURE 06/07: operadoras de cartão/link (FixPay etc.) p/ a ENTRADA de valor.
  // Espelha o painel de pagamento da venda (mesma fonte /caixa/operadoras-cartao).
  const { data: operadorasEntrada = [] } = useQuery<any[]>({
    queryKey: ['operadoras-cartao'],
    queryFn: async () => (await api.get('/caixa/operadoras-cartao')).data,
  })
  const entradaEhLink = entradaForma === 'link'
  const entradaUsaOperadora = entradaForma === 'cartao_credito' || entradaForma === 'cartao_debito' || entradaEhLink
  const entradaOpsDaForma = useMemo(
    () => (operadorasEntrada as any[])
      .filter(o => o.ativo && (o.tipo || 'cartao') === (entradaEhLink ? 'link' : 'cartao'))
      .sort((a, b) => (a.ordem ?? 0) - (b.ordem ?? 0)),
    [operadorasEntrada, entradaEhLink],
  )
  const entradaOpSel = entradaOpsDaForma.find(o => o.id === entradaOperadoraId) || null
  const entradaModalidadeEfetiva = entradaForma === 'cartao_debito' ? 'debito'
    : entradaForma === 'cartao_credito' ? 'credito'
      : (entradaEhLink ? entradaModalidade : '')
  const entradaMostrarParcelas = entradaModalidadeEfetiva === 'credito'
  const entradaTaxaPct = entradaOpSel
    ? (entradaModalidadeEfetiva === 'debito' ? Number(entradaOpSel.taxa_debito || 0)
        : entradaModalidadeEfetiva === 'pix' ? Number(entradaOpSel.taxa_pix || 0)
          : entradaModalidadeEfetiva === 'credito'
            ? Number(entradaOpSel.taxas_credito?.[String(entradaParcelas)] ?? entradaOpSel.taxas_credito?.['1'] ?? 0)
            : 0)
    : 0

  // Ao trocar a forma da entrada: reseta operadora/modalidade/parcelas e pré-seleciona
  // a operadora quando só existe uma daquele tipo (igual à venda).
  function escolherFormaEntrada(forma: string) {
    setEntradaForma(forma)
    setEntradaParcelas(1)
    setEntradaModalidade('')
    const usaOp = forma === 'cartao_credito' || forma === 'cartao_debito' || forma === 'link'
    const ops = (operadorasEntrada as any[]).filter(o => o.ativo && (o.tipo || 'cartao') === (forma === 'link' ? 'link' : 'cartao'))
    setEntradaOperadoraId(usaOp && ops.length === 1 ? ops[0].id : null)
  }

  const saidaMut = useMutation({
    mutationFn: async () => (await api.post(`/caixa/sessoes/${sessaoCaixa.id}/saida`, {
      valor: parseFloat(movValor || '0'),
      descricao: movDescricao || undefined,
      conta_gerencial_id: saidaCgId || undefined,
      senha: movSenha,
      responsavel_id: movResponsavelId || undefined,
    })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['caixa-sessao-atual'] })
      qc.invalidateQueries({ queryKey: ['caixa-sessao-stats', sessaoCaixa?.id] })
      setShowSaida(false); setMovValor(''); setMovDescricao(''); setMovSenha(''); setMovResponsavelId('')
      toast.success('Saída registrada')
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao registrar saída'),
  })

  const entradaMut = useMutation({
    mutationFn: async () => (await api.post(`/caixa/sessoes/${sessaoCaixa.id}/entrada`, {
      valor: parseFloat(movValor || '0'),
      descricao: movDescricao || undefined,
      conta_gerencial_id: entradaCgId || undefined,
      forma: entradaForma || 'dinheiro',   // 01/07: forma de recebimento (dinheiro só na gaveta)
      // FEATURE 06/07: detalhes do cartão/link (só quando forma é cartão/link).
      operadora_id: entradaUsaOperadora ? (entradaOpSel?.id ?? undefined) : undefined,
      operadora_nome: entradaUsaOperadora ? (entradaOpSel?.nome ?? undefined) : undefined,
      taxa_pct: entradaUsaOperadora ? entradaTaxaPct : undefined,
      parcelas: entradaUsaOperadora ? (entradaMostrarParcelas ? entradaParcelas : 1) : undefined,
      modalidade: entradaEhLink ? (entradaModalidade || undefined) : undefined,
    })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['caixa-sessao-atual'] })
      qc.invalidateQueries({ queryKey: ['caixa-sessao-stats', sessaoCaixa?.id] })
      setShowEntrada(false); setMovValor(''); setMovDescricao(''); setEntradaForma('dinheiro')
      setEntradaOperadoraId(null); setEntradaModalidade(''); setEntradaParcelas(1)
      toast.success('Entrada registrada')
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao registrar entrada'),
  })

  const { abrir: abrirPdf, modal: pdfModal } = usePdfPreview()

  const fecharCaixa = useMutation({
    mutationFn: async () => (await api.post(`/caixa/sessoes/${sessaoCaixa.id}/fechar`, {
      valor_contado: parseFloat(valorContado || '0'),
      observacao: obsFechamento || undefined,
    })).data,
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['caixa-sessao-atual'] })
      setShowFecharCaixa(false); setValorContado(''); setObsFechamento('')
      toast.success('Caixa fechado')
      // Abre o relatório num modal de preview (ver/imprimir/fechar), sem aba nova.
      abrirPdf(`/caixa/sessoes/${data.id}/relatorio.pdf`, 'Fechamento de caixa')
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao fechar caixa'),
  })

  // FEATURE 08/07: VIRADA DE DIA — fecha o caixa de ontem (que ficou aberto) e abre um
  // novo pra hoje, separando a movimentação. valor_contado de ontem é opcional.
  const virarDia = useMutation({
    mutationFn: async () => (await api.post(`/caixa/sessoes/${sessaoCaixa.id}/virar-dia`, {
      valor_contado: valorContadoOntem !== '' ? parseFloat(valorContadoOntem) : undefined,
    })).data,
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['caixa-sessao-atual'] })
      setShowVirarDia(false); setValorContadoOntem('')
      toast.success('Caixa virado: ontem fechado, caixa de hoje aberto')
      // Abre o relatório do caixa de ONTEM (fechado) pra conferência/impressão.
      if (data?.fechada?.id) abrirPdf(`/caixa/sessoes/${data.fechada.id}/relatorio.pdf`, 'Fechamento do caixa de ontem')
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao virar o caixa'),
  })

  // ============ VITRINE / MODO GRADE ============
  const [modo, setModo] = useState<'lista' | 'grade'>(
    (typeof localStorage !== 'undefined' && localStorage.getItem('pdv_modo') === 'grade') ? 'grade' : 'lista'
  )
  useEffect(() => { try { localStorage.setItem('pdv_modo', modo) } catch {} }, [modo])
  const [grupoSelecionado, setGrupoSelecionado] = useState<number | null>(null)
  const [subgrupoSelecionado, setSubgrupoSelecionado] = useState<number | null>(null)
  // Vitrine persistente: continua disponível mesmo com itens no carrinho (split).
  // O operador pode recolher pra ganhar espaço; clicar num filtro reabre.
  const [vitrineColapsada, setVitrineColapsada] = useState(false)
  // Estado ocioso (sem busca/itens/filtro) mostra a logo da loja em vez da grade.
  // "Ver produtos" / chip "Todos" forçam a grade completa via verTodos.
  const [verTodos, setVerTodos] = useState(false)

  // Categorias do PDV em lista PLANA: grupos + subgrupos (todos com exibir_pdv).
  // Subgrupos viram chips na mesma linha dos grupos (logo após o pai).
  const { data: categoriasPDV = [] } = useQuery<any[]>({
    queryKey: ['pdv-categorias'],
    queryFn: async () => (await api.get('/cadastros/categorias', { params: { pdv: true } })).data,
  })
  // Tags (grupos raiz) na linha 1; subtags (subgrupos) só aparecem ao clicar na tag.
  const { rootsCategorias, subsPorPai } = useMemo(() => {
    const roots = categoriasPDV.filter((c: any) => !c.parent_id)
      .sort((a: any, b: any) => a.nome.localeCompare(b.nome))
    const subs: Record<number, any[]> = {}
    categoriasPDV.filter((c: any) => c.parent_id).forEach((s: any) => {
      (subs[s.parent_id] ||= []).push(s)
    })
    Object.values(subs).forEach((arr) => arr.sort((a: any, b: any) => a.nome.localeCompare(b.nome)))
    return { rootsCategorias: roots, subsPorPai: subs }
  }, [categoriasPDV])

  const { data: vitrine = [] } = useQuery<ProdutoPDV[]>({
    queryKey: ['pdv-vitrine', grupoSelecionado, subgrupoSelecionado],
    queryFn: async () => (await api.get('/pdv/vitrine', {
      params: {
        // Subgrupo selecionado → filtra só por ele (mais específico); senão pelo grupo.
        grupo_id: subgrupoSelecionado ? undefined : (grupoSelecionado || undefined),
        subgrupo_id: subgrupoSelecionado || undefined,
      },
    })).data,
    // Carrega quando a grade está realmente visível: sem busca, não recolhida, e
    // fora do estado ocioso (que mostra só a logo). Itens no carrinho ou filtro/Todos
    // selecionado já tiram do ocioso. Evita buscar todos os produtos só pra ver a logo.
    enabled: busca.length === 0 && !vitrineColapsada
      && (verTodos || grupoSelecionado != null || subgrupoSelecionado != null || itens.length > 0),
  })

  // Stats da sessao (atualiza com vendas)
  const { data: sessaoStats } = useQuery<any>({
    queryKey: ['caixa-sessao-stats', sessaoCaixa?.id],
    queryFn: async () => sessaoCaixa
      ? (await api.get(`/caixa/sessoes/${sessaoCaixa.id}/stats`)).data
      : null,
    enabled: !!sessaoCaixa,
    refetchInterval: 30000,
  })

  const { data: usuarioAtual } = useQuery<any>({
    queryKey: ['usuario-atual'],
    queryFn: async () => (await api.get('/auth/me')).data,
  })

  // Carrega depositos que aceitam venda para esta empresa
  const { data: depositosVenda = [] } = useQuery<any[]>({
    queryKey: ['pdv-depositos-venda'],
    queryFn: async () => (await api.get('/pdv/depositos-venda')).data,
  })

  // Empresa + filiais — usados pro cabeçalho do cupom
  const { data: empresa } = useQuery<any>({
    queryKey: ['empresa-atual'],
    queryFn: async () => (await api.get('/empresa/atual')).data,
  })
  const { data: filiais = [] } = useQuery<any[]>({
    queryKey: ['filiais-ativas'],
    queryFn: async () => (await api.get('/filiais')).data,
  })
  // FEATURE 30/06: filiais "somente_fiscal" = CNPJs emissores escolhíveis no PDV.
  // Aparecem no modal "De qual filial sai a venda?" (escolha do emissor/cupom), mas NÃO no
  // seletor de filial do topo nem nos relatórios. A venda continua na filial do operador.
  const { data: filiaisFiscais = [] } = useQuery<any[]>({
    queryKey: ['filiais-fiscais'],
    queryFn: async () => (await api.get('/filiais', { params: { somente_fiscais: true } })).data,
  })

  // Filiais ativas — quando há > 1, perguntar no fechamento da venda
  const filiaisAtivas = filiais.filter(f => f.ativo)
  const precisaEscolherFilial = filiaisAtivas.length > 1

  const nomeFilialAtual = (() => {
    const f = filiais.find((x: any) => x.id === filialAtualId)
    return f ? (f.nome_fantasia || f.razao_social || `Loja ${f.codigo}`) : undefined
  })()

  // Depósito de saída não é restrito à filial — qualquer depósito de venda serve.
  const depositosDaFilial = depositosVenda

  // Pre-seleciona o primeiro depósito disponível (respeita filial atual)
  useEffect(() => {
    if (depositosDaFilial.length === 0) return
    const aindaValido = depositoId !== '' && depositosDaFilial.some(d => d.id === depositoId)
    if (!aindaValido) {
      setDepositoId(depositosDaFilial[0].id)
    }
  }, [depositosDaFilial, depositoId])

  // Resolve o foco pendente numa linha do carrinho DEPOIS que a lista renderiza
  // (item recém-adicionado já está no DOM). Executa em toda mudança de `itens`, mas
  // só age quando há alvo pendente — nas edições normais (qtd/preço/desc) é no-op.
  useEffect(() => {
    const alvo = focoPendenteRef.current
    if (!alvo) return
    focoPendenteRef.current = null
    const el = pdvRootRef.current?.querySelector(`[data-item-${alvo.campo}="${alvo.idx}"]`) as HTMLInputElement | null
    if (el) { el.focus(); el.select?.() }
  }, [itens])

  const depositoAtual = depositosVenda.find(d => d.id === depositoId)
  const depositoDeOutraLoja = !!depositoAtual && !modoConsolidado && !!filialAtualId
    && depositoAtual.filial_id != null && depositoAtual.filial_id !== filialAtualId

  // Emissor do cupom: prioriza a filial LOGADA (a da venda, filialId); cai pra filial do
  // depósito e por fim empresa. Filial tem todos os campos fiscais (nome_fantasia, cnpj,
  // endereço, ie, telefone).
  // BUG (02/07): o emissor/logo vinham da filial do DEPÓSITO selecionado. Como todos os
  // depósitos apontam pra filial sem logo (P.A. CAVALCANTE) ou pra NULL, a logo sempre caía
  // no fallback da empresa (/logo-empresa.jpeg = "Bicicletando"), independente da filial logada.
  // FIX: passa a seguir a filial da venda (filialId, que espelha a filial logada filialAtualId),
  // que tem logo e dados fiscais próprios.
  const filialDaVenda = filialId !== ''
    ? filiais.find(f => f.id === filialId)
    : null
  const filialDoDeposito = depositoAtual?.filial_id
    ? filiais.find(f => f.id === depositoAtual.filial_id)
    : null
  const filialEmissor = filialDaVenda || filialDoDeposito || null
  const emissor = (() => {
    const f = filialEmissor
    const src = f || empresa
    if (!src) return { nome: 'Velore ERP', razao: '', cnpj: '', ie: '', im: '', telefone: '', email: '', endereco: '', cidade: '', cep: '', logo_url: (empresa as any)?.logo_url || '' }
    return {
      logo_url: (src as any).logo_url || (empresa as any)?.logo_url || '',
      nome: src.nome_fantasia || src.razao_social || 'Velore ERP',
      razao: src.razao_social || '',
      cnpj: src.cnpj || '',
      ie: src.ie || '',
      im: src.inscricao_municipal || src.im || '',
      telefone: src.telefone || '',
      email: src.email || '',
      endereco: [src.endereco, src.numero ? `nº ${src.numero}` : '', src.complemento, src.bairro].filter(Boolean).join(', '),
      cidade: src.cidade ? `${src.cidade}${src.uf ? `/${src.uf}` : ''}` : '',
      cep: src.cep || '',
    }
  })()

  // Logo da loja = logo ENVIADA na filial do emissor (aba Filiais › Geral), via /filiais/{id}/logo.
  // Muda conforme a filial da venda (logada); cai pro logo da empresa se a filial não tem.
  const filialEmissorId = filialEmissor?.id || null
  const { data: filialLogoData } = useQuery<any>({
    queryKey: ['pdv-filial-logo', filialEmissorId],
    queryFn: async () => (await api.get(`/filiais/${filialEmissorId}/logo`)).data,
    enabled: !!filialEmissorId,
    staleTime: 5 * 60 * 1000,
  })
  const logoLoja = filialLogoData?.logo_data || emissor?.logo_url || '/logo-empresa.jpeg'

  const subtotal = itens.reduce((s, i) => s + (i.quantidade * precoLinha(i) - i.desconto), 0)
  const totalItens = itens.reduce((s, i) => s + i.quantidade, 0)

  // Desconto da venda toda (rodapé). Tipo R$ ou %.
  const [descontoTipo, setDescontoTipo] = useState<'R$' | '%'>('R$')
  const [descontoInput, setDescontoInput] = useState('')
  const descontoVenda = (() => {
    const v = parseFloat(descontoInput.replace(',', '.')) || 0
    if (descontoTipo === '%') return Math.max(0, Math.min(subtotal, subtotal * v / 100))
    return Math.max(0, Math.min(subtotal, v))
  })()
  const totalFinal = Math.max(0, subtotal - descontoVenda)
  // Bike na troca: lançada como linha de pagamento (abate o saldo no painel de
  // pagamento). No resumo mostramos só uma linha informativa de "desconto" pra
  // espelhar o recibo — NÃO altera o totalFinal (a bike segue abatendo no pagamento).
  const bikeTotal = pagamentos
    .filter(p => p.forma === 'recebimento_bike')
    .reduce((s, p) => s + (Number(p.valor) || 0), 0)
  // "Desconto aplicado" exibido = desconto comercial da venda + valor da bike na troca.
  const descontoAplicadoExibido = descontoVenda + bikeTotal

  // ---- Vendas em espera (hold): salvar carrinho e retomar depois ----
  const { data: emEspera = [] } = useQuery<any[]>({
    queryKey: ['pdv-em-espera'],
    queryFn: async () => (await api.get('/pdv/em-espera')).data,
  })
  const salvarEspera = useMutation({
    mutationFn: async () => (await api.post('/pdv/em-espera', {
      cliente_id: cliente?.id ?? null,
      deposito_id: depositoId || null,
      filial_id: filialId || null,
      desconto_total: descontoVenda,
      observacao: observacao || null,
      itens: itens.map(i => ({
        produto_id: i.produto.id, quantidade: i.quantidade,
        preco_unitario: precoLinha(i), desconto: i.desconto,
        grade_valor_ids: (i.grade_valor_ids && i.grade_valor_ids.length) ? i.grade_valor_ids : undefined,
        conversao_id: i.conversao_id || undefined,
        qualidade_exigida: i.qualidade_exigida || undefined,
      })),
    })).data,
    onSuccess: () => {
      toast.success('Venda salva — retome quando quiser')
      setItens([]); setDescontoInput(''); setObservacao(''); setCliente(null)
      setVerTodos(false); setGrupoSelecionado(null); setSubgrupoSelecionado(null)  // volta pra logo, sem filtro
      qc.invalidateQueries({ queryKey: ['pdv-em-espera'] })
      const destino = confirmSair?.destino
      setConfirmSair(null)
      if (destino) nav(destino)
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao salvar venda'),
  })
  async function retomarEspera(id: number) {
    if (itens.length > 0) {
      toast.error('Finalize, salve ou limpe a venda atual antes de retomar outra')
      return
    }
    try {
      const d = (await api.get(`/pdv/em-espera/${id}`)).data
      setItens(d.itens.map((i: any) => ({
        produto: i.produto, quantidade: i.quantidade, desconto: i.desconto,
        // Campos industriais (se a espera os carregava)
        grade_valor_ids: (i.grade_valor_ids && i.grade_valor_ids.length) ? i.grade_valor_ids : undefined,
        grade_descritor: i.grade_descritor || undefined,
        conversao_id: i.conversao_id ?? null,
        unidade_label: i.unidade_label || undefined,
        preco_unitario: i.preco_unitario != null ? Number(i.preco_unitario) : undefined,
        qualidade_exigida: i.qualidade_exigida || undefined,
      })))
      setCliente(d.cliente || null)
      setObservacao(d.observacao || '')
      setDescontoTipo('R$')
      setDescontoInput(d.desconto ? String(d.desconto) : '')
      await api.delete(`/pdv/em-espera/${id}`)
      qc.invalidateQueries({ queryKey: ['pdv-em-espera'] })
      toast.success('Venda retomada')
      inputRef.current?.focus()
    } catch {
      toast.error('Não foi possível retomar a venda')
    }
  }
  async function descartarEspera(id: number) {
    try {
      await api.delete(`/pdv/em-espera/${id}`)
      qc.invalidateQueries({ queryKey: ['pdv-em-espera'] })
    } catch {
      toast.error('Não foi possível descartar')
    }
  }

  // Busca
  // BUG (03/07): a busca digitada ignorava o filtro/subfiltro selecionado e varria
  // todos os produtos. FIX: manda grupo/subgrupo ativos p/ o backend restringir a
  // busca ao escopo do filtro (subgrupo tem prioridade sobre grupo, como na vitrine).
  const { data: resultados = [] } = useQuery<ProdutoPDV[]>({
    queryKey: ['pdv-busca', busca, grupoSelecionado, subgrupoSelecionado],
    queryFn: async () => (await api.get('/pdv/buscar', { params: {
      q: busca,
      grupo_id: subgrupoSelecionado ? undefined : (grupoSelecionado || undefined),
      subgrupo_id: subgrupoSelecionado || undefined,
    } })).data,
    enabled: busca.length >= 1,
    // Mantém os resultados anteriores enquanto a nova busca carrega — evita que a
    // lista pisque pra vazio ao digitar/apertar Enter (ver corrida no handleBarcode).
    placeholderData: keepPreviousData,
  })

  // Finalizar — recebe a lista atual de linhas de pagamento
  const finalizar = useMutation({
    mutationFn: async ({ linhas, creditoGerado = 0 }: { linhas: LinhaPag[]; creditoGerado?: number }) => {
      if (!sessaoCaixa) {
        throw new Error('Caixa não aberto. Abra um caixa antes de finalizar a venda.')
      }
      if (!linhas.length) throw new Error('Adicione ao menos uma forma de pagamento')
      // Venda a prazo (gera conta a receber) exige cliente identificado.
      const formasPrazo = ['prazo', 'boleto', 'crediario', 'carteira']
      if (!cliente?.id && linhas.some(l => formasPrazo.includes(l.forma))) {
        throw new Error('Venda a prazo exige um cliente. Selecione o cliente antes de finalizar.')
      }
      const valorDinheiroRecebido = linhas
        .filter(l => l.forma === 'dinheiro')
        .reduce((s, l) => s + l.valor, 0)
      return (await api.post('/pdv/finalizar', {
        pagamentos: linhas,
        canal,  // FEATURE 30/06: caixa do PDV ('balcao') vs PDV-online ('online')
        cliente_id: cliente?.id || undefined,
        observacao: observacao.trim() || undefined,
        // Legacy: linha de maior valor
        forma_pagamento: linhas.reduce((a, b) => a.valor >= b.valor ? a : b).forma,
        parcelas: linhas.reduce((a, b) => a.valor >= b.valor ? a : b).parcelas,
        desconto_total: descontoVenda,
        deposito_id: depositoId || undefined,
        // Filial escolhida no modal "De qual filial sai a venda?" = EMISSOR (cupom + nota).
        // Pode ser uma filial "apenas fiscal". O backend registra a venda na filial do
        // OPERADOR (não nesta) e usa esta só como emissor. (FEATURE/FIX 30/06)
        filial_id: filialId || undefined,
        valor_recebido: valorDinheiroRecebido > 0 ? Math.max(valorDinheiroRecebido, parseFloat(valorRecebido || '0')) : 0,
        credito_gerado: creditoGerado > 0 ? creditoGerado : undefined,
        vendedor_cad_id: vendedorCadId || undefined,
        // Mecânico só vai quando o vendedor é a OFICINA (rastreio de quem executou).
        mecanico_cad_id: (vendedorEhOficina && mecanicoCadId) ? mecanicoCadId : undefined,
        os_id: osVinculadas[0]?.id || undefined,
        itens: itens.map(i => ({
          produto_id: i.produto.id,
          quantidade: i.quantidade,
          preco_unitario: precoLinha(i),
          desconto: i.desconto,
          // ---- Campos industriais (IBK) — preservados na Velore ----
          grade_valor_ids: (i.grade_valor_ids && i.grade_valor_ids.length) ? i.grade_valor_ids : undefined,
          conversao_id: i.conversao_id || undefined,
          qualidade_exigida: i.qualidade_exigida || 'PRIMEIRA',
        })),
      })).data
    },
    onSuccess: (data) => {
      setItensVenda([...itens])
      if (Number(data?.credito_gerado) > 0 && cliente) {
        toast.success(`R$ ${Number(data.credito_gerado).toFixed(2)} adicionados ao crédito de ${cliente.nome}`, { icon: '💳', duration: 5000 })
      }
      if (osVinculadas.length === 1) toast.success(`OS #${osVinculadas[0].numero} faturada`, { icon: '🔧' })
      // Anexa pagamentos/cliente/obs no objeto venda pra exibir no cupom
      setVendaConcluida({
        os_numero: osVinculadas[0]?.numero || null,
        ...data,
        pagamentos,
        cliente_nome: cliente?.nome || 'Consumidor Final',
        cliente_codigo: cliente?.codigo || null,
        cliente_documento: cliente?.documento || null,
        cliente_telefone: cliente?.telefone || null,
        cliente_endereco: cliente?.endereco || null,
        cliente_numero: cliente?.numero || null,
        cliente_complemento: cliente?.complemento || null,
        cliente_bairro: cliente?.bairro || null,
        cliente_cidade: cliente?.cidade || null,
        cliente_uf: cliente?.uf || null,
        observacao: observacao.trim() || null,
        incluir_cliente: incluirClienteCupom,
        // logoLoja = logo enviada na filial do emissor (cai pro logo da empresa).
        // É a mesma logo mostrada na tela; garante que ela apareça no topo do cupom.
        emissor: { ...emissor, logo_url: logoLoja },
      })
      setShowPagamento(false)
    },
    onError: (e: any) => toast.error(e?.message || e?.response?.data?.detail || 'Erro ao finalizar venda'),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['caixa-sessao-atual'] })
      if (cliente?.id) qc.invalidateQueries({ queryKey: ['cliente-credito', cliente.id] })
    },
  })

  // FIX (item #16 da lista, pedido do cliente): finaliza VÁRIAS OS juntas — chama
  // /pdv/finalizar uma vez POR OS (sem mudar a rota nem o banco), dividindo o
  // pagamento e o desconto proporcionalmente ao valor de cada OS. A ÚLTIMA absorve
  // o resto do arredondamento, pra fechar centavo a centavo. Cada OS gera sua
  // própria venda/nota — é o operador que faz um fluxo de pagamento só.
  async function finalizarMultiplasOS(linhas: LinhaPag[], creditoGerado: number) {
    if (!sessaoCaixa) { toast.error('Caixa não aberto. Abra um caixa antes de finalizar a venda.'); return }
    if (!linhas.length) { toast.error('Adicione ao menos uma forma de pagamento'); return }
    const formasPrazo = ['prazo', 'boleto', 'crediario', 'carteira']
    if (!cliente?.id && linhas.some(l => formasPrazo.includes(l.forma))) {
      toast.error('Venda a prazo exige um cliente. Selecione o cliente antes de finalizar.')
      return
    }
    const itensPorOs: Record<number, typeof itens> = {}
    itens.forEach((i: any) => {
      const oid = i._osId
      if (oid == null) return
      if (!itensPorOs[oid]) itensPorOs[oid] = []
      itensPorOs[oid].push(i)
    })
    const totalPorOs: Record<number, number> = {}
    osVinculadas.forEach(os => {
      totalPorOs[os.id] = (itensPorOs[os.id] || []).reduce(
        (s: number, i: any) => s + (precoLinha(i) * i.quantidade - i.desconto), 0)
    })
    const somaBruta = Object.values(totalPorOs).reduce((a, b) => a + b, 0) || 1
    const pesos = osVinculadas.map(os => (totalPorOs[os.id] || 0) / somaBruta)

    // Divide cada linha de pagamento entre as OS, proporcional ao peso — a ÚLTIMA
    // absorve o que sobrar, pra fechar exatamente (evita erro de "soma não confere").
    const linhasPorOs: LinhaPag[][] = pesos.map(() => [])
    linhas.forEach(l => {
      let restante = l.valor
      pesos.forEach((peso, idx) => {
        if (idx === pesos.length - 1) {
          if (restante > 0.004) linhasPorOs[idx].push({ ...l, valor: Math.round(restante * 100) / 100 })
          return
        }
        const parte = Math.round(l.valor * peso * 100) / 100
        if (parte > 0.004) {
          linhasPorOs[idx].push({ ...l, valor: parte })
          restante = Math.round((restante - parte) * 100) / 100
        }
      })
    })

    const valorDinheiroRecebidoTotal = linhas
      .filter(l => l.forma === 'dinheiro')
      .reduce((s, l) => s + l.valor, 0)

    let descAcumulado = 0
    const gerados: { numero: number; id: number; osNumero: number }[] = []
    setFinalizandoMultiplas(true)
    try {
      for (let idx = 0; idx < osVinculadas.length; idx++) {
        const os = osVinculadas[idx]
        const itensDaOs = itensPorOs[os.id] || []
        if (!itensDaOs.length) continue
        const descOs = idx === osVinculadas.length - 1
          ? Math.round((descontoVenda - descAcumulado) * 100) / 100
          : Math.round((descontoVenda * pesos[idx]) * 100) / 100
        descAcumulado = Math.round((descAcumulado + descOs) * 100) / 100
        const linhasOs = linhasPorOs[idx]
        const creditoOs = idx === osVinculadas.length - 1 ? creditoGerado : 0
        const { data } = await api.post('/pdv/finalizar', {
          pagamentos: linhasOs,
          canal,
          cliente_id: cliente?.id || undefined,
          observacao: observacao.trim() || undefined,
          forma_pagamento: linhasOs.length ? linhasOs.reduce((a, b) => a.valor >= b.valor ? a : b).forma : 'dinheiro',
          parcelas: linhasOs.length ? linhasOs.reduce((a, b) => a.valor >= b.valor ? a : b).parcelas : 1,
          desconto_total: descOs,
          deposito_id: depositoId || undefined,
          filial_id: filialId || undefined,
          valor_recebido: idx === 0 && valorDinheiroRecebidoTotal > 0
            ? Math.max(valorDinheiroRecebidoTotal, parseFloat(valorRecebido || '0')) : 0,
          credito_gerado: creditoOs > 0 ? creditoOs : undefined,
          vendedor_cad_id: vendedorCadId || undefined,
          mecanico_cad_id: (vendedorEhOficina && mecanicoCadId) ? mecanicoCadId : undefined,
          os_id: os.id,
          itens: itensDaOs.map((i: any) => ({
            produto_id: i.produto.id,
            quantidade: i.quantidade,
            preco_unitario: precoLinha(i),
            desconto: i.desconto,
            grade_valor_ids: (i.grade_valor_ids && i.grade_valor_ids.length) ? i.grade_valor_ids : undefined,
            conversao_id: i.conversao_id || undefined,
            qualidade_exigida: i.qualidade_exigida || 'PRIMEIRA',
          })),
        })
        gerados.push({ numero: data.numero, id: data.pedido_id, osNumero: os.numero })
      }
      setItensVenda([...itens])
      toast.success(`${gerados.length} vendas geradas — OS ${gerados.map(g => '#' + g.osNumero).join(', ')} faturadas`, { icon: '🔧' })
      const ultima = gerados[gerados.length - 1]
      setVendaConcluida({
        os_numero: gerados.map(g => g.osNumero).join(', '),
        pedido_id: ultima.id, numero: ultima.numero,
        _pedidos_multiplos: gerados,  // pra reimprimir o cupom de cada uma depois
        pagamentos,
        cliente_nome: cliente?.nome || 'Consumidor Final',
        cliente_codigo: cliente?.codigo || null,
        cliente_documento: cliente?.documento || null,
        cliente_telefone: cliente?.telefone || null,
        cliente_endereco: cliente?.endereco || null,
        cliente_numero: cliente?.numero || null,
        cliente_complemento: cliente?.complemento || null,
        cliente_bairro: cliente?.bairro || null,
        cliente_cidade: cliente?.cidade || null,
        cliente_uf: cliente?.uf || null,
        observacao: observacao.trim() || null,
        incluir_cliente: incluirClienteCupom,
        emissor: { ...emissor, logo_url: logoLoja },
      })
      setShowPagamento(false)
    } catch (e: any) {
      const feitos = gerados.map(g => `#${g.osNumero}`).join(', ')
      toast.error(
        (e?.response?.data?.detail || 'Erro ao finalizar venda múltipla') +
        (feitos ? ` — atenção: as OS ${feitos} JÁ foram faturadas antes do erro.` : ''),
        { duration: 8000 },
      )
    } finally {
      setFinalizandoMultiplas(false)
      qc.invalidateQueries({ queryKey: ['caixa-sessao-atual'] })
      if (cliente?.id) qc.invalidateQueries({ queryKey: ['cliente-credito', cliente.id] })
    }
  }

  // ============ MODAL DE GRADE / UNIDADE / QUALIDADE (industrial — IBK) ============
  // Abre quando o produto tem grades OU conversões de unidade OU é industrial
  // (qualidade exigida pelo cliente).
  const [gradeModal, setGradeModal] = useState<{
    produto: ProdutoPDV
    grades: GradePDVOption[]
    unidades: UnidadePDVOption[]
    is_industrial: boolean
    selecionadas: Record<number, number>  // tipo_id -> valor_id
    conversao_id: number | null            // null = unidade base
    qualidade_exigida: 'PRIMEIRA' | 'SEGUNDA' | 'TERCEIRA'
  } | null>(null)

  // Adiciona produto (qty default 1, ou customizado via atalho N*ean).
  // Antes de adicionar, checa se o produto tem grades/unidades alternativas
  // ou é industrial; nesse caso, abre o modal de seleção.
  const addProduto = useCallback(async (p: ProdutoPDV, qtd: number = 1) => {
    // KIT: explode nos componentes (filhos) — mostra os filhos no carrinho, não o pai.
    // Rateia o preço do kit entre os componentes (proporcional ao preço de venda deles),
    // igual ao Pedido. Se o kit não tem preço, usa o preço próprio de cada filho.
    if (p.tipo === 'kit') {
      try {
        const { data: comps } = await api.get(`/cadastros/produtos/${p.id}/kit-componentes`)
        if (!comps || comps.length === 0) { toast.error('Kit sem componentes cadastrados'); return }
        const kitPreco = Number(p.preco_venda) || 0
        const base = comps.reduce((s: number, c: any) => s + Number(c.componente_preco_venda || 0) * Number(c.quantidade || 1), 0)
        const kitTotal = kitPreco * qtd
        const novas = comps.map((c: any) => {
          const q = Number(c.quantidade || 1) * qtd
          let precoUnit = Number(c.componente_preco_venda || 0)
          if (kitPreco > 0 && base > 0) {
            const totalComp = kitTotal * ((Number(c.componente_preco_venda || 0) * Number(c.quantidade || 1)) / base)
            precoUnit = q > 0 ? totalComp / q : 0
          }
          return {
            produto: {
              id: c.componente_produto_id, codigo: c.componente_codigo || '', ean: null,
              descricao: c.componente_descricao || 'Item', preco_venda: precoUnit,
              estoque_atual: 0, unidade: c.componente_unidade || 'UN',
            } as ProdutoPDV,
            quantidade: q, desconto: 0, preco_unitario: precoUnit,
          }
        })
        setItens(prev => [...prev, ...novas])
        setBusca(''); inputRef.current?.focus()
        toast.success(`Kit "${p.descricao}" → ${comps.length} ${comps.length === 1 ? 'item' : 'itens'}`)
      } catch {
        toast.error('Erro ao carregar componentes do kit')
      }
      return
    }
    const [gradesRes, unidadesRes] = await Promise.allSettled([
      api.get(`/cadastros/produtos/${p.id}/grades`),
      api.get(`/cadastros/produtos/${p.id}/unidades-conversao`),
    ])
    const grades: GradePDVOption[] = gradesRes.status === 'fulfilled' ? (gradesRes.value.data || []) : []
    const unidades: UnidadePDVOption[] = unidadesRes.status === 'fulfilled'
      ? (unidadesRes.value.data || [])
          .filter((u: any) => u.ativo !== false)
          .map((u: any) => ({
            id: u.id, unidade: u.unidade, fator: Number(u.fator),
            preco_venda: u.preco_venda != null ? Number(u.preco_venda) : null,
            descricao_alt: u.descricao_alt,
          }))
      : []

    const isIndustrial = !!p.is_industrial
    if (grades.length > 0 || unidades.length > 0 || isIndustrial) {
      setGradeModal({
        produto: p, grades, unidades,
        is_industrial: isIndustrial,
        selecionadas: {}, conversao_id: null,
        qualidade_exigida: 'PRIMEIRA',
      })
      return
    }

    // Fluxo de teclado: ao adicionar, o foco vai pro campo de QUANTIDADE do item.
    // BUG (03/07): addProduto é useCallback([]) e NÃO enxerga o `itens` atual, então o
    // índice era calculado DENTRO do updater e lido FORA dele — mas setItens é
    // assíncrono/batelado, então focarItemCampo recebia -1 e caía no foco da busca
    // ("só adicionava e pronto"). FIX: o próprio updater agenda o alvo de foco
    // (focoPendenteRef) com o índice certo (novo = fim; agrupado = índice existente);
    // o effect [itens] foca a quantidade após o render.
    setItens(prev => {
      // Só agrupa quando NÃO tem variação de grade/conversão.
      const exIdx = prev.findIndex(i => i.produto.id === p.id && !i.grade_valor_ids?.length && !i.conversao_id)
      if (exIdx >= 0) {
        focoPendenteRef.current = { idx: exIdx, campo: 'qtd' }
        return prev.map((i, k) => k === exIdx ? { ...i, quantidade: i.quantidade + qtd } : i)
      }
      focoPendenteRef.current = { idx: prev.length, campo: 'qtd' }
      return [...prev, { produto: p, quantidade: qtd, desconto: 0 }]
    })
    // 14/10: o avisarEstoque só era chamado ao MUDAR a quantidade de um item já
    // no carrinho — adicionar e finalizar (o caminho mais comum) passava em
    // silêncio. Avisa aqui também, com o produto que acabou de entrar.
    const _est = Number(p.estoque_atual)
    if (Number.isFinite(_est)) {
      if (_est < 0) {
        toast(`"${p.descricao}" está com estoque NEGATIVO (${_est}). Confira o cadastro.`,
          { icon: '🚨', duration: 7000 })
      } else if (_est === 0) {
        toast(`"${p.descricao}" está SEM ESTOQUE no sistema.`,
          { icon: '⚠️', duration: 6000 })
      }
    }
    setBusca('')
  }, [])

  function confirmarGrade() {
    if (!gradeModal) return
    const tiposNecessarios = new Set(gradeModal.grades.map(g => g.tipo_id))
    if (Object.keys(gradeModal.selecionadas).length < tiposNecessarios.size) {
      toast.error('Escolha todas as opções')
      return
    }
    const ids = Object.values(gradeModal.selecionadas)
    const labels = ids
      .map(vid => gradeModal.grades.find(g => g.id === vid)?.label || '')
      .filter(Boolean)
    const descritor = labels.join(' · ')
    const conv = gradeModal.conversao_id
      ? gradeModal.unidades.find(u => u.id === gradeModal.conversao_id)
      : null
    const precoBase = Number(gradeModal.produto.preco_venda) || 0
    const preco_unitario = conv
      ? (conv.preco_venda != null ? conv.preco_venda : precoBase * conv.fator)
      : precoBase
    const idxFoco = itens.length  // item novo entra no fim da lista
    setItens(prev => [...prev, {
      produto: gradeModal.produto,
      quantidade: 1,
      desconto: 0,
      grade_valor_ids: ids.length ? ids : undefined,
      grade_descritor: descritor || undefined,
      conversao_id: conv ? conv.id : null,
      unidade_label: conv ? conv.unidade : gradeModal.produto.unidade,
      preco_unitario,
      qualidade_exigida: gradeModal.is_industrial ? gradeModal.qualidade_exigida : undefined,
    }])
    setGradeModal(null)
    setBusca('')
    // Fluxo de teclado: foca a quantidade do item recém-adicionado (não a busca).
    focarItemCampo(idxFoco, 'qtd')
  }

  // Busca: setas navegam os resultados, Enter adiciona o destacado (ou busca EAN), Esc limpa.
  // Suporta atalho `N*EAN` (ex: 3*7891234567890 adiciona 3 unidades).
  async function handleBarcode(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      if (resultados.length > 0) setBuscaIdx(i => Math.min(i + 1, resultados.length - 1))
      else (pdvRootRef.current?.querySelector('[data-nav]') as HTMLElement | null)?.focus()  // entra na vitrine/filtros
      return
    }
    if (e.key === 'ArrowUp') { e.preventDefault(); setBuscaIdx(i => Math.max(i - 1, 0)); return }
    if (e.key === 'Escape') { if (busca) { e.preventDefault(); setBusca('') } return }
    if (e.key === 'Enter' && busca.trim()) {
      e.preventDefault()
      let termo = busca.trim()
      let qtd = 1
      const m = termo.match(/^(\d+(?:[.,]\d+)?)\s*[\*xX]\s*(.+)$/)
      if (m) {
        qtd = parseFloat(m[1].replace(',', '.')) || 1
        termo = m[2].trim()
      }
      // 1) Tenta código/EAN exato (encodeURIComponent p/ nomes com espaço/acento
      //    não quebrarem a URL).
      try {
        const { data } = await api.get(`/pdv/buscar-ean/${encodeURIComponent(termo)}`)
        if (data) { addProduto(data, qtd); return }
      } catch { /* não é EAN/código — segue pra busca por nome */ }
      // 2) Usa os resultados já carregados na tela, se houver.
      if (resultados.length > 0) {
        addProduto(resultados[Math.min(buscaIdx, resultados.length - 1)] || resultados[0], qtd)
        return
      }
      // 3) BUG (03/07): a lista de resultados podia estar VAZIA por um instante (a query
      //    reinicia ao trocar o termo), então o Enter rápido caía direto no "Produto não
      //    encontrado" mesmo com o produto existindo. FIX: faz uma busca fresca aqui e só
      //    erra se realmente não vier nada — elimina a corrida entre o Enter e a busca.
      try {
        // Mesmo escopo de filtro da lista visível (ver query da busca acima).
        const { data } = await api.get('/pdv/buscar', { params: {
          q: termo,
          grupo_id: subgrupoSelecionado ? undefined : (grupoSelecionado || undefined),
          subgrupo_id: subgrupoSelecionado || undefined,
        } })
        if (Array.isArray(data) && data.length > 0) { addProduto(data[0], qtd); return }
      } catch { /* ignora — cai no erro abaixo */ }
      toast.error('Produto não encontrado')
    }
  }

  // Navegação TOTAL por teclado: setas movem o foco entre filtros (chips) e produtos da
  // vitrine; Enter no botão focado já dispara o clique (nativo). Funciona quando o foco
  // NÃO está num input (a busca tem navegação própria, e ArrowDown dela entra aqui).
  function colsNaLinha(els: HTMLElement[], i: number): number {
    const top = els[i].getBoundingClientRect().top
    const perto = (a: number, b: number) => Math.abs(a - b) < 4
    let ini = i, fim = i
    while (ini - 1 >= 0 && perto(els[ini - 1].getBoundingClientRect().top, top)) ini--
    while (fim + 1 < els.length && perto(els[fim + 1].getBoundingClientRect().top, top)) fim++
    return Math.max(1, fim - ini + 1)
  }
  function navTeclado(e: React.KeyboardEvent) {
    if (!['ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight'].includes(e.key)) return
    const cur = document.activeElement as HTMLElement | null
    const tag = (cur?.tagName || '').toLowerCase()
    if (tag === 'input' || tag === 'textarea' || tag === 'select') return  // a busca cuida das setas dela
    const root = pdvRootRef.current
    if (!root) return
    const els = Array.from(root.querySelectorAll('[data-nav]')) as HTMLElement[]
    if (!els.length) return
    let i = cur ? els.indexOf(cur) : -1
    if (i < 0) { e.preventDefault(); els[0].focus(); return }
    let next = i
    if (e.key === 'ArrowRight') next = i + 1
    else if (e.key === 'ArrowLeft') next = i - 1
    else { const cols = colsNaLinha(els, i); next = e.key === 'ArrowDown' ? i + cols : i - cols }
    if (e.key === 'ArrowUp' && next < 0) { e.preventDefault(); inputRef.current?.focus(); return }  // sobe pra busca
    next = Math.max(0, Math.min(els.length - 1, next))
    if (next !== i) { e.preventDefault(); els[next].focus() }
  }

  // Acesso rápido aos campos do checkout (botões + atalhos F6..F9)
  function focarCampo(sel: string) {
    const el = pdvRootRef.current?.querySelector(sel) as HTMLElement | null
    if (el) { el.focus(); el.scrollIntoView({ block: 'nearest' }); if (el instanceof HTMLInputElement) el.select?.() }
  }
  // Fluxo de teclado nas linhas do carrinho: foca (e seleciona) um campo de um item
  // pelo índice. Usa data-attrs nos inputs (data-item-qtd/preco/desc). Se o input já
  // existe (encadeamento qtd→preço→desc), foca na hora; se ainda não (item recém
  // adicionado), agenda o foco no ref e o effect [itens] resolve após o render.
  function focarItemCampo(idx: number, campo: 'qtd' | 'preco' | 'desc') {
    if (idx < 0) { inputRef.current?.focus(); return }
    const el = pdvRootRef.current?.querySelector(`[data-item-${campo}="${idx}"]`) as HTMLInputElement | null
    if (el) { el.focus(); el.select?.() }
    else focoPendenteRef.current = { idx, campo }
  }

  // Avisa (não bloqueia) quando a quantidade passa do estoque disponível do produto.
  // Só para itens em unidade base (conversao_id nulo) e com estoque conhecido > 0 —
  // evita falso alarme em unidades convertidas e em componentes de kit (estoque 0).
  function avisarEstoque(item: ItemCarrinho, qtd: number) {
    if (item.conversao_id) return
    const est = Number(item.produto.estoque_atual)
    if (!Number.isFinite(est)) return
    // BUG (14/10): a condição era `est > 0 && qtd > est`, então estoque ZERO ou
    // NEGATIVO nunca avisava — justamente o caso que mais importa. Agora os três
    // cenários avisam, cada um com a mensagem do seu problema.
    if (est < 0) {
      toast(`"${item.produto.descricao}" está com estoque NEGATIVO (${est}). Confira o cadastro.`,
        { icon: '🚨', duration: 7000 })
    } else if (est === 0) {
      toast(`"${item.produto.descricao}" está SEM ESTOQUE no sistema.`,
        { icon: '⚠️', duration: 6000 })
    } else if (qtd > est) {
      toast(`Estoque de "${item.produto.descricao}" é ${est} — acima do disponível`, { icon: '⚠️' })
    }
  }

  // Esc num campo do item (qtd/preço/desc) remove aquele produto e volta pra busca.
  function removerItem(idx: number) {
    setItens(prev => prev.filter((_, i) => i !== idx))
    inputRef.current?.focus()
  }

  function irCliente() { focarCampo('#pdv-cliente input') }
  function irVendedor() { setAbrirVendedor(true); setTimeout(() => focarCampo('#pdv-vendedor'), 0) }
  function irObservacao() { focarCampo('#pdv-obs') }
  function irDesconto() { focarCampo('#pdv-desconto') }

  // Abre o painel de pagamento.
  // ANTES: com >1 filial, forçava o seletor de filial/depósito TODA vez que entrava
  // no pagamento — redundante, já que o usuário escolhe a filial (chip do topo) e o
  // depósito (menu "Mais ações") lá em cima, e ambos ficam persistidos.
  // FIX (03/07): só força o seletor quando AINDA não há filial ou depósito definidos
  // (rede de segurança da 1ª venda); caso contrário abre o pagamento direto.
  function prosseguirPagamento() {
    setPagamentos([])
    setValorRecebido('')
    if (precisaEscolherFilial && (filialId === '' || depositoId === '')) {
      setShowSeletorFilial(true)
    } else {
      setShowPagamento(true)
    }
  }

  function iniciarPagamento() {
    // PEDIDO DO CLIENTE (19/10): venda não fecha sem cliente completo
    // (nome+sobrenome, CPF/CNPJ, endereço e telefone). Gate antes do de vendedor.
    if (faltaNoCliente(cliente).length > 0) {
      setShowClienteGate(true)
      return
    }
    // FEATURE (03/07): venda não é permitida sem vendedor escolhido. Se há vendedores
    // disponíveis e nenhum foi selecionado, abre o gate de escolha rápida em vez de pagar.
    if (vendedoresPDV.length > 0 && !vendedorCadId) {
      setBuscaVendedor(''); setGateHi(0); setShowVendedorGate(true)
      return
    }
    // OFICINA exige o mecânico que executou. Reabre o gate (que mostra o passo do
    // mecânico, já que o vendedor OFICINA está selecionado). (06/07)
    if (vendedorEhOficina && !mecanicoCadId) {
      setShowVendedorGate(true)
      return
    }
    prosseguirPagamento()
  }

  // Escolha de vendedor DENTRO do gate: se for a OFICINA, não segue direto —
  // mantém o gate aberto no passo do mecânico; senão, segue pro pagamento. (06/07)
  function escolherVendedorNoGate(v: any) {
    setVendedorCadId(v.id); setBuscaVendedor(v.nome)
    const ehOfi = String(v.nome || '').trim().toLowerCase().startsWith('oficina')
    if (ehOfi) { setGateHi(0); return }  // fica no gate → aparece o campo Mecânico
    setShowVendedorGate(false); prosseguirPagamento()
  }
  // Confirma o passo do mecânico (OFICINA) e segue pro pagamento.
  function confirmarMecanicoNoGate() {
    if (!mecanicoCadId) { toast.error('Selecione o mecânico'); return }
    setShowVendedorGate(false); prosseguirPagamento()
  }

  // Atalhos
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'F2' && itens.length > 0) { e.preventDefault(); iniciarPagamento() }
      if (e.key === 'F3') { e.preventDefault(); setShowServicosOS(true) }
      if (e.key === 'F4') { e.preventDefault(); inputRef.current?.focus() }
      if (e.key === 'F6') { e.preventDefault(); irCliente() }
      if (e.key === 'F7') { e.preventDefault(); irVendedor() }
      if (e.key === 'F8') { e.preventDefault(); irObservacao() }
      if (e.key === 'F9') { e.preventDefault(); irDesconto() }
      if (e.key === 'Escape') {
        e.preventDefault()
        // ESC = VOLTAR em etapas: fecha o painel/menu aberto do topo; só sai do PDV
        // pro sistema quando NADA está aberto. (Veja também o Enter logo abaixo.)
        if (confirmSair) { setConfirmSair(null); return }          // Esc no modal = continuar na venda (fica)
        if (gradeModal) { setGradeModal(null); return }
        if (showSeletorFilial) { setShowSeletorFilial(false); return }
        if (showVendedorGate) { setShowVendedorGate(false); return }  // gate de vendedor: Esc cancela e volta pra venda
        if (showClienteGate) { setShowClienteGate(false); return }    // gate de cliente: idem
        if (abrirVendedor) { setAbrirVendedor(false); return }     // dropdown do vendedor fecha 1º (inclusive durante o pagamento)
        if (showPagamento) { setShowPagamento(false); setFormaPag(''); return }
        // BUG (relatado 01/07): ESC na tela "Venda Finalizada!" só fazia setVendaConcluida(null),
        // fechando o modal MAS deixando o carrinho/cliente/pagamentos sujos — a venda já concluída
        // (cupom impresso, registrada) voltava a aparecer "em andamento" na tela.
        // FIX: usar novaVenda() (mesmo reset completo do botão "Nova Venda") em vez de só fechar o modal.
        if (vendaConcluida) { novaVenda(); return }
        if (showResumoCaixa) { setShowResumoCaixa(false); return }
        if (showFecharCaixa) { setShowFecharCaixa(false); return }
        if (showSangria) { setShowSangria(false); return }
        if (showSuprimento) { setShowSuprimento(false); return }
        if (showEntrada) { setShowEntrada(false); return }
        if (showSaida) { setShowSaida(false); return }
        if (showAbrirCaixa) { setShowAbrirCaixa(false); return }
        if (showAddCredito) { setShowAddCredito(false); return }
        if (showServicosOS) { setShowServicosOS(false); return }
        if (showVendasDoDia) { setShowVendasDoDia(false); return }
        if (showTutorial) { setShowTutorial(false); return }
        if (showMenu) { setShowMenu(false); return }
        if (busca) { setBusca(''); return }                        // 1º limpa busca ativa, depois sai
        tentarSair('/')                                            // nada aberto → volta pro sistema
      }
      // Enter no modal "Venda em andamento" = descartar a venda e voltar (Esc = continuar).
      // Só intercepta enquanto o modal está aberto, pra não atrapalhar o Enter do PDV.
      if (e.key === 'Enter' && confirmSair) {
        e.preventDefault()
        const destino = confirmSair.destino || '/'
        setItens([]); setDescontoInput(''); setConfirmSair(null); nav(destino)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [itens.length, precisaEscolherFilial, busca, confirmSair, gradeModal, showSeletorFilial,
      showPagamento, vendaConcluida, showResumoCaixa, showFecharCaixa, showSangria, showSuprimento, showSaida, showEntrada, showAbrirCaixa,
      showAddCredito, showServicosOS, showVendasDoDia, showTutorial, abrirVendedor, showMenu,
      showVendedorGate, showClienteGate, vendedorCadId])

  useEffect(() => { inputRef.current?.focus() }, [])
  // teclado da busca: ao digitar, volta pro 1º resultado; mantém o destacado visível
  useEffect(() => { setBuscaIdx(0) }, [busca])
  useEffect(() => {
    resultsRef.current?.querySelector(`[data-bidx="${buscaIdx}"]`)?.scrollIntoView({ block: 'nearest' })
  }, [buscaIdx])

  // Avisa antes de fechar/recarregar a aba quando há venda em aberto
  useEffect(() => {
    if (itens.length === 0) return
    function onBeforeUnload(e: BeforeUnloadEvent) {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [itens.length])

  // Pede confirmação antes de sair do PDV se há itens no carrinho
  function tentarSair(destino: string) {
    if (itens.length === 0) { nav(destino); return }
    setConfirmSair({ destino })
  }

  function novaVenda() {
    setItens([]); setVendaConcluida(null); setBusca(''); setValorRecebido(''); setFormaPag('')
    // 03/07 (pedido do usuário): ao finalizar/nova venda, volta pra LOGO e SEM filtro
    // (limpa grupo/subgrupo além do verTodos — senão a vitrine ficava presa no filtro).
    setVerTodos(false); setGrupoSelecionado(null); setSubgrupoSelecionado(null)
    setItensVenda([]); setDescontoInput(''); setPagamentos([])
    setObservacao(''); setCliente(null); setBuscaCliente(''); setIncluirClienteCupom(true)
    setOsVinculadas([]); setVendedorCadId(''); setBuscaVendedor(''); setMecanicoCadId('')
    inputRef.current?.focus()
  }

  // Carrega uma OS concluída no carrinho pra cobrar no PDV (não rebaixa estoque).
  function carregarOS(os: any) {
    const cartItens = (os.itens || []).map((it: any) => ({
      produto: {
        id: it.produto_id, codigo: '', ean: null, descricao: it.descricao,
        preco_venda: it.preco_unit, estoque_atual: 0, unidade: 'UN',
      },
      quantidade: it.quantidade,
      desconto: it.desconto || 0,
      preco_unitario: it.preco_unit,
      _osId: os.id,  // FIX (item #16): marca de qual OS veio, usado no split ao juntar várias
    }))
    if (cartItens.length === 0) { toast.error('OS sem itens pra cobrar'); return }
    setItens(cartItens)
    setCliente({ id: os.cliente_id, nome: os.cliente_nome })
    // Traz o vendedor da OS pro PDV (antes só vinha cliente+itens e o operador
    // tinha que reselecionar o vendedor → comissão saía sem vendedor/errada).
    // Usa o vendedor_nome vindo do backend (a lista de vendedores do PDV é
    // filtrada por filial e pode não conter a OFICINA, que é global).
    if (os.vendedor_cad_id) {
      setVendedorCadId(os.vendedor_cad_id)
      if (os.vendedor_nome) setBuscaVendedor(os.vendedor_nome)
    }
    // FIX (relatado 10/09): o mecânico já cadastrado na OS (tecnico_cad_id) nunca
    // vinha pro PDV — o operador tinha que selecionar de novo manualmente, mesmo
    // a OS já indicando quem fez o serviço.
    if (os.tecnico_cad_id) setMecanicoCadId(os.tecnico_cad_id)
    setOsVinculadas([os])
    setShowServicosOS(false)
    toast.success(`OS #${os.numero} carregada — ${cartItens.length} item(ns)`)
  }

  // FIX (item #16 da lista, pedido do cliente — "juntar mais de uma OS do mesmo
  // cliente no PDV pra passar junto"): sem mexer no banco. Junta os itens de TODAS
  // as OS marcadas num carrinho só (cada item guarda _osId); o pagamento é feito
  // uma vez só e, ao finalizar, é dividido proporcionalmente — cada OS ainda vira
  // sua própria venda/nota (ver finalizarMultiplasOS, mais abaixo).
  function carregarOSMultiplas(osArray: any[]) {
    if (!osArray.length) return
    const cartItens = osArray.flatMap((os: any) => (os.itens || []).map((it: any) => ({
      produto: {
        id: it.produto_id, codigo: '', ean: null, descricao: it.descricao,
        preco_venda: it.preco_unit, estoque_atual: 0, unidade: 'UN',
      },
      quantidade: it.quantidade,
      desconto: it.desconto || 0,
      preco_unitario: it.preco_unit,
      _osId: os.id,
    })))
    if (cartItens.length === 0) { toast.error('OS sem itens pra cobrar'); return }
    setItens(cartItens)
    const primeira = osArray[0]
    setCliente({ id: primeira.cliente_id, nome: primeira.cliente_nome })
    if (primeira.vendedor_cad_id) {
      setVendedorCadId(primeira.vendedor_cad_id)
      if (primeira.vendedor_nome) setBuscaVendedor(primeira.vendedor_nome)
    }
    if (primeira.tecnico_cad_id) setMecanicoCadId(primeira.tecnico_cad_id)
    setOsVinculadas(osArray)
    setShowServicosOS(false)
    toast.success(`${osArray.length} OS carregadas junto — ${cartItens.length} item(ns)`)
  }

  // FIX (bug relatado — carrinho vazio ao converter orçamento em venda): este
  // bloco estava colado DENTRO de carregarOS() acima, chamando useRef/useEffect
  // fora do corpo do componente (viola as Regras dos Hooks) — nunca executava,
  // porque carregarOS só é chamada pelo fluxo de Balcão, não pelo de orçamento.
  // Agora é um efeito de verdade, no nível do componente, que roda ao montar o
  // PDV e lê o carrinho deixado pelo Orçamento (Orcamentodetalhe.tsx → converter).
  const orcamentoRef = useRef(false)
  useEffect(() => {
    if (orcamentoRef.current) return
    const bruto = sessionStorage.getItem('orcamento_para_pdv')
    if (!bruto) return
    orcamentoRef.current = true
    sessionStorage.removeItem('orcamento_para_pdv')   // consome: não recarrega no refresh
    try {
      const orc = JSON.parse(bruto)
      const cartItens = (orc.itens || []).map((it: any) => ({
        produto: {
          id: it.produto_id, codigo: '', ean: null, descricao: it.descricao,
          preco_venda: it.preco_unit, estoque_atual: 0, unidade: 'UN',
        },
        quantidade: it.quantidade,
        desconto: it.desconto || 0,
        preco_unitario: it.preco_unit,   // preço congelado do orçamento
      }))
      if (cartItens.length === 0) return
      setItens(cartItens)
      if (orc.cliente_id) setCliente({ id: orc.cliente_id, nome: orc.cliente_nome || '' })
      if (orc.vendedor_cad_id) {
        setVendedorCadId(orc.vendedor_cad_id)
        if (orc.vendedor_nome) setBuscaVendedor(orc.vendedor_nome)
      }
      if (orc.desconto > 0) {
        setDescontoTipo('R$')          // orçamento guarda desconto em valor, não em %
        setDescontoInput(String(orc.desconto))
      }
      setObservacao(`Orçamento #${orc.orcamento_numero}`)
      toast.success(`Orçamento #${orc.orcamento_numero} carregado — ${cartItens.length} item(ns)`)
    } catch {
      toast.error('Não foi possível carregar o orçamento no carrinho')
    }
  }, [])
  // Balcão: quando a NovaOS manda pro PDV via `nav('/pdv', { state:{ balcaoOS } })`,
  // busca essa OS (já concluída) e carrega no carrinho automaticamente. Roda uma
  // única vez e limpa o state pra não recarregar em re-render/refresh.
  const balcaoRef = useRef(false)
  useEffect(() => {
    const numero = (location.state as any)?.balcaoOS
    if (!numero || balcaoRef.current) return
    balcaoRef.current = true
    nav(location.pathname, { replace: true, state: null })
    ;(async () => {
      try {
        const lista = (await api.get('/servicos/pdv/para-faturar', { params: { q: String(numero) } })).data
        const os = (lista || []).find((o: any) => o.numero === numero && o.faturavel !== false)
        if (os) carregarOS(os)
        else toast.error(`OS #${numero} não está disponível pra cobrar`)
      } catch {
        toast.error('Não consegui carregar a OS do balcão')
      }
    })()
  }, [location.state])

  // ============ VENDA CONCLUÍDA ============
  if (vendaConcluida) {
    const troco = vendaConcluida.troco || 0
    return (
      <div className="fixed inset-0 bg-ink-950 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6 sm:p-8">
          <div className="text-center">
            <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
              <Check size={40} className="text-emerald-600" />
            </div>
            <h2 className="text-2xl font-bold text-ink-900 mb-1">Venda Finalizada!</h2>
            <p className="text-ink-500 mb-5">Pedido #{vendaConcluida.numero}</p>
          </div>

          <div className="bg-ink-50 rounded-xl p-4 mb-5 space-y-2">
            {vendaConcluida.cliente_nome && vendaConcluida.cliente_nome !== 'Consumidor Final' && (
              <div className="flex justify-between text-sm pb-2 border-b border-ink-200">
                <span className="text-ink-600">Cliente</span>
                <span className="font-medium text-ink-900 text-right">
                  {vendaConcluida.cliente_nome}
                  {vendaConcluida.cliente_documento && (
                    <span className="block text-[11px] text-ink-500">{vendaConcluida.cliente_documento}</span>
                  )}
                </span>
              </div>
            )}
            <div className="flex justify-between text-sm">
              <span className="text-ink-600">Total</span>
              <span className="text-2xl font-bold text-ink-900">{formatMoney(vendaConcluida.total)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-ink-600">Pagamento</span>
              <span className="capitalize font-medium">{vendaConcluida.forma_pagamento.replace('_', ' ')}</span>
            </div>
            {vendaConcluida.observacao && (
              <div className="pt-2 border-t border-ink-200">
                <span className="text-ink-600 text-sm">Observação</span>
                <p className="text-sm text-ink-900 mt-1 whitespace-pre-line">{vendaConcluida.observacao}</p>
              </div>
            )}
            {troco > 0 && (
              <div className="flex justify-between text-sm pt-2 border-t border-ink-200">
                <span className="text-ink-600">Troco</span>
                <span className="text-2xl font-bold text-emerald-600">{formatMoney(troco)}</span>
              </div>
            )}
          </div>

          {/* Impressão 80mm — só o Recibo (01/07: removido "Cupom Não Fiscal", que era praticamente igual) */}
          <div className="mb-4">
            <button onClick={() => imprimirCupomPdf(vendaConcluida.pedido_id, 'recibo')}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-ink-100 hover:bg-ink-200 text-ink-800 font-medium transition-colors">
              <Printer size={18} /> Imprimir Recibo
            </button>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <button onClick={novaVenda}
              className="flex items-center justify-center gap-2 py-3.5 rounded-xl bg-brand-600 hover:bg-brand-700 text-white font-semibold transition-colors">
              <RotateCcw size={18} /> Nova Venda
            </button>
            {/* BUG (11/08): este botão chamava `/fiscal/emitir`, que passa pelo
                get_fiscal_provider(). Sem FISCAL_API_TOKEN o provider é o MockProvider:
                ele devolvia "autorizado" com chave INVENTADA sem falar com a SEFAZ —
                cupom FALSO. (Foi assim que entraram 4 NFC-e com chave de SP numa empresa
                do CE.) FIX: emissor PRÓPRIO, que assina, gera QR-Code e transmite de
                verdade — o mesmo que autoriza as NF-e. Resposta tem outro formato:
                `autorizada` (bool) + cStat/xMotivo/protocolo, não `status`/`mensagem`. */}
            <button onClick={() => {
              toast.loading('Emitindo NFC-e...')
              api.post(`/nfe-proprio/emitir-nfce/${vendaConcluida.pedido_id}`, null, { timeout: 120000 })
                .then(r => {
                  toast.dismiss()
                  const d = r.data || {}
                  if (d.autorizada) {
                    toast.success(
                      d.ja_existia
                        ? `NFC-e já autorizada (nº ${d.numero})`
                        : `NFC-e autorizada! nº ${d.numero}${d.protocolo ? ` · protocolo ${d.protocolo}` : ''}`,
                      { duration: 6000 })
                  } else {
                    // Motivo REAL da SEFAZ (ou da validação local: CSC ausente, etc).
                    toast.error(`NFC-e não autorizada: ${d.xMotivo || d.erro || 'motivo não informado'}`,
                      { duration: 10000 })
                  }
                })
                .catch((e: any) => {
                  toast.dismiss()
                  toast.error(e?.response?.data?.detail || 'Erro ao emitir NFC-e', { duration: 8000 })
                })
            }}
              className="flex items-center justify-center gap-2 py-3.5 rounded-xl bg-amber-600 hover:bg-amber-700 text-white font-semibold transition-colors">
              <FileText size={18} /> Emitir NFC-e
            </button>
          </div>
        </div>
      </div>
    )
  }

  // ============ GATE DE FILIAL ============
  // PDV opera sobre UMA loja (caixa, cupom e estoque são por filial). Em modo
  // consolidado / sem filial no topo, em vez de abrir o caixa e bater no 400 do
  // backend ("Selecione a loja..."), exige escolher a loja AQUI. Vale pra todos,
  // inclusive admin. Ao escolher, vira a filial atual (sai do consolidado) e o
  // caixa/sessão recarregam sob o header X-Filial-Id da loja escolhida.
  if (semFilialLoja) {
    return (
      <div className="fixed inset-0 bg-ink-950 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6 sm:p-8">
          <div className="text-center mb-6">
            <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
              <Store size={32} className="text-amber-600" />
            </div>
            <h2 className="text-xl font-bold text-ink-900 mb-1">Escolha uma loja</h2>
            <p className="text-sm text-ink-500">
              O PDV vende por loja (caixa, cupom e estoque são da filial). Selecione
              a loja para abrir o caixa e operar.
            </p>
          </div>

          {/* Deixa claro o PORQUÊ: só cai aqui conta ADM em modo consolidado (todas
              as filiais) sem nenhuma loja selecionada. Operador comum já entra na
              filial dele e não vê esta tela. */}
          <div className="flex items-start gap-2 mb-5 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2.5">
            <Store size={15} className="text-amber-600 mt-0.5 shrink-0" />
            <p className="text-xs text-amber-800 leading-relaxed">
              Esta tela aparece porque você está numa <b>conta administrativa</b> em
              <b> modo consolidado</b> (todas as filiais) e <b>nenhuma loja está selecionada</b>.
              Um operador comum já entra direto na filial dele. Escolha abaixo a loja
              em que vai operar o PDV.
            </p>
          </div>
          {filiaisAtivas.length === 0 ? (
            <p className="text-center text-sm text-ink-500 py-4">
              Nenhuma filial ativa cadastrada. Cadastre uma filial em Cadastros › Filiais.
            </p>
          ) : (
            <div className="space-y-2 max-h-[50vh] overflow-y-auto">
              {filiaisAtivas.map(f => (
                <button
                  key={f.id}
                  onClick={() => {
                    setFilialAtual(f as any)
                    setModoConsolidado(false)
                    try { localStorage.setItem('pain_filial_explicita', 'true') } catch {}
                    setFilialId(f.id)
                    qc.invalidateQueries()
                  }}
                  className="w-full flex items-center gap-3 p-3.5 rounded-xl bg-ink-50 hover:bg-brand-50 border border-ink-200 hover:border-brand-300 text-left transition-colors"
                >
                  <div className="w-9 h-9 rounded-lg bg-brand-100 flex items-center justify-center shrink-0">
                    <Store size={18} className="text-brand-600" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-semibold text-ink-900 truncate">
                      {f.nome_fantasia || f.razao_social || `Loja ${f.codigo}`}
                    </div>
                    {(f.cidade || f.uf) && (
                      <div className="text-[11px] text-ink-500 truncate">
                        {[f.cidade, f.uf].filter(Boolean).join(' / ')}
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    )
  }

  // ============ PDV PRINCIPAL ============
  return (
    <div data-no-enter-advance ref={pdvRootRef} onKeyDown={navTeclado} className={`pdv-root ${escuro ? 'pdv-dark' : 'pdv-light'} fixed inset-0 ${T.outer} flex flex-col lg:flex-row z-40 overflow-hidden`}>
      {pdfModal}
      {/* CSS overlay — em modo escuro, troca os tons claros pelos escuros sem reescrever cada classe.
          Escopo limitado a .pdv-dark, não vaza pra resto do app. */}
      <style>{`
        .pdv-dark .bg-white { background-color: #343945 !important; }
        .pdv-dark .bg-ink-50 { background-color: #3a4250 !important; }
        .pdv-dark .bg-ink-100 { background-color: #21242d !important; }
        .pdv-dark .bg-ink-200 { background-color: #434c5e !important; }
        .pdv-dark .text-ink-900 { color: #f6f7f9 !important; }
        .pdv-dark .text-ink-800 { color: #d5dae2 !important; }
        .pdv-dark .text-ink-700 { color: #b1bac8 !important; }
        .pdv-dark .text-ink-600 { color: #8794a8 !important; }
        .pdv-dark .text-ink-500 { color: #8794a8 !important; }
        .pdv-dark .text-ink-400 { color: #68768d !important; }
        .pdv-dark .text-ink-300 { color: #525e74 !important; }
        .pdv-dark .border-ink-200 { border-color: #434c5e !important; }
        .pdv-dark .border-ink-100 { border-color: #3a4250 !important; }
        .pdv-dark .hover\\:bg-ink-200:hover { background-color: #434c5e !important; }
        .pdv-dark .hover\\:bg-ink-100:hover { background-color: #3a4250 !important; }
        .pdv-dark .focus\\:bg-white:focus { background-color: #3a4250 !important; }
        .pdv-dark .hover\\:bg-brand-50:hover { background-color: #064884 !important; }
        .pdv-dark .bg-brand-50 { background-color: #07264a !important; }
        .pdv-dark .text-brand-700 { color: #7cc2fa !important; }
        .pdv-dark .text-brand-600 { color: #36a2f5 !important; }
        .pdv-dark .hover\\:bg-red-50:hover { background-color: rgba(248, 113, 113, 0.15) !important; }
      `}</style>

      {/* ESQUERDA — Busca + Itens */}
      <div className={`flex-1 flex flex-col min-w-0 ${escuro ? 'bg-ink-950' : 'bg-ink-50'}`}>
        {/* Header — minimalista. Apenas: voltar, logo, status caixa, Pagamento, menu */}
        <div className={`h-14 ${T.header} flex items-center px-3 sm:px-4 gap-3 shrink-0`}>
          <button onClick={() => tentarSair('/')} className={`p-2 rounded-lg ${T.headerIcon}`}>
            <ArrowLeft size={20} />
          </button>
          <img src={logoLoja} alt="Logo" className="w-8 h-8 rounded-md object-cover bg-white" />
          <span className={`font-semibold ${escuro ? 'text-white' : 'text-ink-900'}`}>PDV</span>

          {/* Status compacto do caixa: quando aberto mostra um chip; quando fechado, botão pra abrir */}
          {sessaoCaixa ? (
            <div className="hidden sm:flex items-center gap-1.5 ml-2 px-2 py-1 rounded-lg bg-emerald-900/40 text-emerald-300 text-xs"
              title={`Caixa #${sessaoCaixa.id} · aberto ${sessaoCaixa.aberto_em ? new Date(sessaoCaixa.aberto_em).toLocaleString('pt-BR') : ''}${sessaoStats ? ` · ${sessaoStats.qtd_vendas} venda(s)` : ''} · Recebido na sessão ${formatMoney(sessaoCaixa.recebido_em_caixa ?? 0)} (inclui cartão/pix) · Gaveta/dinheiro ${formatMoney(sessaoCaixa.valor_sistema_atual ?? 0)}`}>
              <Unlock size={12} /> Gaveta
              <span className="text-emerald-400 ml-1">{formatMoney(sessaoCaixa.valor_sistema_atual ?? 0)}</span>
              {(sessaoCaixa.recebido_em_caixa ?? 0) > (sessaoCaixa.detalhes?.receitas_dinheiro ?? 0) && (
                <span className="ml-1 text-emerald-200/70 hidden md:inline">· Recebido {formatMoney(sessaoCaixa.recebido_em_caixa ?? 0)}</span>
              )}
            </div>
          ) : (
            <button onClick={() => setShowAbrirCaixa(true)}
              className="flex items-center gap-1.5 ml-2 px-3 py-1.5 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-xs font-medium transition-colors">
              <Lock size={13} /> Abrir caixa
            </button>
          )}

          {/* FEATURE 30/06: badge do PDV-online (caixa separado) */}
          {canal === 'online' && (
            <span className="hidden md:inline-flex items-center gap-1 ml-2 px-2 py-1 rounded-lg text-xs font-bold bg-violet-600 text-white">
              ONLINE
            </span>
          )}

          {/* Chip de filial atual (apenas quando há mais de 1 filial cadastrada) */}
          {precisaEscolherFilial && filialId !== '' && (() => {
            const f = filiaisAtivas.find(x => x.id === filialId)
            if (!f) return null
            return (
              <button onClick={() => setShowSeletorFilial(true)}
                title="Trocar filial/estoque"
                className={`hidden md:flex items-center gap-1 ml-1 px-2 py-1 rounded-lg text-xs font-medium transition-colors
                  ${escuro ? 'bg-brand-900/40 text-brand-200 hover:bg-brand-900/60' : 'bg-brand-50 text-brand-700 hover:bg-brand-100'}`}>
                <Warehouse size={12} /> {f.nome_fantasia || f.codigo}
              </button>
            )
          })()}

          {/* Ações principais à direita (botão Pagamento removido do topo 30/06 —
              o pagamento é pelo painel direito / F2; evita duplicidade). */}
          <div className="ml-auto flex items-center gap-2">
            {/* Buscar OS / cobrar serviço: tirado do menu (06/07) e promovido a botão
                fixo no topo — ação frequente, ganha atalho visual (também no F3). */}
            <button onClick={() => setShowServicosOS(true)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium transition-colors"
              title="Buscar OS / cobrar serviço (F3)">
              <Wrench size={15} /> <span className="hidden sm:inline">Buscar OS / cobrar serviço (F3)</span>
            </button>

            {/* Menu de ações secundárias — o antigo ícone de 3 pontinhos virou botão
                nomeado "Opções / Caixa" (06/07) pra ficar autoexplicativo. FIX 06/07:
                sem fundo ele sumia no tema dark (mesma cor do header) → ganhou
                fundo/borda visível, theme-aware. */}
            <div className="relative">
              <button onClick={() => setShowMenu(v => !v)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border transition-colors
                  ${escuro
                    ? 'bg-ink-800 hover:bg-ink-700 text-ink-100 border-ink-700'
                    : 'bg-ink-100 hover:bg-ink-200 text-ink-700 border-ink-200'}`}
                title="Opções / Caixa">
                <MoreVertical size={16} /> Opções / Caixa
              </button>
              {showMenu && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowMenu(false)} />
                  <div className="absolute right-0 top-full mt-1 w-64 bg-ink-900 border border-ink-700 rounded-xl shadow-2xl z-50 py-1 text-sm">
                    {/* Info compacta do operador/caixa/stats */}
                    {(usuarioAtual || sessaoStats) && (
                      <div className="px-3 py-2 border-b border-ink-800 space-y-1 text-xs text-ink-400">
                        {usuarioAtual && (
                          <div className="flex items-center gap-1.5"><User size={12} /> {usuarioAtual.nome?.split(' ')[0] || usuarioAtual.email}</div>
                        )}
                        {sessaoCaixa?.aberto_em && (
                          <div className="flex items-center gap-1.5"><Clock size={12} /> Desde {new Date(sessaoCaixa.aberto_em).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</div>
                        )}
                        {sessaoStats != null && (
                          <div className="flex items-center gap-1.5 text-emerald-400"><Receipt size={12} /> {sessaoStats.qtd_vendas} venda{sessaoStats.qtd_vendas !== 1 ? 's' : ''} · {formatMoney(sessaoStats.total_receitas)}</div>
                        )}
                      </div>
                    )}

                    {/* Depósito (quando há mais de um) */}
                    {depositosVenda.length > 1 && (
                      <div className="px-3 py-2 border-b border-ink-800">
                        <label className="block text-[10px] uppercase tracking-wide text-ink-500 mb-1">
                          Depósito de saída
                          {depositoDeOutraLoja && (
                            <span className="ml-1.5 normal-case text-amber-400 font-semibold">
                              ⚠️ outra loja
                            </span>
                          )}
                        </label>
                        <select
                          value={depositoId}
                          onChange={e => {
                            const novo = Number(e.target.value)
                            setDepositoId(novo)
                            const dep = depositosVenda.find(d => d.id === novo)
                            if (dep && !modoConsolidado && filialAtualId
                                && dep.filial_id != null && dep.filial_id !== filialAtualId) {
                              toast(`Saída de outra loja: o estoque vai baixar de ${dep.nome}`,
                                    { icon: '⚠️', duration: 5000 })
                            }
                          }}
                          className={`w-full bg-ink-800 border rounded px-2 py-1.5 text-white text-xs ${
                            depositoDeOutraLoja ? 'border-amber-500' : 'border-ink-700'}`}>
                          {depositosVenda.map(d => (
                            <option key={d.id} value={d.id} className="bg-ink-900">{d.nome}</option>
                          ))}
                        </select>
                      </div>
                    )}
                    {depositosVenda.length === 1 && depositoAtual && (
                      <div className="px-3 py-2 border-b border-ink-800 text-xs text-ink-400 flex items-center gap-1.5">
                        <Warehouse size={12} /> {depositoAtual.nome}
                      </div>
                    )}

                    {/* Toggle modo grade/lista */}
                    <button onClick={() => { setModo(modo === 'lista' ? 'grade' : 'lista'); setShowMenu(false) }}
                      className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-ink-800 text-ink-200">
                      {modo === 'lista' ? <LayoutGrid size={15} /> : <List size={15} />}
                      Modo {modo === 'lista' ? 'grade visual' : 'lista'}
                    </button>

                    {/* Toggle tema escuro/claro */}
                    <button onClick={() => { setTema(escuro ? 'claro' : 'escuro'); setShowMenu(false) }}
                      className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-ink-800 text-ink-200">
                      {escuro ? <Sun size={15} /> : <Moon size={15} />}
                      Tema {escuro ? 'claro' : 'escuro'}
                    </button>

                    {/* Atualizar sistema (busca a versão mais nova, limpa cache) */}
                    <button onClick={() => { setShowMenu(false); forcarAtualizacaoApp() }}
                      className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-ink-800 text-brand-300">
                      <RefreshCw size={15} /> Atualizar sistema
                    </button>

                    {/* Trocar filial/estoque (só aparece com >1 filial) */}
                    {precisaEscolherFilial && (
                      <button onClick={() => { setShowSeletorFilial(true); setShowMenu(false) }}
                        className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-ink-800 text-ink-200">
                        <Warehouse size={15} /> Trocar filial / estoque
                      </button>
                    )}

                    {/* "Buscar produto" removido do menu 30/06 — a busca já fica fixa no topo. */}

                    {/* Vendas e movimentos do caixa (reimprimir / cancelar / estornar) */}
                    <button onClick={() => { setShowVendasDoDia(true); setShowMenu(false) }}
                      className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-ink-800 text-ink-200">
                      <Receipt size={15} /> Vendas e movimentos do caixa
                    </button>

                    {/* Adicionar crédito do cliente */}
                    <button onClick={() => { setShowAddCredito(true); setShowMenu(false) }}
                      className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-ink-800 text-cyan-300">
                      <HandCoins size={15} /> Adicionar crédito do cliente
                    </button>

                    {/* "Buscar OS / cobrar serviço" saiu do menu 06/07 — agora é botão
                        fixo no topo do PDV (continua no atalho F3). */}

                    {sessaoCaixa && (
                      <>
                        <div className="border-t border-ink-800 my-1" />
                        <button onClick={() => { setShowSangria(true); setMovValor(''); setMovDescricao(''); setMovSenha(''); setMovResponsavelId(''); setShowMenu(false) }}
                          className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-ink-800 text-amber-300">
                          <ArrowDownToLine size={15} /> Sangria
                        </button>
                        <button onClick={() => { setShowSuprimento(true); setMovValor(''); setMovDescricao(''); setShowMenu(false) }}
                          className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-ink-800 text-sky-300">
                          <ArrowUpFromLine size={15} /> Suprimento
                        </button>
                        <button onClick={() => { setShowEntrada(true); setMovValor(''); setMovDescricao(''); setShowMenu(false) }}
                          className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-ink-800 text-emerald-300">
                          <Wallet size={15} /> Entrada de valor (receita)
                        </button>
                        {/* FEATURE 01/07: receber conta a prazo do cliente direto no caixa (baixa o título + entra na gaveta) */}
                        <button onClick={() => { setShowReceberTitulo(true); setShowMenu(false) }}
                          className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-ink-800 text-teal-300">
                          <HandCoins size={15} /> Receber a prazo (Contas a Receber)
                        </button>
                        <button onClick={() => { setShowSaida(true); setMovValor(''); setMovDescricao(''); setMovSenha(''); setMovResponsavelId(''); setShowMenu(false) }}
                          className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-ink-800 text-rose-300">
                          <Wallet size={15} /> Saída de valor (despesa)
                        </button>
                        <button onClick={() => { setShowResumoCaixa(true); setShowMenu(false) }}
                          className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-ink-800 text-sky-300">
                          <Eye size={15} /> Ver resumo do caixa
                        </button>
                        <button onClick={() => { setShowFecharCaixa(true); setValorContado(sessaoCaixa?.valor_sistema_atual != null ? String(sessaoCaixa.valor_sistema_atual) : ''); setObsFechamento(''); setShowMenu(false) }}
                          className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-ink-800 text-rose-300">
                          <Lock size={15} /> Fechar caixa
                        </button>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* FEATURE 08/07: aviso de caixa aberto em DIA ANTERIOR — a movimentação exibida
            ainda mistura ontem + hoje porque o turno nunca foi fechado. Botão faz a virada
            (fecha ontem, abre hoje), separando os dois. */}
        {sessaoCaixa?.aberto_dia_anterior && (
          <div className="px-3 py-2.5 bg-amber-50 border-b-2 border-amber-300 shrink-0 flex items-center gap-3">
            <AlertTriangle size={18} className="text-amber-600 shrink-0" />
            <div className="flex-1 min-w-0 text-sm text-amber-800">
              <span className="font-semibold">Caixa aberto desde {sessaoCaixa.aberto_data ? new Date(sessaoCaixa.aberto_data + 'T12:00:00').toLocaleDateString('pt-BR') : 'um dia anterior'}.</span>{' '}
              A movimentação mostrada inclui dias anteriores. Feche o caixa de ontem para separar de hoje.
            </div>
            <button onClick={() => { setValorContadoOntem(''); setShowVirarDia(true) }}
              className="shrink-0 px-3 py-1.5 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold flex items-center gap-1.5">
              <RefreshCw size={14} /> Fechar ontem e abrir hoje
            </button>
          </div>
        )}

        {/* Busca */}
        <div className="p-3 bg-white border-b border-ink-200 shrink-0">
          <div className="relative">
            <Search size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-ink-400" />
            <input
              ref={inputRef}
              value={busca}
              onChange={e => setBusca(e.target.value)}
              onKeyDown={handleBarcode}
              placeholder="Buscar produto ou ler código de barras..."
              className="w-full pl-12 pr-14 py-3.5 text-base bg-ink-50 border-2 border-ink-200 rounded-xl
                         focus:border-brand-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-brand-200
                         placeholder:text-ink-400"
              autoComplete="off"
            />
            <button type="button" onClick={() => setShowTutorial(true)} title="Tutorial e atalhos do teclado"
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-lg text-ink-400 hover:text-brand-600 hover:bg-brand-50">
              <HelpCircle size={20} />
            </button>
          </div>

          {busca.length >= 1 && resultados.length > 0 && (
            <div ref={resultsRef} className="mt-2 bg-white border border-ink-200 rounded-xl shadow-lg max-h-72 overflow-auto">
              {resultados.map((p, i) => (
                <button key={p.id} data-bidx={i} onClick={() => addProduto(p)} onMouseEnter={() => setBuscaIdx(i)}
                  className={`w-full text-left px-3 py-2.5 border-b border-ink-100 last:border-0 flex items-center gap-3 transition-colors ${
                    i === buscaIdx ? 'bg-brand-50 ring-1 ring-inset ring-brand-300' : 'hover:bg-brand-50'}`}>
                  <div className="w-12 h-12 rounded-lg bg-ink-100 overflow-hidden flex items-center justify-center shrink-0 border border-ink-200">
                    {p.foto_thumb
                      ? <img src={p.foto_thumb} alt="" className="w-full h-full object-cover" loading="lazy" />
                      : <Package size={18} className="text-ink-400" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-ink-900 truncate">{p.descricao}</div>
                    <div className="text-xs text-ink-500 mt-0.5">
                        {p.codigo}{p.ean ? ` · EAN: ${p.ean}` : ''} ·{' '}
                        <span className={corEstoque(p)}>Est: {textoEstoque(p, nomeFilialAtual)}</span>
                    </div>
                  </div>
                  <div className="text-lg font-bold text-brand-600 shrink-0">{formatMoney(p.preco_venda)}</div>
                </button>
              ))}
            </div>
          )}

          {/* Chips de categorias: linha 1 = tags (grupos); linha 2 = subtags da tag selecionada */}
          {rootsCategorias.length > 0 && (
            <div className="mt-3 space-y-1.5">
              {/* Linha 1 — tags (grupos) */}
              <div className="flex flex-wrap gap-1.5">
                {/* 03/07 (pedido do usuário): "Todos" só fica ativo quando está MOSTRANDO
                    tudo (verTodos); no estado inicial (logo) nenhum filtro fica marcado.
                    Clicar em "Todos" já ativo volta pro início (logo). */}
                <button data-nav onClick={() => {
                    const mostrandoTudo = verTodos && grupoSelecionado == null && subgrupoSelecionado == null
                    setGrupoSelecionado(null); setSubgrupoSelecionado(null); setVitrineColapsada(false)
                    setVerTodos(!mostrandoTudo)
                  }}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium transition focus:outline-none focus:ring-2 focus:ring-brand-500 ${
                    verTodos && grupoSelecionado == null && subgrupoSelecionado == null
                      ? 'bg-brand-600 text-white' : 'bg-ink-100 text-ink-700 hover:bg-ink-200'
                  }`}>Todos</button>
                {rootsCategorias.map((c: any) => {
                  const ativo = grupoSelecionado === c.id
                  const temSub = (subsPorPai[c.id]?.length || 0) > 0
                  // 03/07 (pedido do usuário): clicar no grupo JÁ ativo DESMARCA (volta pra "Todos").
                  return (
                    <button key={`g-${c.id}`} data-nav
                      onClick={() => {
                        if (ativo) { setGrupoSelecionado(null); setSubgrupoSelecionado(null); setVerTodos(false) }  // desmarca → volta pro início (logo)
                        else { setGrupoSelecionado(c.id); setSubgrupoSelecionado(null) }
                        setVitrineColapsada(false)
                      }}
                      className={`px-2.5 py-1 rounded-full text-xs font-medium transition focus:outline-none focus:ring-2 focus:ring-brand-500 ${
                        ativo ? 'bg-brand-600 text-white' : 'bg-ink-100 text-ink-700 hover:bg-ink-200'
                      }`}>
                      {c.nome}{temSub && <span className="ml-1 opacity-60">›</span>}
                    </button>
                  )
                })}
              </div>
              {/* Linha 2 — subtags (só ao selecionar uma tag que tenha subgrupos) */}
              {grupoSelecionado != null && (subsPorPai[grupoSelecionado]?.length || 0) > 0 && (
                <div className="flex flex-wrap gap-1.5 pl-2 ml-0.5 border-l-2 border-brand-200">
                  <button data-nav onClick={() => { setSubgrupoSelecionado(null); setVitrineColapsada(false) }}
                    className={`px-2.5 py-1 rounded-full text-xs font-medium transition focus:outline-none focus:ring-2 focus:ring-brand-500 ${
                      subgrupoSelecionado == null
                        ? 'bg-brand-500 text-white' : 'bg-ink-50 text-ink-600 hover:bg-ink-100 ring-1 ring-ink-200'
                    }`}>Todas</button>
                  {/* 03/07: clicar no subgrupo JÁ ativo desmarca (volta pra "Todas"). */}
                  {subsPorPai[grupoSelecionado].map((s: any) => (
                    <button key={`s-${s.id}`} data-nav
                      onClick={() => { setSubgrupoSelecionado(subgrupoSelecionado === s.id ? null : s.id); setVitrineColapsada(false) }}
                      className={`px-2.5 py-1 rounded-full text-xs font-medium transition focus:outline-none focus:ring-2 focus:ring-brand-500 ${
                        subgrupoSelecionado === s.id
                          ? 'bg-brand-500 text-white' : 'bg-ink-50 text-ink-600 hover:bg-ink-100 ring-1 ring-ink-200'
                      }`}>{s.nome}</button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Vitrine de produtos/serviços — persistente: aparece sempre que não há busca
            ativa e não está recolhida, MESMO com itens no carrinho (split vertical).
            Modo grade = cards; modo lista = linhas. Inclui vendas em espera no topo. */}
        {busca.length === 0 && !vitrineColapsada && (
          <div className={`${itens.length > 0
            ? 'shrink-0 max-h-[42vh] border-b-4 border-ink-200'
            : 'flex-1'} overflow-auto p-3 bg-ink-100 space-y-3`}>
            {/* Cabeçalho da vitrine no modo split — permite recolher pra dar espaço ao carrinho */}
            {itens.length > 0 && (
              <div className="sticky -top-3 -mx-3 -mt-3 mb-1 px-3 py-1 bg-ink-100 flex items-center justify-between z-10 border-b border-ink-200">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-ink-500 flex items-center gap-1.5">
                  <Package size={13} /> Produtos
                </span>
                <button onClick={() => setVitrineColapsada(true)}
                  className="text-[11px] font-medium text-ink-500 hover:text-ink-800 flex items-center gap-1 px-2 py-0.5 rounded-lg hover:bg-ink-200">
                  <ChevronUp size={13} /> Recolher
                </button>
              </div>
            )}
            {/* Vendas em espera — clique pra retomar */}
            {emEspera.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-amber-700 text-xs font-semibold uppercase tracking-wide px-1">
                  <Clock size={14} /> Vendas em espera ({emEspera.length})
                </div>
                {emEspera.map((v: any) => (
                  <div key={v.id}
                    className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-center gap-3 hover:bg-amber-100 transition-colors cursor-pointer"
                    onClick={() => retomarEspera(v.id)}>
                    <div className="w-9 h-9 rounded-full bg-amber-200 text-amber-800 flex items-center justify-center shrink-0">
                      <ShoppingCart size={16} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-ink-900 truncate">
                        {v.cliente_nome} · {v.qtd_itens} {v.qtd_itens === 1 ? 'item' : 'itens'}
                      </div>
                      <div className="text-xs text-ink-500">
                        {formatMoney(v.total)}{v.observacao ? ` · ${v.observacao}` : ''}
                      </div>
                    </div>
                    <span className="text-xs font-semibold text-amber-700 shrink-0">Retomar</span>
                    <button
                      onClick={(e) => { e.stopPropagation(); pedirExclusao({ descricao: `a venda em espera de ${v.cliente_nome}`, onConfirm: () => descartarEspera(v.id) }) }}
                      className="p-1.5 rounded-lg hover:bg-amber-200 text-ink-400 hover:text-rose-600 shrink-0"
                      title="Descartar">
                      <Trash2 size={15} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {(itens.length === 0 && grupoSelecionado == null && subgrupoSelecionado == null && !verTodos) ? (
              /* Estado ocioso — vitrine de marca: logo da loja em vez dos produtos */
              <div className="relative flex flex-col items-center justify-center h-full py-8 select-none overflow-hidden">
                {/* Brilho de fundo suave com a cor da marca */}
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                  <div className="w-[320px] h-[320px] max-w-[80%] rounded-full bg-brand-500/10 blur-3xl" />
                </div>
                <div className="relative flex flex-col items-center text-center px-6">
                  {/* Logo num cartão arredondado com sombra; a própria imagem com cantos arredondados */}
                  <div className="bg-white rounded-2xl shadow-lg shadow-ink-900/10 ring-1 ring-ink-200/60 p-5 sm:p-6 mb-5">
                    <img src={logoLoja} alt={emissor?.nome || 'Loja'}
                      className="max-h-36 sm:max-h-44 max-w-[60vw] sm:max-w-[300px] object-contain rounded-xl" />
                  </div>
                  {emissor?.nome && (
                    <div className="text-2xl sm:text-3xl font-extrabold tracking-tight text-ink-800">{emissor.nome}</div>
                  )}
                  <p className="text-sm text-ink-400 mt-2 max-w-xs leading-relaxed">
                    Busque um produto, leia o código de barras ou escolha uma categoria pra começar
                  </p>
                  <button onClick={() => { setVerTodos(true); setVitrineColapsada(false) }}
                    className="mt-4 px-3 py-1.5 rounded-lg bg-brand-600 hover:bg-brand-700 text-white text-xs font-medium flex items-center gap-1.5 shadow-sm transition-colors">
                    <LayoutGrid size={14} /> Ver produtos
                  </button>
                </div>
              </div>
            ) : vitrine.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-ink-400 py-20">
                <ImageIcon size={48} className="mb-3 opacity-30" />
                <p className="text-sm">
                  {grupoSelecionado || subgrupoSelecionado
                    ? 'Nenhum item cadastrado nesta categoria'
                    : 'Nenhum produto ou serviço cadastrado'}
                </p>
                <p className="text-xs mt-1 text-ink-500">
                  Busque pelo código/nome ou leia o código de barras
                </p>
              </div>
            ) : modo === 'lista' ? (
              /* 30/06: linhas mais compactas (menos padding/altura) p/ caber mais produtos */
              <div className="space-y-1">
                {vitrine.map(p => (
                  <button key={p.id} data-nav onClick={() => addProduto(p)}
                    className="w-full bg-white rounded-lg border border-ink-200 hover:border-brand-400 hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-500 transition-all px-2 py-1.5 flex items-center gap-2.5 text-left">
                    <div className="w-8 h-8 rounded-md bg-ink-100 overflow-hidden flex items-center justify-center shrink-0 border border-ink-200">
                      {p.foto_thumb
                        ? <img src={p.foto_thumb} alt="" loading="lazy" className="w-full h-full object-cover" />
                        : <Package size={15} className="text-ink-300" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-ink-900 truncate leading-tight">{p.descricao}</div>
                        <div className="text-[11px] text-ink-500 leading-tight">{p.codigo} ·{' '}
                          <span className={corEstoque(p)}>{textoEstoque(p, nomeFilialAtual)}</span></div>
                    </div>
                    <span className="text-sm font-bold text-brand-600 shrink-0">{formatMoney(p.preco_venda)}</span>
                  </button>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2.5">
                {vitrine.map(p => (
                  <button key={p.id} data-nav onClick={() => addProduto(p)}
                    className="bg-white rounded-xl overflow-hidden border border-ink-200 hover:border-brand-400 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-brand-500 transition-all text-left group">
                    <div className="aspect-square bg-ink-100 overflow-hidden flex items-center justify-center">
                      {p.foto_thumb
                        ? <img src={p.foto_thumb} alt="" loading="lazy"
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                        : <Package size={32} className="text-ink-300" />}
                    </div>
                    <div className="p-2">
                      <div className="text-xs font-medium text-ink-900 line-clamp-2 leading-tight min-h-[2.2em]">{p.descricao}</div>
                      <div className="flex items-end justify-between mt-1.5">
                        <span className={`text-xs text-ink-500 ${corEstoque(p)}`}>{textoEstoque(p, nomeFilialAtual)}</span>
                        <span className="text-base font-bold text-brand-600">{formatMoney(p.preco_venda)}</span>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Barra "Mostrar produtos" — só quando a vitrine está recolhida e sem busca */}
        {busca.length === 0 && vitrineColapsada && (
          <button onClick={() => setVitrineColapsada(false)}
            className="shrink-0 mx-3 mt-2 mb-1 px-3 py-2 rounded-xl bg-ink-100 hover:bg-ink-200 text-ink-600 text-sm font-medium flex items-center justify-center gap-2 transition-colors border border-ink-200">
            <ChevronDown size={16} /> Mostrar produtos
          </button>
        )}

        {/* Lista de itens (carrinho) — escondida só quando a vitrine ocupa tudo (sem itens, aberta, sem busca) */}
        <div className={`${
          busca.length === 0 && !vitrineColapsada && itens.length === 0 ? 'hidden' : 'flex-1'
        } overflow-auto p-3 space-y-1`}>
          {/* Cabeçalho da lista de itens — espelha o "Produtos" da vitrine */}
          {itens.length > 0 && (
            <div className={`sticky -top-3 -mx-3 -mt-3 mb-1 px-3 py-1 ${escuro ? 'bg-ink-950' : 'bg-ink-50'} flex items-center justify-between z-10 border-b border-ink-200`}>
              <span className="text-[11px] font-semibold uppercase tracking-wide text-ink-500 flex items-center gap-1.5">
                <ShoppingCart size={13} /> Itens ({itens.length})
              </span>
              <span className="text-[11px] font-medium text-ink-400">
                {itens.reduce((s, i) => s + i.quantidade, 0)} un.
              </span>
            </div>
          )}
          {itens.map((item, idx) => {
            const precoItem = precoLinha(item)
            const subtotalItem = item.quantidade * precoItem
            const totalItem = subtotalItem - item.desconto
            return (
            <div key={idx}
              className="bg-white rounded-lg border border-ink-200 px-2 py-1 flex items-center gap-2 hover:shadow-sm transition-shadow">
              <div className="w-6 h-6 rounded-full bg-brand-50 text-brand-700 text-[11px] font-bold flex items-center justify-center shrink-0">
                {idx + 1}
              </div>

              <div className="w-9 h-9 rounded-md bg-ink-100 overflow-hidden flex items-center justify-center shrink-0 border border-ink-200">
                {item.produto.foto_thumb
                  ? <img src={item.produto.foto_thumb} alt="" className="w-full h-full object-cover" loading="lazy" />
                  : <Package size={15} className="text-ink-400" />}
              </div>

              <div className="flex-1 min-w-0">
                <div className="font-medium text-ink-900 truncate text-sm leading-tight">
                  {item.produto.descricao}
                  {item.grade_descritor && (
                    <span className="ml-2 text-xs font-medium text-brand-700 bg-brand-50 px-1.5 py-0.5 rounded">{item.grade_descritor}</span>
                  )}
                  {item.qualidade_exigida && item.qualidade_exigida !== 'PRIMEIRA' && (
                    <span className="ml-1.5 text-[10px] font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">{item.qualidade_exigida}</span>
                  )}
                </div>
                <div className="text-xs text-ink-500 mt-0.5">
                  {item.produto.codigo} · {formatMoney(precoItem)}/{item.unidade_label || item.produto.unidade}
                  {item.conversao_id && (
                    <span className="ml-1 text-emerald-700 font-medium">[{item.unidade_label}]</span>
                  )}
                </div>
              </div>

              {/* Quantidade editável */}
              <div className="flex items-center bg-ink-100 rounded-lg shrink-0">
                <button onClick={() => {
                  if (item.quantidade <= 1) setItens(prev => prev.filter((_, i) => i !== idx))
                  else setItens(prev => prev.map((it, i) => i === idx ? { ...it, quantidade: it.quantidade - 1 } : it))
                }} className="p-1.5 hover:bg-ink-200 rounded-l-lg transition-colors">
                  {item.quantidade <= 1 ? <Trash2 size={14} className="text-red-500" /> : <Minus size={14} className="text-ink-700" />}
                </button>
                <input
                  type="number" min="1" step="1"
                  data-item-qtd={idx}
                  value={item.quantidade}
                  onChange={e => {
                    // Digitação livre, mas nunca negativa. Vazio/inválido mantém o valor.
                    const v = parseFloat(e.target.value)
                    if (Number.isNaN(v)) return
                    const nova = Math.max(0, v)
                    setItens(prev => prev.map((it, i) => i === idx ? { ...it, quantidade: nova } : it))
                    avisarEstoque(item, nova)
                  }}
                  onFocus={e => e.target.select()}
                  onKeyDown={e => {
                    // Esc remove o produto e volta pra busca. stopPropagation impede o
                    // handler global de Esc (que tentaria SAIR do PDV) de disparar junto.
                    if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); removerItem(idx); return }
                    if (e.key === 'Enter') { e.preventDefault(); focarItemCampo(idx, 'preco'); return }
                    // Setas mexem de 1 em 1 (inteiro), nunca abaixo de 1 — remover é pelo
                    // botão de lixeira/X. Antes o step 0.001 nativo mexia em milésimos.
                    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                      e.preventDefault()
                      const delta = e.key === 'ArrowUp' ? 1 : -1
                      const nova = Math.max(1, Math.round(item.quantidade) + delta)
                      setItens(prev => prev.map((it, i) => i === idx ? { ...it, quantidade: nova } : it))
                      if (delta > 0) avisarEstoque(item, nova)
                    }
                  }}
                  className="w-11 text-center font-bold text-ink-900 text-sm bg-transparent outline-none focus:bg-white focus:ring-2 focus:ring-brand-300 rounded"
                />
                <button onClick={() => {
                  const nova = item.quantidade + 1
                  setItens(prev => prev.map((it, i) => i === idx ? { ...it, quantidade: nova } : it))
                  avisarEstoque(item, nova)
                }}
                  className="p-1.5 hover:bg-ink-200 rounded-r-lg transition-colors">
                  <Plus size={14} className="text-ink-700" />
                </button>
              </div>

              {/* Preço unitário editável — SÓ desta venda (não altera o cadastro do produto) */}
              <div className="shrink-0 flex flex-col items-end leading-none">
                <label className="text-[9px] text-ink-400 uppercase tracking-wide leading-none mb-0.5">Preço un.</label>
                <input
                  type="number" min="0" step="0.01"
                  data-item-preco={idx}
                  value={precoItem}
                  onChange={e => {
                    const v = parseFloat(e.target.value)
                    setItens(prev => prev.map((it, i) => i === idx
                      ? { ...it, preco_unitario: Number.isNaN(v) ? undefined : Math.max(0, v) } : it))
                  }}
                  onFocus={e => e.target.select()}
                  onKeyDown={e => {
                    if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); removerItem(idx); return }
                    if (e.key === 'Enter') { e.preventDefault(); focarItemCampo(idx, 'desc') }
                  }}
                  title="Alterar o preço só nesta venda (não muda o cadastro)"
                  className={`w-20 px-1.5 py-0.5 text-right text-xs border rounded focus:bg-white focus:border-brand-400 focus:outline-none focus:ring-1 focus:ring-brand-300 ${
                    item.preco_unitario != null
                      ? 'bg-amber-50 border-amber-300 text-amber-800 font-semibold'
                      : 'bg-ink-50 border-ink-200'}`}
                />
              </div>

              {/* Desconto por item */}
              <div className="shrink-0 flex flex-col items-end leading-none">
                <label className="text-[9px] text-ink-400 uppercase tracking-wide leading-none mb-0.5">Desc R$</label>
                <input
                  type="number" min="0" step="0.01"
                  data-item-desc={idx}
                  value={item.desconto || ''}
                  onChange={e => {
                    const v = parseFloat(e.target.value) || 0
                    setItens(prev => prev.map((it, i) => i === idx ? { ...it, desconto: Math.max(0, Math.min(v, subtotalItem)) } : it))
                  }}
                  onFocus={e => e.target.select()}
                  onKeyDown={e => {
                    if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); removerItem(idx); return }
                    if (e.key === 'Enter') { e.preventDefault(); inputRef.current?.focus() }
                  }}
                  placeholder="0"
                  className="w-16 px-1.5 py-0.5 text-right text-xs bg-ink-50 border border-ink-200 rounded focus:bg-white focus:border-brand-400 focus:outline-none focus:ring-1 focus:ring-brand-300"
                />
              </div>

              <div className="text-right shrink-0 w-20">
                {item.desconto > 0 && (
                  <div className="text-[10px] text-ink-400 line-through leading-none">{formatMoney(subtotalItem)}</div>
                )}
                <div className="font-bold text-ink-900 text-sm">{formatMoney(totalItem)}</div>
              </div>

              <button onClick={() => setItens(prev => prev.filter((_, i) => i !== idx))}
                className="p-2 rounded-lg hover:bg-red-50 text-ink-400 hover:text-red-500 transition-colors shrink-0">
                <X size={18} />
              </button>
            </div>
          )})}
        </div>

        {/* Cliente + Observação no mobile (linha compacta acima da barra de pagar) */}
        <div className="lg:hidden px-3 py-2 bg-white border-t border-ink-200 space-y-2 shrink-0">
          <ClienteSelector cliente={cliente} setCliente={setCliente}
            busca={buscaCliente} setBusca={setBuscaCliente}
            incluirNoCupom={incluirClienteCupom} setIncluirNoCupom={setIncluirClienteCupom}
            compact />
          <textarea
            value={observacao}
            onChange={e => setObservacao(e.target.value.toUpperCase())}
            rows={1}
            placeholder="Observação (modelo, cor, série...)"
            className="w-full px-2 py-1.5 text-xs bg-ink-50 border border-ink-200 rounded
                       focus:bg-white focus:border-brand-400 focus:outline-none resize-none" />
        </div>

        {/* Barra inferior mobile com botões */}
        <div className="lg:hidden flex items-center justify-between px-3 py-2 bg-white border-t border-ink-200 shrink-0">
          <div>
            <span className="text-sm text-ink-500">{totalItens} itens</span>
            <span className="text-lg font-bold text-ink-900 ml-3">{formatMoney(totalFinal)}</span>
            {descontoAplicadoExibido > 0 && <span className="text-[10px] text-amber-600 ml-2">−{formatMoney(descontoAplicadoExibido)}</span>}
          </div>
          <button
            disabled={itens.length === 0}
            onClick={iniciarPagamento}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700
                       disabled:opacity-30 text-white font-semibold transition-colors">
            <CreditCard size={18} /> Pagar
          </button>
        </div>
      </div>

      {/* DIREITA — Resumo + Pagamento */}
      {/* 30/06: painel direito um pouco mais largo (w-96→440px); produtos (flex-1) encolhe. */}
      <div className={`hidden lg:flex w-[440px] ${T.rightPanel} flex-col shrink-0 overflow-y-auto min-h-0`}>
        {/* Barra de acesso rápido — sempre visível, só TEXTO (sem ícone), compacta. F6-F9 funcionam. */}
        <div className={`grid grid-cols-5 gap-1 px-1.5 py-1 border-b ${T.rightPanelDivider} shrink-0`}>
          {([
            { lb: 'Cliente', fk: 'F6', fn: irCliente },
            { lb: 'Vendedor', fk: 'F7', fn: irVendedor },
            { lb: 'Obs', fk: 'F8', fn: irObservacao },
            { lb: 'Desconto', fk: 'F9', fn: irDesconto },
            { lb: 'Pagar', fk: 'F2', fn: iniciarPagamento, pay: true },
          ] as any[]).map((b) => (
            <button key={b.fk} onClick={b.fn} title={`${b.lb} (${b.fk})`}
              disabled={b.pay && itens.length === 0}
              className={`flex flex-col items-center px-1 py-0.5 rounded text-[10px] font-medium leading-tight transition disabled:opacity-30 ${
                b.pay ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                      : escuro ? 'bg-ink-800 text-ink-200 hover:bg-ink-700' : 'bg-ink-100 text-ink-700 hover:bg-brand-100'}`}>
              <span>{b.lb}</span>
              <span className="text-[8px] opacity-50">{b.fk}</span>
            </button>
          ))}
        </div>
        {/* Cliente + Observação (sempre visíveis no topo do painel direito) */}
        <div className={`p-2 border-b ${T.rightPanelDivider} space-y-1.5 shrink-0`}>
          {osVinculadas.length > 0 && (
            <div className="flex items-center justify-between gap-2 px-2.5 py-1.5 rounded bg-amber-500/15 border border-amber-500/40 text-amber-300 text-xs font-semibold">
              <span className="flex items-center gap-1.5">
                <Wrench size={13} />
                {osVinculadas.length > 1
                  ? `Cobrando ${osVinculadas.length} OS (${osVinculadas.map(o => '#' + o.numero).join(', ')})`
                  : `Cobrando OS #${osVinculadas[0].numero}`}
              </span>
              <button onClick={() => { setOsVinculadas([]); setItens([]); setCliente(null) }}
                className="text-amber-400 hover:text-amber-200"><X size={14} /></button>
            </div>
          )}
          <div id="pdv-cliente">
            <ClienteSelector cliente={cliente} setCliente={setCliente}
              busca={buscaCliente} setBusca={setBuscaCliente}
              incluirNoCupom={incluirClienteCupom} setIncluirNoCupom={setIncluirClienteCupom} />
          </div>
          {vendedoresPDV.length > 0 && (
            <div className={vendedorEhOficina ? 'flex gap-2 items-start' : ''}>
            <div className={`relative ${vendedorEhOficina ? 'w-1/2' : ''}`} ref={vendedorRef}>
              <label className="text-[9px] text-ink-500 uppercase tracking-wide flex items-center gap-1">
                <UserPlus size={10} /> Vendedor
              </label>
              <input
                id="pdv-vendedor"
                value={buscaVendedor}
                placeholder="Nome ou número do vendedor..."
                autoComplete="off"
                onChange={e => { setBuscaVendedor(e.target.value); setAbrirVendedor(true); setVendHi(0); if (vendedorCadId) setVendedorCadId('') }}
                onFocus={() => { setAbrirVendedor(true); setVendHi(0) }}
                onKeyDown={e => {
                  if (e.key === 'ArrowDown') { e.preventDefault(); setAbrirVendedor(true); setVendHi(h => Math.min(h + 1, vendedoresFiltrados.length - 1)) }
                  else if (e.key === 'ArrowUp') { e.preventDefault(); setVendHi(h => Math.max(h - 1, 0)) }
                  else if (e.key === 'Enter' && vendedoresFiltrados.length > 0) {
                    e.preventDefault()
                    const v = vendedoresFiltrados[Math.min(vendHi, vendedoresFiltrados.length - 1)]
                    if (v) { setVendedorCadId(v.id); setBuscaVendedor(v.nome); setAbrirVendedor(false) }
                  }
                }}
                className={`w-full mt-0.5 px-2 py-1 border rounded text-xs focus:border-brand-500 focus:outline-none
                  ${escuro ? 'bg-ink-900 border-ink-700 text-white placeholder:text-ink-600' : 'bg-white border-ink-200 text-ink-900 placeholder:text-ink-400'}`} />
              {abrirVendedor && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-ink-200 rounded-lg shadow-lg z-30 max-h-48 overflow-y-auto">
                  <button type="button" onMouseDown={e => e.preventDefault()}
                    onClick={() => { setVendedorCadId(''); setBuscaVendedor(''); setAbrirVendedor(false) }}
                    className="w-full px-3 py-2 text-left text-xs text-ink-500 hover:bg-ink-50 border-b">— Sem vendedor —</button>
                  {vendedoresFiltrados.length === 0 ? (
                    <div className="p-3 text-center text-xs text-ink-400">Nenhum vendedor</div>
                  ) : vendedoresFiltrados.map((v: any, idx: number) => (
                    <button key={v.id} type="button" onMouseDown={e => e.preventDefault()}
                      ref={el => { if (idx === vendHi) el?.scrollIntoView({ block: 'nearest' }) }}
                      onMouseEnter={() => setVendHi(idx)}
                      onClick={() => { setVendedorCadId(v.id); setBuscaVendedor(v.nome); setAbrirVendedor(false) }}
                      className={`w-full px-3 py-2 text-left text-xs text-ink-800 border-b last:border-0 ${idx === vendHi ? 'bg-brand-100' : 'hover:bg-brand-50'}`}>
                      {v.nome}{v.codigo ? <span className="text-ink-400 font-mono"> · {v.codigo}</span> : ''}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {/* OFICINA (06/07): campo Mecânico aparece ao lado — o vendedor encolhe pra metade. */}
            {vendedorEhOficina && (
              <div className="w-1/2">
                <label className="text-[9px] text-ink-500 uppercase tracking-wide flex items-center gap-1">
                  <Wrench size={10} /> Mecânico
                </label>
                <select id="pdv-mecanico" value={mecanicoCadId}
                  onChange={e => setMecanicoCadId(e.target.value ? Number(e.target.value) : '')}
                  className={`w-full mt-0.5 px-2 py-1 border rounded text-xs focus:border-brand-500 focus:outline-none
                    ${escuro ? 'bg-ink-900 border-ink-700 text-white' : 'bg-white border-ink-200 text-ink-900'}`}>
                  <option value="">Selecione o mecânico...</option>
                  {mecanicosPDV.map((m: any) => (
                    <option key={m.id} value={m.id}>{m.nome}</option>
                  ))}
                </select>
              </div>
            )}
            </div>
          )}
          {/* BUG (03/07): a observação sumia ao entrar no pagamento (escondida por
              !showPagamento) — o operador não conseguia anotar nada durante o pagamento.
              FIX: sempre visível; só encolhe (1 linha) durante o pagamento pra caber. */}
          <div>
            <label className="text-[9px] text-ink-500 uppercase tracking-wide flex items-center gap-1">
              <FileText size={10} /> Observação (sai no cupom)
            </label>
            <textarea
              id="pdv-obs"
              value={observacao}
              onChange={e => setObservacao(e.target.value.toUpperCase())}
              rows={showPagamento ? 1 : 2}
              placeholder="Ex: cor, série, qualidade exigida..."
              className={`w-full mt-0.5 px-2 py-1 border rounded text-xs focus:border-brand-500 focus:outline-none resize-none
                ${escuro
                  ? 'bg-ink-900 border-ink-700 text-white placeholder:text-ink-600'
                  : 'bg-white border-ink-200 text-ink-900 placeholder:text-ink-400'}`} />
          </div>
        </div>

        {/* Resumo — completo na montagem; compacto durante o pagamento.
            30/06: no pagamento o grupo (resumo + formas) ancora EMBAIXO (mt-auto) e
            cresce pra cima quando precisa de espaço (ex.: form de bike na troca). */}
        <div className={showPagamento ? 'mt-auto px-4 pt-3 pb-1 shrink-0' : 'p-5 flex-1 flex flex-col justify-end min-h-0'}>
          <div className={showPagamento ? 'space-y-0.5' : 'space-y-3'}>
            <div className={`flex justify-between ${showPagamento ? 'text-[10px]' : 'text-sm'} ${escuro ? 'text-ink-400' : 'text-ink-600'}`}>
              <span>{totalItens} {totalItens === 1 ? 'item' : 'itens'}</span>
              <span>{formatMoney(subtotal)}</span>
            </div>

            {/* Desconto da venda — editor só na montagem (antes de pagar) */}
            {!showPagamento && (
              <div className={`rounded-lg p-2 space-y-1.5 border ${escuro ? 'bg-ink-900 border-ink-800' : 'bg-ink-50 border-ink-200'}`}>
                <div className="flex items-center justify-between">
                  <span className={`text-[11px] uppercase tracking-wide ${escuro ? 'text-ink-400' : 'text-ink-500'}`}>Desconto na venda</span>
                  <div className={`flex items-center rounded text-xs ${escuro ? 'bg-ink-800' : 'bg-ink-200'}`}>
                    <button onClick={() => setDescontoTipo('R$')}
                      className={`px-2 py-0.5 rounded-l ${descontoTipo === 'R$' ? 'bg-brand-600 text-white' : (escuro ? 'text-ink-400' : 'text-ink-600')}`}>
                      R$
                    </button>
                    <button onClick={() => setDescontoTipo('%')}
                      className={`px-2 py-0.5 rounded-r ${descontoTipo === '%' ? 'bg-brand-600 text-white' : (escuro ? 'text-ink-400' : 'text-ink-600')}`}>
                      %
                    </button>
                  </div>
                </div>
                <input
                  id="pdv-desconto"
                  type="number" min="0" step="0.01"
                  value={descontoInput}
                  disabled={itens.length === 0}
                  onChange={e => setDescontoInput(e.target.value)}
                  onFocus={e => e.target.select()}
                  placeholder="0,00"
                  className={`w-full px-3 py-1.5 border rounded text-right text-base focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:opacity-40
                    ${escuro ? 'bg-ink-950 border-ink-700 text-white' : 'bg-white border-ink-200 text-ink-900'}`}
                />
                {descontoVenda > 0 && (
                  <div className={`flex justify-between text-xs ${escuro ? 'text-amber-400' : 'text-amber-600'}`}>
                    <span>Desconto aplicado</span>
                    <span>−{formatMoney(descontoVenda)}</span>
                  </div>
                )}
              </div>
            )}

            {/* No pagamento: desconto editável em versão compacta (1 linha) */}
            {showPagamento && (
              <div className="flex items-center justify-between gap-2">
                <span className={`text-xs uppercase tracking-wide ${escuro ? 'text-ink-400' : 'text-ink-500'}`}>Desconto</span>
                <div className="flex items-center gap-1">
                  <div className={`flex items-center rounded text-[11px] ${escuro ? 'bg-ink-800' : 'bg-ink-200'}`}>
                    <button onClick={() => setDescontoTipo('R$')}
                      className={`px-1.5 py-0.5 rounded-l ${descontoTipo === 'R$' ? 'bg-brand-600 text-white' : (escuro ? 'text-ink-400' : 'text-ink-600')}`}>R$</button>
                    <button onClick={() => setDescontoTipo('%')}
                      className={`px-1.5 py-0.5 rounded-r ${descontoTipo === '%' ? 'bg-brand-600 text-white' : (escuro ? 'text-ink-400' : 'text-ink-600')}`}>%</button>
                  </div>
                  <input
                    type="number" min="0" step="0.01"
                    value={descontoInput}
                    disabled={itens.length === 0}
                    onChange={e => setDescontoInput(e.target.value)}
                    onFocus={e => e.target.select()}
                    placeholder="0,00"
                    className={`w-24 px-2 py-1 border rounded text-right text-sm focus:border-brand-500 focus:outline-none disabled:opacity-40
                      ${escuro ? 'bg-ink-950 border-ink-700 text-white' : 'bg-white border-ink-200 text-ink-900'}`}
                  />
                </div>
              </div>
            )}
            {showPagamento && descontoVenda > 0 && (
              <div className={`flex justify-between text-[11px] ${escuro ? 'text-amber-400' : 'text-amber-600'}`}>
                <span>Desconto aplicado</span>
                <span>−{formatMoney(descontoVenda)}</span>
              </div>
            )}
            {showPagamento && bikeTotal > 0 && (
              <div className={`flex justify-between text-[11px] ${escuro ? 'text-amber-400' : 'text-amber-600'}`}>
                <span>Bicicleta na troca</span>
                <span>−{formatMoney(bikeTotal)}</span>
              </div>
            )}
            {showPagamento && descontoVenda > 0 && bikeTotal > 0 && (
              <div className={`flex justify-between text-[11px] font-semibold ${escuro ? 'text-amber-300' : 'text-amber-700'}`}>
                <span>Total de desconto</span>
                <span>−{formatMoney(descontoAplicadoExibido)}</span>
              </div>
            )}

            <div className={`flex justify-between items-end ${showPagamento ? 'pt-0' : 'pt-1'}`}>
              <span className={`${showPagamento ? 'text-xs' : ''} ${escuro ? 'text-ink-400' : 'text-ink-600'}`}>Total</span>
              {/* 30/06: total continua GRANDE durante o pagamento (antes virava text-base, ficava minúsculo). */}
              <span className={showPagamento ? 'text-4xl font-bold tracking-tight' : 'text-5xl font-bold tracking-tight'}>{formatMoney(totalFinal)}</span>
            </div>
          </div>
        </div>

        {/* Botões de pagamento */}
        <div className="p-4 space-y-2 shrink-0">
          {!showPagamento ? (
            <>
              <button
                disabled={itens.length === 0}
                onClick={iniciarPagamento}
                className="w-full py-4 rounded-xl bg-emerald-600 hover:bg-emerald-700 disabled:opacity-30 disabled:cursor-not-allowed
                           text-white text-lg font-semibold transition-colors flex items-center justify-center gap-2">
                <CreditCard size={22} /> Pagamento
              </button>
              <button onClick={() => { setItens([]); setDescontoInput(''); inputRef.current?.focus() }}
                disabled={itens.length === 0}
                className={`w-full py-2.5 rounded-xl text-sm transition-colors flex items-center justify-center gap-2 disabled:opacity-30
                  ${escuro
                    ? 'bg-ink-800 hover:bg-ink-700 text-ink-200'
                    : 'bg-ink-100 hover:bg-ink-200 text-ink-700'}`}>
                <Trash2 size={14} /> Limpar Carrinho
              </button>
            </>
                    ) : (
            <PainelPagamento
              total={totalFinal}
              cliente={cliente}
              pagamentos={pagamentos}
              setPagamentos={setPagamentos}
              valorRecebido={valorRecebido}
              setValorRecebido={setValorRecebido}
              finalizar={finalizar}
              onBack={() => { setShowPagamento(false) }}
              osVinculadas={osVinculadas}
              finalizandoMultiplas={finalizandoMultiplas}
              finalizarMultiplasOS={finalizarMultiplasOS}
            />
          )}
        </div>
      </div>

      {/* MODAL PAGAMENTO MOBILE */}
      {showPagamento && (
        <div className="lg:hidden fixed inset-0 bg-ink-950/90 flex items-end z-50">
          <div className="bg-ink-900 w-full rounded-t-2xl p-4 pb-8 max-h-[85vh] overflow-auto">
            <div className="flex items-center justify-between mb-4">
              <span className="text-white font-semibold">Total: {formatMoney(totalFinal)}</span>
              <button onClick={() => { setShowPagamento(false) }}
                className="p-2 rounded-lg hover:bg-ink-800 text-ink-400"><X size={20} /></button>
            </div>
            <PainelPagamento
              total={totalFinal}
              cliente={cliente}
              pagamentos={pagamentos}
              setPagamentos={setPagamentos}
              valorRecebido={valorRecebido}
              setValorRecebido={setValorRecebido}
              finalizar={finalizar}
              onBack={() => { setShowPagamento(false) }}
              osVinculadas={osVinculadas}
              finalizandoMultiplas={finalizandoMultiplas}
              finalizarMultiplasOS={finalizarMultiplasOS}
            />
          </div>
        </div>
      )}

      {/* GATE DE CLIENTE — bloqueia a venda até o cadastro estar completo (19/10). */}
      {showClienteGate && (
        <ClienteGate cliente={cliente} setCliente={setCliente}
          onClose={() => setShowClienteGate(false)}
          onOk={iniciarPagamento} />
      )}

      {/* GATE DE VENDEDOR — bloqueia a venda até escolher o vendedor (03/07).

      {/* GATE DE VENDEDOR — bloqueia a venda até escolher o vendedor (03/07).
          Escolha rápida: digita nome/número, ↑↓ navega, Enter seleciona e já segue
          pro pagamento. Esc cancela e volta pra venda. */}
      {showVendedorGate && (
        <div className="fixed inset-0 bg-ink-950/85 z-[70] flex items-center justify-center p-4"
          role="dialog" aria-modal="true" aria-label="Escolher vendedor"
          onClick={() => setShowVendedorGate(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-5" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-1">
              <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center">
                {vendedorEhOficina ? <Wrench size={18} className="text-amber-600" /> : <UserPlus size={18} className="text-amber-600" />}
              </div>
              <div>
                <div className="font-semibold text-ink-900">{vendedorEhOficina ? 'Selecione o mecânico' : 'Escolha o vendedor'}</div>
                <div className="text-xs text-ink-500">{vendedorEhOficina ? 'Vendedor OFICINA — informe quem executou o serviço.' : 'Obrigatório para finalizar a venda.'}</div>
              </div>
            </div>

            {/* Passo 2 (OFICINA): escolher o mecânico. Passo 1: escolher o vendedor. (06/07) */}
            {vendedorEhOficina ? (
              <>
                <select
                  autoFocus
                  value={mecanicoCadId}
                  onChange={e => setMecanicoCadId(e.target.value ? Number(e.target.value) : '')}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); confirmarMecanicoNoGate() } }}
                  className="w-full mt-3 px-3 py-2 border border-ink-300 rounded-lg text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 text-ink-900">
                  <option value="">Selecione o mecânico...</option>
                  {mecanicosPDV.map((m: any) => (
                    <option key={m.id} value={m.id}>{m.nome}{m.codigo ? ` · ${m.codigo}` : ''}</option>
                  ))}
                </select>
                {mecanicosPDV.length === 0 && (
                  <div className="mt-2 text-xs text-amber-600">Nenhum mecânico (colaborador tipo técnico) cadastrado nesta filial.</div>
                )}
                <div className="mt-3 flex justify-between">
                  <button type="button" onClick={() => { setVendedorCadId(''); setBuscaVendedor(''); setMecanicoCadId('') }}
                    className="px-3 py-1.5 rounded-lg text-sm text-ink-600 hover:bg-ink-100">← Trocar vendedor</button>
                  <button type="button" onClick={confirmarMecanicoNoGate}
                    className="px-4 py-1.5 rounded-lg text-sm font-medium bg-amber-600 hover:bg-amber-700 text-white">Continuar</button>
                </div>
              </>
            ) : (
              <>
                <input
                  autoFocus
                  value={buscaVendedor}
                  placeholder="Nome ou número do vendedor..."
                  autoComplete="off"
                  onChange={e => { setBuscaVendedor(e.target.value); setGateHi(0) }}
                  onKeyDown={e => {
                    if (e.key === 'ArrowDown') { e.preventDefault(); setGateHi(h => Math.min(h + 1, vendedoresFiltrados.length - 1)) }
                    else if (e.key === 'ArrowUp') { e.preventDefault(); setGateHi(h => Math.max(h - 1, 0)) }
                    else if (e.key === 'Enter' && vendedoresFiltrados.length > 0) {
                      e.preventDefault()
                      const v = vendedoresFiltrados[Math.min(gateHi, vendedoresFiltrados.length - 1)]
                      if (v) escolherVendedorNoGate(v)
                    }
                  }}
                  className="w-full mt-3 px-3 py-2 border border-ink-300 rounded-lg text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 text-ink-900" />
                <div className="mt-2 max-h-64 overflow-y-auto border border-ink-100 rounded-lg divide-y divide-ink-100">
                  {vendedoresFiltrados.length === 0 ? (
                    <div className="p-4 text-center text-sm text-ink-400">Nenhum vendedor encontrado</div>
                  ) : vendedoresFiltrados.map((v: any, idx: number) => (
                    <button key={v.id} type="button" onMouseDown={e => e.preventDefault()}
                      ref={el => { if (idx === gateHi) el?.scrollIntoView({ block: 'nearest' }) }}
                      onMouseEnter={() => setGateHi(idx)}
                      onClick={() => escolherVendedorNoGate(v)}
                      className={`w-full px-3 py-2 text-left text-sm text-ink-800 ${idx === gateHi ? 'bg-brand-100' : 'hover:bg-brand-50'}`}>
                      {v.nome}{v.codigo ? <span className="text-ink-400 font-mono"> · {v.codigo}</span> : ''}
                    </button>
                  ))}
                </div>
                <div className="mt-3 flex justify-end">
                  <button type="button" onClick={() => setShowVendedorGate(false)}
                    className="px-3 py-1.5 rounded-lg text-sm text-ink-600 hover:bg-ink-100">Cancelar (Esc)</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* MODAL — Seleção de grade/unidade/QUALIDADE do produto (industrial — IBK) */}
      {gradeModal && (
        <div className="fixed inset-0 bg-ink-950/85 z-[60] flex items-center justify-center p-4"
          role="dialog" aria-modal="true" aria-label="Selecionar opções do produto">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-brand-100 flex items-center justify-center text-xl">🎨</div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-ink-900 truncate">{gradeModal.produto.descricao}</h3>
                <p className="text-xs text-ink-500">Escolha as opções pra adicionar ao carrinho</p>
              </div>
              <button onClick={() => setGradeModal(null)}
                className="p-1.5 rounded-lg hover:bg-ink-100 text-ink-500">
                <X size={18} />
              </button>
            </div>

            <div className="space-y-3 mb-5">
              {gradeModal.is_industrial && (
                <div>
                  <div className="text-xs font-semibold text-ink-600 uppercase mb-1.5">Qualidade exigida pelo cliente</div>
                  <div className="flex flex-wrap gap-1.5">
                    {(() => {
                      const STYLES = {
                        PRIMEIRA: { ativo: 'bg-emerald-600 text-white border-emerald-600', emoji: '🟢' },
                        SEGUNDA:  { ativo: 'bg-amber-500 text-white border-amber-500',     emoji: '🟡' },
                        TERCEIRA: { ativo: 'bg-red-600 text-white border-red-600',         emoji: '🔴' },
                      } as const
                      return (['PRIMEIRA', 'SEGUNDA', 'TERCEIRA'] as const).map(q => {
                        const ativo = gradeModal.qualidade_exigida === q
                        return (
                          <button key={q} type="button"
                            onClick={() => setGradeModal(prev => prev && ({ ...prev, qualidade_exigida: q }))}
                            className={'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border transition ' +
                              (ativo ? STYLES[q].ativo : 'bg-white text-ink-700 border-ink-300 hover:border-brand-400')}>
                            {STYLES[q].emoji} {q}
                          </button>
                        )
                      })
                    })()}
                  </div>
                  <p className="text-[11px] text-ink-500 mt-1">
                    PRIMEIRA = produto sem defeito · SEGUNDA = defeito leve (com desconto) · TERCEIRA = defeito funcional
                  </p>
                </div>
              )}
              {gradeModal.unidades.length > 0 && (
                <div>
                  <div className="text-xs font-semibold text-ink-600 uppercase mb-1.5">Unidade de venda</div>
                  <div className="flex flex-wrap gap-1.5">
                    <button type="button"
                      onClick={() => setGradeModal(prev => prev && ({ ...prev, conversao_id: null }))}
                      className={'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border transition ' +
                        (!gradeModal.conversao_id
                          ? 'bg-brand-600 text-white border-brand-600 shadow-sm'
                          : 'bg-white text-ink-700 border-ink-300 hover:border-brand-400')}>
                      {gradeModal.produto.unidade}
                    </button>
                    {gradeModal.unidades.map(u => {
                      const ativo = gradeModal.conversao_id === u.id
                      return (
                        <button key={u.id} type="button"
                          onClick={() => setGradeModal(prev => prev && ({ ...prev, conversao_id: u.id }))}
                          className={'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border transition ' +
                            (ativo
                              ? 'bg-brand-600 text-white border-brand-600 shadow-sm'
                              : 'bg-white text-ink-700 border-ink-300 hover:border-brand-400')}
                          title={`1 ${u.unidade} = ${u.fator} ${gradeModal.produto.unidade}`}>
                          {u.unidade}
                          <span className="opacity-60 text-xs">×{u.fator}</span>
                        </button>
                      )
                    })}
                  </div>
                </div>
              )}
              {Array.from(new Map(gradeModal.grades.map(g => [g.tipo_id, g])).values()).map(g => {
                const tipoId = g.tipo_id
                const opcoes = gradeModal.grades.filter(x => x.tipo_id === tipoId)
                const escolhida = gradeModal.selecionadas[tipoId]
                return (
                  <div key={tipoId}>
                    <div className="text-xs font-semibold text-ink-600 uppercase mb-1.5">{g.tipo_label}</div>
                    <div className="flex flex-wrap gap-1.5">
                      {opcoes.map(opt => {
                        const ativo = escolhida === opt.id
                        return (
                          <button key={opt.id} type="button"
                            onClick={() => setGradeModal(prev => prev && ({
                              ...prev,
                              selecionadas: { ...prev.selecionadas, [tipoId]: opt.id }
                            }))}
                            className={'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border transition ' +
                              (ativo
                                ? 'bg-brand-600 text-white border-brand-600 shadow-sm'
                                : 'bg-white text-ink-700 border-ink-300 hover:border-brand-400')}>
                            {opt.hex_cor && (
                              <span className="w-4 h-4 rounded-full border border-white/40"
                                style={{ backgroundColor: opt.hex_cor }} />
                            )}
                            {opt.label}
                          </button>
                        )
                      })}
                    </div>
                  </div>
                )
              })}
            </div>

            <div className="flex gap-2">
              <button onClick={() => setGradeModal(null)}
                className="flex-1 py-2.5 rounded-lg border border-ink-300 text-ink-700 font-semibold hover:bg-ink-50">
                Cancelar
              </button>
              <button onClick={confirmarGrade}
                className="flex-1 py-2.5 rounded-lg bg-brand-600 text-white font-semibold hover:bg-brand-700">
                Adicionar ao carrinho
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL — Abrir caixa */}
      {showAbrirCaixa && !sessaoCaixa && (
        <div className="fixed inset-0 bg-ink-950/85 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center"><Lock size={20} className="text-amber-700" /></div>
              <div>
                <h3 className="text-lg font-bold text-ink-900">Abrir Caixa</h3>
                <p className="text-xs text-ink-500">Necessário antes de iniciar vendas</p>
              </div>
            </div>
            <div className="space-y-3" onKeyDown={enterAdvance}>
              <div>
                <label className="label">Conta / Caixa</label>
                <select className="input" value={caixaContaId}
                  onChange={e => setCaixaContaId(e.target.value ? Number(e.target.value) : '')}>
                  <option value="">Selecione...</option>
                  {contasBancarias.filter((c: any) => c.ativo !== false).map((c: any) => (
                    <option key={c.id} value={c.id}>{c.nome}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label">Fundo de troco (R$)</label>
                <input className="input" type="number" step="0.01" min="0" value={valorAbertura}
                  onChange={e => setValorAbertura(e.target.value)} placeholder="0,00" autoFocus />
              </div>
              <div>
                <label className="label">Observação (opcional)</label>
                <input className="input" value={obsAbertura}
                  onChange={e => setObsAbertura(e.target.value.toUpperCase())} placeholder="Ex: turno manhã" />
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <button className="btn-secondary flex-1" onClick={() => setShowAbrirCaixa(false)}>Cancelar</button>
              <button className="btn-primary flex-1" disabled={!caixaContaId || abrirCaixa.isPending}
                onClick={() => abrirCaixa.mutate()}>
                {abrirCaixa.isPending ? <Loader2 size={16} className="animate-spin" /> : <><Unlock size={14} className="inline mr-1" />Abrir caixa</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL — Sangria */}
      {showSangria && sessaoCaixa && (
        <div className="fixed inset-0 bg-ink-950/85 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center"><ArrowDownToLine size={20} className="text-amber-700" /></div>
              <div>
                <h3 className="text-lg font-bold text-ink-900">Sangria</h3>
                <p className="text-xs text-ink-500">Retirada de dinheiro do caixa</p>
              </div>
            </div>
            <div className="space-y-3" onKeyDown={enterAdvance}>
              <div>
                <label className="label">Valor (R$)</label>
                <input className="input text-lg font-bold" type="number" step="0.01" min="0.01"
                  value={movValor} onChange={e => setMovValor(e.target.value)} placeholder="0,00" autoFocus />
              </div>
              <div>
                <label className="label">Motivo</label>
                <input className="input" value={movDescricao}
                  onChange={e => setMovDescricao(e.target.value)} placeholder="Ex: troco para banco, pagamento fornecedor" />
              </div>
              <div>
                <label className="label">Vendedor responsável</label>
                <select className="input" value={movResponsavelId}
                  onChange={e => setMovResponsavelId(e.target.value ? Number(e.target.value) : '')}>
                  <option value="">— selecione o vendedor —</option>
                  {vendedoresPDV.map((v: any) => (
                    <option key={v.id} value={v.id}>{v.nome}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label">Senha de autorização <span className="text-ink-400 font-normal">(qualquer usuário)</span></label>
                {/* type=text + text-security: mascara visualmente SEM ser um campo de senha,
                    assim o navegador/gerenciador não oferece senhas salvas */}
                <input className="input" type="text" value={movSenha} name="sangria-confirm"
                  autoComplete="off" data-lpignore="true" data-1p-ignore data-form-type="other"
                  style={{ WebkitTextSecurity: 'disc', textSecurity: 'disc' } as React.CSSProperties}
                  onChange={e => setMovSenha(e.target.value)} placeholder="Confirme com sua senha" />
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <button className="btn-secondary flex-1" onClick={() => { setShowSangria(false); setMovSenha(''); setMovResponsavelId('') }}>Cancelar</button>
              <button className="btn-primary flex-1 !bg-amber-600 hover:!bg-amber-700"
                disabled={!movValor || parseFloat(movValor) <= 0 || !movResponsavelId || !movSenha || sangriaMut.isPending}
                onClick={() => sangriaMut.mutate()}>
                {sangriaMut.isPending ? <Loader2 size={16} className="animate-spin" /> : 'Confirmar sangria'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL — Suprimento */}
      {showSuprimento && sessaoCaixa && (
        <div className="fixed inset-0 bg-ink-950/85 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center"><ArrowUpFromLine size={20} className="text-sky-700" /></div>
              <div>
                <h3 className="text-lg font-bold text-ink-900">Suprimento</h3>
                <p className="text-xs text-ink-500">Entrada extra de dinheiro no caixa</p>
              </div>
            </div>
            <div className="space-y-3" onKeyDown={enterAdvance}>
              <div>
                <label className="label">Valor (R$)</label>
                <input className="input text-lg font-bold" type="number" step="0.01" min="0.01"
                  value={movValor} onChange={e => setMovValor(e.target.value)} placeholder="0,00" autoFocus />
              </div>
              <div>
                <label className="label">Motivo</label>
                <input className="input" value={movDescricao}
                  onChange={e => setMovDescricao(e.target.value)} placeholder="Ex: reforço de troco" />
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <button className="btn-secondary flex-1" onClick={() => setShowSuprimento(false)}>Cancelar</button>
              <button className="btn-primary flex-1 !bg-sky-600 hover:!bg-sky-700"
                disabled={!movValor || parseFloat(movValor) <= 0 || suprimentoMut.isPending}
                onClick={() => suprimentoMut.mutate()}>
                {suprimentoMut.isPending ? <Loader2 size={16} className="animate-spin" /> : 'Confirmar suprimento'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL — Saída de valor (despesa do caixa) */}
      {showSaida && sessaoCaixa && (
        <div className="fixed inset-0 bg-ink-950/85 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-rose-100 flex items-center justify-center"><Wallet size={20} className="text-rose-700" /></div>
              <div>
                <h3 className="text-lg font-bold text-ink-900">Saída de valor</h3>
                <p className="text-xs text-ink-500">Despesa paga pelo caixa (sai da gaveta e vira despesa). Não é sangria.</p>
              </div>
            </div>
            <div className="space-y-3" onKeyDown={enterAdvance}>
              <div>
                <label className="label">Valor (R$)</label>
                <input className="input text-lg font-bold" type="number" step="0.01" min="0.01"
                  value={movValor} onChange={e => setMovValor(e.target.value)} placeholder="0,00" autoFocus />
              </div>
              <div>
                <label className="label">Categoria (plano de contas)</label>
                <select className="input" value={saidaCgId}
                  onChange={e => setSaidaCgId(e.target.value ? Number(e.target.value) : '')}>
                  <option value="">— sem categoria —</option>
                  {categoriasSaida.map((c: any) => (
                    <option key={c.id} value={c.id}>{c.codigo ? `${c.codigo} · ` : ''}{c.nome}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label">Descrição</label>
                <input className="input" value={movDescricao}
                  onChange={e => setMovDescricao(e.target.value)} placeholder="Ex: pagamento entregador de água, material de limpeza" />
              </div>
              <div>
                <label className="label">Vendedor responsável</label>
                <select className="input" value={movResponsavelId}
                  onChange={e => setMovResponsavelId(e.target.value ? Number(e.target.value) : '')}>
                  <option value="">— selecione o vendedor —</option>
                  {vendedoresPDV.map((v: any) => (
                    <option key={v.id} value={v.id}>{v.nome}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label">Senha de autorização <span className="text-ink-400 font-normal">(qualquer usuário)</span></label>
                <input className="input" type="text" value={movSenha} name="saida-confirm"
                  autoComplete="off" data-lpignore="true" data-1p-ignore data-form-type="other"
                  style={{ WebkitTextSecurity: 'disc', textSecurity: 'disc' } as React.CSSProperties}
                  onChange={e => setMovSenha(e.target.value)} placeholder="Confirme com sua senha" />
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <button className="btn-secondary flex-1" onClick={() => { setShowSaida(false); setMovSenha(''); setMovResponsavelId('') }}>Cancelar</button>
              <button className="btn-primary flex-1 !bg-rose-600 hover:!bg-rose-700"
                disabled={!movValor || parseFloat(movValor) <= 0 || !movResponsavelId || !movSenha || saidaMut.isPending}
                onClick={() => saidaMut.mutate()}>
                {saidaMut.isPending ? <Loader2 size={16} className="animate-spin" /> : 'Confirmar saída'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL — Entrada de valor (receita do caixa) */}
      {showEntrada && sessaoCaixa && (
        <div className="fixed inset-0 bg-ink-950/85 z-[60] flex items-center justify-center p-4">
          {/* 06/07: max-h + overflow p/ NUNCA passar da tela quando o cartão abre operadora/parcelas. */}
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-5 max-h-[92vh] overflow-y-auto">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center shrink-0"><Wallet size={18} className="text-emerald-700" /></div>
              <div>
                <h3 className="text-base font-bold text-ink-900">Entrada de valor</h3>
                <p className="text-[11px] text-ink-500">Receita recebida no caixa (vira receita). Não é suprimento/troco.</p>
              </div>
            </div>
            <div className="space-y-2.5" onKeyDown={enterAdvance}>
              <div>
                <label className="label">Valor (R$)</label>
                <input className="input text-lg font-bold" type="number" step="0.01" min="0.01"
                  value={movValor} onChange={e => setMovValor(e.target.value)} placeholder="0,00" autoFocus />
              </div>
              <div>
                <label className="label">Forma de recebimento</label>
                <select className="input" value={entradaForma}
                  onChange={e => escolherFormaEntrada(e.target.value)}>
                  {formasEntrada.length === 0 && <option value="dinheiro">Dinheiro</option>}
                  {formasEntrada.map((f: any) => (
                    <option key={f.id ?? f.forma} value={f.forma}>{f.label || f.forma}</option>
                  ))}
                </select>
                <p className="text-[11px] text-ink-400 mt-1">
                  {entradaForma === 'dinheiro'
                    ? 'Dinheiro entra na gaveta do caixa.'
                    : 'Recebido na máquina/PIX — vira receita, mas não entra no numerário da gaveta.'}
                </p>
              </div>

              {/* FEATURE 06/07: cartão/link abre operadora/máquina, modalidade (link) e
                  parcelas (crédito) + taxa — igual ao painel de pagamento da venda.
                  BUG: antes o modal de entrada não mostrava nada disso. */}
              {entradaUsaOperadora && entradaOpsDaForma.length > 0 && (
                <div className="rounded-lg border border-ink-200 bg-ink-50 p-2 space-y-1.5">
                  <div>
                    <label className="text-[10px] text-ink-500 uppercase tracking-wide font-semibold">
                      {entradaEhLink ? 'Provedor' : 'Operadora / máquina'}
                    </label>
                    <div className="flex flex-wrap gap-1 mt-0.5">
                      {entradaOpsDaForma.map((o: any) => {
                        const sel = entradaOperadoraId === o.id
                        return (
                          <button key={o.id} type="button" onClick={() => setEntradaOperadoraId(o.id)}
                            className={`px-2 py-0.5 rounded text-[11px] font-medium border transition-colors ${
                              sel ? 'bg-emerald-600 border-emerald-600 text-white'
                                  : 'bg-white border-ink-200 text-ink-700 hover:bg-ink-100'
                            }`}>{o.nome}</button>
                        )
                      })}
                    </div>
                  </div>

                  {entradaEhLink && (
                    <div>
                      <label className="text-[10px] text-ink-500 uppercase tracking-wide font-semibold">Modalidade</label>
                      <div className="flex gap-1 mt-0.5">
                        {[['debito', 'Débito'], ['credito', 'Crédito'], ['pix', 'Pix']].map(([m, lbl]) => (
                          <button key={m} type="button" onClick={() => { setEntradaModalidade(m as string); setEntradaParcelas(1) }}
                            className={`flex-1 py-0.5 rounded text-[11px] font-medium border transition-colors ${
                              entradaModalidade === m ? 'bg-emerald-600 border-emerald-600 text-white'
                                                      : 'bg-white border-ink-200 text-ink-700 hover:bg-ink-100'
                            }`}>{lbl}</button>
                        ))}
                      </div>
                    </div>
                  )}

                  {entradaMostrarParcelas && (
                    <div>
                      <label className="text-[10px] text-ink-500 uppercase tracking-wide font-semibold">Parcelas</label>
                      <div className="flex flex-wrap gap-0.5 mt-0.5">
                        {Array.from({ length: 18 }, (_, i) => i + 1).map(n => (
                          <button key={n} type="button" onClick={() => setEntradaParcelas(n)}
                            className={`min-w-[26px] py-0.5 rounded text-[11px] font-medium border transition-colors ${
                              entradaParcelas === n ? 'bg-emerald-600 border-emerald-600 text-white'
                                                    : 'bg-white border-ink-200 text-ink-700 hover:bg-ink-100'
                            }`}>{n}x</button>
                        ))}
                      </div>
                    </div>
                  )}

                  {entradaOpSel && entradaModalidadeEfetiva && (
                    <div className="text-[10px] text-ink-500">
                      Taxa {entradaModalidadeEfetiva === 'debito' ? 'débito' : entradaModalidadeEfetiva === 'pix' ? 'pix' : `crédito ${entradaParcelas}x`}:{' '}
                      <span className="text-amber-600 font-semibold">{entradaTaxaPct}%</span>
                      {parseFloat(movValor || '0') > 0 && entradaTaxaPct > 0 && (
                        <> · custo {formatMoney(parseFloat(movValor) * entradaTaxaPct / 100)}</>
                      )}
                    </div>
                  )}
                </div>
              )}
              <div>
                <label className="label">Categoria (plano de contas)</label>
                <select className="input" value={entradaCgId}
                  onChange={e => setEntradaCgId(e.target.value ? Number(e.target.value) : '')}>
                  <option value="">— sem categoria —</option>
                  {categoriasEntrada.map((c: any) => (
                    <option key={c.id} value={c.id}>{c.codigo ? `${c.codigo} · ` : ''}{c.nome}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label">Descrição</label>
                <input className="input" value={movDescricao}
                  onChange={e => setMovDescricao(e.target.value)} placeholder="Ex: recebimento avulso, reembolso, venda de sucata" />
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <button className="btn-secondary flex-1" onClick={() => { setShowEntrada(false); setEntradaForma('dinheiro'); setEntradaOperadoraId(null); setEntradaModalidade(''); setEntradaParcelas(1) }}>Cancelar</button>
              <button className="btn-primary flex-1 !bg-emerald-600 hover:!bg-emerald-700"
                disabled={!movValor || parseFloat(movValor) <= 0 || entradaMut.isPending}
                onClick={() => {
                  // FEATURE 06/07: cartão/link exige operadora (e modalidade no link) quando há operadora cadastrada.
                  if (entradaUsaOperadora && entradaOpsDaForma.length > 0) {
                    if (!entradaOpSel) { toast.error('Escolha a operadora'); return }
                    if (entradaEhLink && !entradaModalidade) { toast.error('Escolha débito, crédito ou pix'); return }
                  }
                  entradaMut.mutate()
                }}>
                {entradaMut.isPending ? <Loader2 size={16} className="animate-spin" /> : 'Confirmar entrada'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* FEATURE 01/07: MODAL — Receber título a prazo pelo caixa */}
      {showReceberTitulo && sessaoCaixa && (
        <ReceberTituloModal
          sessaoId={sessaoCaixa.id}
          clienteInicial={cliente}
          onClose={() => setShowReceberTitulo(false)}
          onSuccess={() => {
            setShowReceberTitulo(false)
            qc.invalidateQueries({ queryKey: ['caixa-sessao-atual'] })
            qc.invalidateQueries({ queryKey: ['caixa-sessao-stats', sessaoCaixa?.id] })
          }}
        />
      )}

      {/* MODAL — Confirmar saída com venda em aberto */}
      {confirmSair && (
        <div className="fixed inset-0 bg-ink-950/85 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                <AlertTriangle size={20} className="text-amber-700" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-ink-900">Venda em andamento</h3>
                <p className="text-xs text-ink-500">
                  Há {totalItens} {totalItens === 1 ? 'item' : 'itens'} no carrinho ({formatMoney(totalFinal)}).
                </p>
              </div>
            </div>
            <p className="text-sm text-ink-700 mb-5">
              O que deseja fazer com a venda em andamento?
            </p>
            <div className="flex flex-col gap-2">
              <button
                onClick={() => setConfirmSair(null)}
                className="w-full py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold transition-colors">
                Continuar venda
              </button>
              <button
                onClick={() => salvarEspera.mutate()}
                disabled={salvarEspera.isPending}
                className="w-full py-3 rounded-xl bg-amber-500 hover:bg-amber-600 text-white font-semibold transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
                {salvarEspera.isPending ? <Loader2 size={18} className="animate-spin" /> : <Clock size={18} />}
                Salvar venda e sair
              </button>
              <button
                onClick={() => {
                  const destino = confirmSair?.destino || '/'
                  setItens([])
                  setDescontoInput('')
                  setConfirmSair(null)
                  nav(destino)
                }}
                className="w-full py-3 rounded-xl bg-rose-600 hover:bg-rose-700 text-white font-semibold transition-colors">
                Cancelar venda e sair
              </button>
            </div>
            <p className="mt-4 text-center text-xs text-ink-400">
              <kbd className="px-1.5 py-0.5 rounded bg-ink-100 text-ink-600 font-mono">Esc</kbd> continuar na venda
              <span className="mx-2">·</span>
              <kbd className="px-1.5 py-0.5 rounded bg-ink-100 text-ink-600 font-mono">Enter</kbd> descartar e voltar
            </p>
          </div>
        </div>
      )}

      {/* MODAL — Vendas de hoje / reimprimir cupom */}
      {showVendasDoDia && (
        <ModalVendasDoDia emissor={{ ...emissor, logo_url: logoLoja }} sessaoId={sessaoCaixa?.id ?? null} onClose={() => setShowVendasDoDia(false)} />
      )}

      {showAddCredito && (
        <ModalAddCredito
          clienteAtual={cliente}
          onClose={() => setShowAddCredito(false)}
          onFeito={() => { if (cliente?.id) qc.invalidateQueries({ queryKey: ['cliente-credito', cliente.id] }); qc.invalidateQueries({ queryKey: ['caixa-sessao-atual'] }); setShowAddCredito(false) }}
        />
      )}

      {showServicosOS && (
        <ModalServicosConcluidos onClose={() => setShowServicosOS(false)} onEscolher={carregarOS} onEscolherMultiplas={carregarOSMultiplas} />
      )}

      {showTutorial && <TutorialPDV onClose={() => setShowTutorial(false)} />}

      {/* MODAL — Seletor de filial/estoque (aparece no fechamento da venda) */}
      {/* 30/06: o modal inclui as filiais "apenas fiscal" como opção de emissor da nota.
          A venda continua na filial do operador (resolvido no backend). */}
      {showSeletorFilial && (
        <ModalSeletorFilial
          filiais={[...filiaisAtivas, ...filiaisFiscais]}
          depositos={depositosVenda}
          itensCarrinho={itens.map(i => ({ produto_id: i.produto.id, quantidade: i.quantidade }))}
          filialAtual={filialId === '' ? null : filialId}
          depositoAtual={depositoId === '' ? null : depositoId}
          confirmLabel={itens.length > 0 ? 'Confirmar e ir pra pagamento' : 'Confirmar'}
          onConfirmar={(novoFilial, novoDeposito) => {
            setFilialId(novoFilial)
            setDepositoId(novoDeposito)
            setShowSeletorFilial(false)
            // Se há itens no carrinho, o user veio do botão Pagamento — abre painel.
            if (itens.length > 0) setShowPagamento(true)
          }}
          onClose={() => setShowSeletorFilial(false)}
        />
      )}

      {/* MODAL — Fechar caixa */}
      {/* UI 02/07: o PDV tem tema próprio (não usa .app-dark global); este modal ficava
          BRANCO no escuro (bg-white sem tema). FIX: aplica a classe app-dark quando
          escuro, e aí todos os tokens (bg-white, bg-ink, text-ink) remapeiam pelo index.css.
          Alargado (max-w-lg para max-w-3xl) p/ caber mais formas na matriz sem rolar. */}
      {/* FEATURE 08/07: modal da VIRADA DE DIA — fecha o caixa de ontem e abre o de hoje. */}
      {showVirarDia && sessaoCaixa && (
        <div className={`fixed inset-0 bg-ink-950/85 z-[60] flex items-center justify-center p-4 ${escuro ? 'app-dark' : ''}`}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-5">
            <div className="flex items-center gap-2.5 mb-3">
              <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center"><RefreshCw size={17} className="text-amber-700" /></div>
              <div>
                <h3 className="text-base font-bold text-ink-900 leading-tight">Virada de dia</h3>
                <p className="text-[11px] text-ink-500">Fecha o caixa de ontem e abre um novo para hoje</p>
              </div>
            </div>
            <div className="text-[13px] text-ink-700 bg-amber-50 border border-amber-200 rounded-xl p-3 mb-3 space-y-1.5">
              <p>Este caixa foi aberto em <b>{sessaoCaixa.aberto_data ? new Date(sessaoCaixa.aberto_data + 'T12:00:00').toLocaleDateString('pt-BR') : 'um dia anterior'}</b> e nunca foi fechado.</p>
              <p>A movimentação até o fim de ontem fica no caixa fechado; as vendas de hoje passam para um caixa novo, com o saldo da gaveta carregado.</p>
            </div>
            <label className="label">Valor contado na gaveta ao fim de ontem <span className="text-ink-400 font-normal">(opcional)</span></label>
            <input type="number" step="0.01" inputMode="decimal" className="input text-lg font-bold"
              value={valorContadoOntem} onChange={e => setValorContadoOntem(e.target.value)}
              placeholder="Deixe em branco para usar o valor calculado" />
            <p className="text-[11px] text-ink-500 mt-1">
              Se você não contou a gaveta ontem, deixe em branco: o caixa fecha no valor calculado (diferença zero, marcado como não conferido).
            </p>
            <div className="flex gap-2 mt-4">
              <button onClick={() => { setShowVirarDia(false); setValorContadoOntem('') }}
                className="flex-1 py-2.5 rounded-xl border border-ink-200 text-ink-700 font-medium hover:bg-ink-50">Cancelar</button>
              <button onClick={() => virarDia.mutate()} disabled={virarDia.isPending}
                className="flex-1 py-2.5 rounded-xl bg-amber-600 hover:bg-amber-700 text-white font-semibold disabled:opacity-60">
                {virarDia.isPending ? 'Processando…' : 'Fechar ontem e abrir hoje'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* FEATURE (pedido do cliente): prévia do fechamento — mesmo resumo do modal de
          Fechar Caixa, mas sem campo de valor contado e sem ação de fechar. Só ver. */}
      {showResumoCaixa && sessaoCaixa && (
        <div className={`fixed inset-0 bg-ink-950/85 z-[60] flex items-center justify-center p-4 ${escuro ? 'app-dark' : ''}`}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl p-4 max-h-[92vh] overflow-auto">
            <div className="flex items-center gap-2.5 mb-2.5">
              <div className="w-8 h-8 rounded-full bg-sky-100 flex items-center justify-center"><Eye size={16} className="text-sky-700" /></div>
              <div>
                <h3 className="text-base font-bold text-ink-900 leading-tight">Resumo do Caixa</h3>
                <p className="text-[11px] text-ink-500">Como está o caixa agora — o turno continua aberto</p>
              </div>
            </div>

            <ResumoVendasSessao sessaoId={sessaoCaixa.id} escuro={escuro} />

            <div className="bg-ink-50 rounded-xl p-2.5 mb-2.5 text-[13px] space-y-0.5">
              <div className="flex justify-between">
                <span className="text-ink-600">(+) Abertura (fundo)</span>
                <span className="font-medium text-ink-900">{formatMoney(sessaoCaixa.valor_abertura ?? 0)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-ink-600">(+) Vendas em dinheiro</span>
                <span className="font-medium text-ink-900">{formatMoney(sessaoCaixa.detalhes?.receitas_dinheiro ?? 0)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-ink-600">(+) Suprimentos</span>
                <span className="font-medium text-ink-900">{formatMoney(sessaoCaixa.detalhes?.suprimentos ?? 0)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-ink-600">(−) Sangrias</span>
                <span className="font-medium text-ink-900">{formatMoney(sessaoCaixa.detalhes?.sangrias ?? 0)}</span>
              </div>
              <div className="flex items-center justify-between gap-3 pt-2 mt-0.5 border-t border-ink-200">
                <span className="font-semibold text-ink-900">Esperado em caixa (dinheiro) agora</span>
                <span className="font-extrabold text-brand-700 text-2xl leading-none tabular-nums">{formatMoney(sessaoCaixa.valor_sistema_atual ?? 0)}</span>
              </div>
              {(sessaoCaixa.recebido_em_caixa ?? 0) > (sessaoCaixa.detalhes?.receitas_dinheiro ?? 0) && (
                <div className="flex justify-between text-[11px] text-ink-400">
                  <span>Total recebido hoje (dinheiro + pix + cartão)</span>
                  <span>{formatMoney(sessaoCaixa.recebido_em_caixa ?? 0)}</span>
                </div>
              )}
            </div>

            {/* Total por forma — mesmo quadro do fechamento (pedido do cliente 23/09):
                o operador precisa ver quanto entrou em dinheiro, cartão, pix etc.
                ANTES de fechar, não só o esperado em dinheiro. */}
            <div className="bg-ink-50 rounded-xl p-2.5 mb-2.5">
              <div className="text-[13px] font-semibold text-ink-900 mb-1.5">Total por forma de pagamento</div>
              {!detalheSessao ? (
                <div className="text-[11px] text-ink-500 py-2">Carregando…</div>
              ) : (
                <table className="w-full text-[13px]">
                  <thead className="text-[11px] text-ink-500 border-b border-ink-200">
                    <tr>
                      <th className="text-left py-1">Forma</th>
                      <th className="text-right py-1">Vendas</th>
                      <th className="text-right py-1">Recebimento</th>
                      <th className="text-right py-1">Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-ink-200/60">
                    {resumoPorForma.map(f => {
                      const fv = formaVisual(f.forma)
                      const fc = escuro ? fv.dark : fv.light
                      return (
                        <tr key={f.forma}>
                          <td className="py-1">
                            <span className="inline-flex items-center gap-1 text-xs capitalize" style={{ color: fc.text }}>
                              <fv.icon size={11} style={{ color: fc.icon }} />{f.forma.replace(/_/g, ' ')}
                            </span>
                          </td>
                          <td className="text-right py-1">{formatMoney(f.vendas)}</td>
                          <td className={`text-right py-1 ${f.receb > 0 ? 'text-sky-700' : 'text-ink-400'}`}>
                            {f.receb > 0 ? '+ ' + formatMoney(f.receb) : '—'}
                          </td>
                          <td className="text-right py-1 font-semibold">{formatMoney(f.total)}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={3} className="py-2 px-2 font-semibold text-white bg-brand-600 rounded-l-lg">Total Geral</td>
                      <td className="py-2 px-2 text-right font-bold text-white bg-brand-600 rounded-r-lg">
                        {formatMoney(resumoTotalGeral)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              )}
              <p className="text-[10px] text-ink-500 mt-1.5">
                Dinheiro = o esperado na gaveta agora (abertura + vendas + suprimentos − sangrias).
                No fechamento, ele é substituído pelo valor que você contar.
              </p>
            </div>

            <button className="btn-secondary w-full" onClick={() => setShowResumoCaixa(false)}>Fechar visualização</button>
          </div>
        </div>
      )}

      {showFecharCaixa && sessaoCaixa && (
        <div className={`fixed inset-0 bg-ink-950/85 z-[60] flex items-center justify-center p-4 ${escuro ? 'app-dark' : ''}`}>
          {/* UI 02/07: modal apertado p/ caber sem rolar (paddings/fontes menores). */}
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl p-4 max-h-[92vh] overflow-auto">
            <div className="flex items-center gap-2.5 mb-2.5">
              <div className="w-8 h-8 rounded-full bg-rose-100 flex items-center justify-center"><Lock size={16} className="text-rose-700" /></div>
              <div>
                <h3 className="text-base font-bold text-ink-900 leading-tight">Fechar Caixa</h3>
                <p className="text-[11px] text-ink-500">Conferência e fechamento da sessão</p>
              </div>
            </div>

            {/* Vendas da sessão — fonte de verdade pra fechamento */}
            <ResumoVendasSessao sessaoId={sessaoCaixa.id} escuro={escuro} />

            {/* Resumo do esperado em caixa */}
            <div className="bg-ink-50 rounded-xl p-2.5 mb-2.5 text-[13px] space-y-0.5">
              <div className="flex justify-between">
                <span className="text-ink-600">(+) Abertura (fundo)</span>
                <span className="font-medium text-ink-900">{formatMoney(sessaoCaixa.valor_abertura ?? 0)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-ink-600">(+) Vendas em dinheiro</span>
                <span className="font-medium text-ink-900">{formatMoney(sessaoCaixa.detalhes?.receitas_dinheiro ?? 0)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-ink-600">(+) Suprimentos</span>
                <span className="font-medium text-ink-900">{formatMoney(sessaoCaixa.detalhes?.suprimentos ?? 0)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-ink-600">(−) Sangrias</span>
                <span className="font-medium text-ink-900">{formatMoney(sessaoCaixa.detalhes?.sangrias ?? 0)}</span>
              </div>
              <div className="flex items-center justify-between gap-3 pt-2 mt-0.5 border-t border-ink-200">
                <span className="font-semibold text-ink-900">Esperado em caixa (dinheiro)</span>
                <span className="font-extrabold text-brand-700 text-2xl leading-none tabular-nums">{formatMoney(sessaoCaixa.valor_sistema_atual ?? 0)}</span>
              </div>
              {(sessaoCaixa.recebido_em_caixa ?? 0) > (sessaoCaixa.detalhes?.receitas_dinheiro ?? 0) && (
                <div className="flex justify-between text-[11px] text-ink-400">
                  <span>Total recebido hoje (dinheiro + pix + cartão)</span>
                  <span>{formatMoney(sessaoCaixa.recebido_em_caixa ?? 0)}</span>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <div>
                <label className="label flex items-center gap-1.5"><DollarSign size={14} /> Valor contado em dinheiro (R$)</label>
                <input className="input text-xl font-bold text-center !py-1.5" type="number" step="0.01" min="0"
                  value={valorContado} onChange={e => setValorContado(e.target.value)} placeholder="0,00" autoFocus />
                {valorContado && (
                  <div className={`text-xs mt-1 font-medium ${
                    parseFloat(valorContado) - (sessaoCaixa.valor_sistema_atual ?? 0) >= 0 ? 'text-emerald-600' : 'text-rose-600'
                  }`}>
                    Diferença: {formatMoney(parseFloat(valorContado) - (sessaoCaixa.valor_sistema_atual ?? 0))}
                    {parseFloat(valorContado) - (sessaoCaixa.valor_sistema_atual ?? 0) > 0 && ' (sobra)'}
                    {parseFloat(valorContado) - (sessaoCaixa.valor_sistema_atual ?? 0) < 0 && ' (falta)'}
                  </div>
                )}
              </div>
              <div>
                <label className="label">Observação</label>
                <textarea className="input !py-1.5" rows={1} value={obsFechamento}
                  onChange={e => setObsFechamento(e.target.value.toUpperCase())} placeholder="Ex: justificativa da diferença, ocorrências do turno" />
              </div>
            </div>

            <div className="flex gap-2 mt-3">
              <button className="btn-secondary flex-1" onClick={() => setShowFecharCaixa(false)}>Cancelar</button>
              <button className="btn-primary flex-1 !bg-rose-600 hover:!bg-rose-700"
                disabled={valorContado === '' || fecharCaixa.isPending}
                onClick={() => {
                  const diferenca = parseFloat(valorContado) - (sessaoCaixa.valor_sistema_atual ?? 0)
                  const msgDiferenca = Math.abs(diferenca) > 0.01
                    ? `\n\nAtenção: há uma diferença de ${formatMoney(diferenca)} entre o valor contado e o esperado.`
                    : ''
                  if (!window.confirm(`Confirma o fechamento deste caixa?${msgDiferenca}\n\nEssa ação não pode ser desfeita.`)) return
                  fecharCaixa.mutate()
                }}>
                {fecharCaixa.isPending ? <Loader2 size={16} className="animate-spin" /> : 'Fechar e gerar PDF'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ============ PAINEL DE PAGAMENTO ============
// Fluxo: usuário escolhe forma → digita valor (Enter) → linha entra na lista,
// saldo no topo desconta. Quando saldo = 0, aparece botão Finalizar.
// Crédito abre seletor de parcelas; crediário pede vencimento.
function PainelPagamento({ total, cliente, pagamentos, setPagamentos, valorRecebido, setValorRecebido, finalizar, onBack,
  osVinculadas, finalizandoMultiplas, finalizarMultiplasOS }: {
  total: number;
  cliente: { id: number; nome: string } | null;
  pagamentos: LinhaPag[]; setPagamentos: (l: LinhaPag[]) => void;
  valorRecebido: string; setValorRecebido: (v: string) => void;
  finalizar: any; onBack: () => void;
  // FIX (item #16): pra saber se é fechamento de VÁRIAS OS juntas e chamar o
  // split certo — sem isso o componente não enxergava essas variáveis do PDVPage.
  osVinculadas: any[]; finalizandoMultiplas: boolean;
  finalizarMultiplasOS: (linhas: LinhaPag[], creditoGerado: number) => Promise<void>;
}) {
  const valorPago = pagamentos.reduce((s, p) => s + p.valor, 0)
  // tolerância de 1 centavo p/ comparação de float
  const saldoRestante = Math.max(0, Math.round((total - valorPago) * 100) / 100)
  // Sobra de pagamento (linhas somam mais que o total) — ex.: bike vale mais que a
  // compra. Pra cliente identificado, vira crédito automático.
  const sobraLinhas = Math.max(0, Math.round((valorPago - total) * 100) / 100)
  const totalBatido = valorPago >= total - 0.01

  // Crédito (haver) do cliente identificado — pra mostrar e oferecer abater.
  const { data: creditoCli } = useQuery<any>({
    queryKey: ['cliente-credito', cliente?.id],
    queryFn: async () => (await api.get(`/financeiro/creditos/${cliente!.id}`)).data,
    enabled: !!cliente?.id,
  })
  const saldoCredito = Number(creditoCli?.saldo || 0)
  // Já tem linha de crédito? (pra não oferecer de novo)
  const jaUsaCredito = pagamentos.some(p => p.forma === 'credito_cliente')
  // Quando sobra valor: operador decide devolver troco ou deixar como crédito.
  const [sobraDestino, setSobraDestino] = useState<'credito' | 'troco'>('credito')
  const temDinheiro = pagamentos.some(p => p.forma === 'dinheiro')

  // Forma sendo digitada agora
  const [formaAtual, setFormaAtual] = useState<string>('')
  const [valorAtual, setValorAtual] = useState<string>('')
  const [parcelasAtual, setParcelasAtual] = useState<number>(1)
  // FEATURE 03/07: operadora de cartão (FixPay etc.) escolhida ao pagar em débito/crédito.
  const [operadoraAtual, setOperadoraAtual] = useState<number | null>(null)
  // Forma 'link' (FixPay): modalidade escolhida na hora ('debito' | 'credito' | 'pix').
  const [modalidadeLink, setModalidadeLink] = useState<string>('')
  const [vencAtual, setVencAtual] = useState<string>(() => {
    const d = new Date()
    d.setDate(d.getDate() + 30)
    return d.toISOString().slice(0, 10)
  })
  const [bikeAtual, setBikeAtual] = useState<BikeRecebida>({})
  const inputValorRef = useRef<HTMLInputElement>(null)
  const dragIdx = useRef<number | null>(null)
  const [formaHi, setFormaHi] = useState(0)  // forma destacada na grade (teclado ↑↓←→)
  const gridFormasRef = useRef<HTMLDivElement>(null)
  // Ao mostrar a grade de formas (sem forma escolhida), foca-a p/ as setas já funcionarem.
  useEffect(() => {
    if (!totalBatido && !formaAtual) { setFormaHi(0); setTimeout(() => gridFormasRef.current?.focus(), 60) }
  }, [totalBatido, formaAtual])

  const qc = useQueryClient()
  const { data: cfgFormas = [] } = useQuery<any[]>({
    queryKey: ['config-formas'],
    queryFn: async () => (await api.get('/caixa/config-formas')).data,
  })
  // FEATURE 03/07: operadoras de cartão ativas — escolhidas ao pagar em débito/crédito.
  const { data: operadoras = [] } = useQuery<any[]>({
    queryKey: ['operadoras-cartao'],
    queryFn: async () => (await api.get('/caixa/operadoras-cartao')).data,
  })
  const operadorasAtivas = useMemo(() =>
    (operadoras as any[]).filter(o => o.ativo).sort((a, b) => (a.ordem ?? 0) - (b.ordem ?? 0)),
    [operadoras])
  const reordenar = useMutation({
    mutationFn: async (ordemIds: string[]) =>
      (await api.put('/caixa/config-formas/reordenar', { ordem: ordemIds })).data,
    onError: () => {
      toast.error('Não consegui salvar a ordem')
      qc.invalidateQueries({ queryKey: ['config-formas'] })
    },
  })

  // Config é a fonte de verdade (fallback enquanto carrega).
  const cfgList: any[] = (cfgFormas as any[]).length > 0 ? (cfgFormas as any[]) : FORMAS_FALLBACK
  // Formas exibidas: ativas, ordenadas, com ícone/cor/comportamento dos metadados.
  const formas = useMemo(() => cfgList
    .filter(c => c.ativo)
    .slice()
    .sort((a, b) => (a.ordem ?? 0) - (b.ordem ?? 0) || String(a.forma).localeCompare(String(b.forma)))
    .map(c => ({ id: c.forma as string, label: c.label as string, ...metaFor(c.forma) })),
    [cfgFormas])

  function aoSoltar(targetIdx: number) {
    const from = dragIdx.current
    dragIdx.current = null
    if (from == null || from === targetIdx) return
    const ids = formas.map(f => f.id)
    const [movido] = ids.splice(from, 1)
    ids.splice(targetIdx, 0, movido)
    const pos = new Map(ids.map((id, i) => [id, i]))
    qc.setQueryData(['config-formas'], (old: any[] = []) =>
      old.map(c => (pos.has(c.forma) ? { ...c, ordem: pos.get(c.forma) } : c)))
    reordenar.mutate(ids)
  }

  const formaConfig = formas.find(f => f.id === formaAtual)

  // FEATURE 03/07: operadora + taxa por modalidade.
  // Cartão (cartao_debito/credito) usa operadora tipo 'cartao' com modalidade FIXA pela forma.
  // Link (FixPay) usa operadora tipo 'link' e o operador escolhe débito/crédito/pix na hora.
  const ehCartaoDebito = formaAtual === 'cartao_debito'
  const ehCartaoCredito = formaAtual === 'cartao_credito'
  const ehLink = formaAtual === 'link'
  const usaOperadora = ehCartaoDebito || ehCartaoCredito || ehLink
  const tipoOperadoraForma = ehLink ? 'link' : 'cartao'
  const operadorasDaForma = operadorasAtivas.filter(o => (o.tipo || 'cartao') === tipoOperadoraForma)
  const operadoraSel = operadorasDaForma.find(o => o.id === operadoraAtual) || null
  const modalidadeEfetiva = ehCartaoDebito ? 'debito' : ehCartaoCredito ? 'credito' : (ehLink ? modalidadeLink : '')
  const mostrarParcelas = usaOperadora ? modalidadeEfetiva === 'credito' : !!formaConfig?.parcelas
  const taxaCartaoAtual = operadoraSel
    ? (modalidadeEfetiva === 'debito'
        ? Number(operadoraSel.taxa_debito || 0)
        : modalidadeEfetiva === 'pix'
          ? Number(operadoraSel.taxa_pix || 0)
          : modalidadeEfetiva === 'credito'
            ? Number(operadoraSel.taxas_credito?.[String(parcelasAtual)] ?? operadoraSel.taxas_credito?.['1'] ?? 0)
            : 0)
    : 0

  // Quando seleciona forma, sugere o saldo restante e foca input
  function escolherForma(forma: string) {
    setFormaAtual(forma)
    setParcelasAtual(1)
    // Cartão/Link: pré-seleciona a operadora quando só há uma ativa daquele tipo.
    const tipoForma = forma === 'link' ? 'link' : 'cartao'
    const usaOp = forma === 'cartao_debito' || forma === 'cartao_credito' || forma === 'link'
    const opsDaForma = operadorasAtivas.filter(o => (o.tipo || 'cartao') === tipoForma)
    setOperadoraAtual(usaOp && opsDaForma.length === 1 ? opsDaForma[0].id : null)
    setModalidadeLink('')  // link: força escolher débito/crédito/pix
    // Bike: NÃO sugere o saldo restante — o valor aqui é o que a BIKE vale na troca,
    // não o total da venda. Sugerir o total fazia o operador lançar a venda inteira como bike.
    setValorAtual(forma === 'recebimento_bike' ? '' : (saldoRestante > 0 ? saldoRestante.toFixed(2) : ''))
    if (forma === 'dinheiro') setValorRecebido(saldoRestante.toFixed(2))
    if (forma !== 'recebimento_bike') setBikeAtual({})
    setTimeout(() => inputValorRef.current?.focus(), 50)
  }

  function adicionarLinha() {
    const v = parseFloat(valorAtual.replace(',', '.')) || 0
    if (!formaAtual || v <= 0) {
      toast.error('Informe forma e valor')
      return
    }
    // Cliente identificado pode pagar A MAIS (deu R$100 numa compra de R$95, ou bike
    // que vale mais que a compra) → a sobra vira troco OU crédito (escolhe no fechamento).
    const permiteSobra = !!cliente
    if (!permiteSobra && v > saldoRestante + 0.01) {
      toast.error(`Valor excede saldo restante (${formatMoney(saldoRestante)})`)
      return
    }
    if (formaConfig?.bike) {
      if (!bikeAtual.marca?.trim() || !bikeAtual.modelo?.trim()) {
        toast.error('Informe marca e modelo da bike recebida')
        return
      }
      // Trava anti-erro: valor da bike cobrindo a venda inteira é quase sempre engano
      // (o operador colocou o total da venda no lugar do valor da bike). Pede confirmação.
      if (v >= total - 0.01 && total > 0) {
        const ok = window.confirm(
          `Atenção: o valor da BIKE (${formatMoney(v)}) cobre a venda INTEIRA (${formatMoney(total)}).\n\n` +
          `Aqui vai SÓ quanto a bike vale na troca — não o total da venda. ` +
          `Se o cliente ainda paga uma parte em dinheiro/cartão/pix, lance a bike só pelo valor dela e o resto na outra forma.\n\n` +
          `A bike vale mesmo a venda toda? Confirmar?`
        )
        if (!ok) return
      }
    }
    // Cartão/Link: exige operadora (e, no link, a modalidade) quando há operadoras cadastradas.
    if (usaOperadora && operadorasDaForma.length > 0) {
      if (!operadoraSel) { toast.error('Escolha a operadora'); return }
      if (ehLink && !modalidadeLink) { toast.error('Escolha débito, crédito ou pix'); return }
    }
    const linha: LinhaPag = { forma: formaAtual, valor: v, parcelas: mostrarParcelas ? parcelasAtual : 1 }
    // Nome cadastrado já aqui, pra tela mostrar "PIX MAQUINA" em vez de "PIX"
    // enquanto o operador monta o pagamento (o cupom pega do backend de qualquer jeito).
    if (formaConfig?.label) linha.forma_label = formaConfig.label
    if (formaConfig?.vencimento) linha.primeiro_venc = vencAtual
    if (formaConfig?.bike) linha.bike = { ...bikeAtual }
    if (usaOperadora && operadoraSel) {
      linha.operadora_id = operadoraSel.id
      linha.operadora_nome = operadoraSel.nome
      linha.taxa_pct = taxaCartaoAtual
      if (ehLink) linha.modalidade = modalidadeLink
    }
    setPagamentos([...pagamentos, linha])
    setFormaAtual('')
    setValorAtual('')
    setParcelasAtual(1)
    setOperadoraAtual(null)
    setModalidadeLink('')
    setBikeAtual({})
  }

  function removerLinha(idx: number) {
    setPagamentos(pagamentos.filter((_, i) => i !== idx))
  }

  function usarCredito() {
    const usar = Math.min(saldoCredito, saldoRestante)
    if (usar <= 0) return
    setPagamentos([...pagamentos, { forma: 'credito_cliente', valor: Math.round(usar * 100) / 100, parcelas: 1 }])
    toast.success(`Crédito de ${formatMoney(usar)} aplicado`)
  }

  function finalizarAgora() {
    if (!totalBatido) {
      toast.error(`Falta ${formatMoney(saldoRestante)} para fechar o pagamento`)
      return
    }
    let linhas = pagamentos
    let creditoGerado = 0
    if (sobraLinhas > 0) {
      if (!cliente) {
        toast.error('Sobra de pagamento só é permitida com cliente identificado.')
        return
      }
      if (sobraDestino === 'credito' || !temDinheiro) {
        // Sobra vira crédito (haver) do cliente. Linhas já somam total + sobra.
        creditoGerado = sobraLinhas
      } else {
        // Devolver em dinheiro (troco): tira a sobra da linha de dinheiro; o
        // valor_recebido (o que o cliente entregou) faz o backend calcular o troco.
        let restante = sobraLinhas
        linhas = pagamentos.map(p => {
          if (restante > 0 && p.forma === 'dinheiro') {
            const tira = Math.min(restante, p.valor)
            restante = Math.round((restante - tira) * 100) / 100
            return { ...p, valor: Math.round((p.valor - tira) * 100) / 100 }
          }
          return p
        }).filter(p => p.valor > 0)
      }
    }
    if (osVinculadas.length > 1) {
      finalizarMultiplasOS(linhas, creditoGerado)
    } else {
      finalizar.mutate({ linhas, creditoGerado })
    }
  }

  // Troco quando paga em dinheiro acima do necessário (linha em digitação)
  const trocoDinheiro = formaAtual === 'dinheiro'
    ? Math.max(0, parseFloat(valorRecebido || '0') - saldoRestante)
    : 0

  return (
    <div className="space-y-2">
      {/* Saldo restante — destaque */}
      <div className={`rounded-lg p-1.5 border ${totalBatido
        ? 'bg-emerald-900/40 border-emerald-700'
        : 'bg-ink-800 border-ink-700'}`}>
        <div className="flex justify-between items-center">
          <span className="text-[10px] uppercase tracking-wide text-ink-400">
            {totalBatido ? 'Pagamento completo' : 'Falta receber'}
          </span>
          <span className={`text-lg font-bold ${totalBatido ? 'text-emerald-300' : 'text-white'}`}>
            {formatMoney(saldoRestante)}
          </span>
        </div>
        <div className="flex justify-between text-[10px] text-ink-500 mt-0.5">
          <span>Total da venda: {formatMoney(total)}</span>
          {valorPago > 0 && <span>Pago: {formatMoney(valorPago)}</span>}
        </div>
      </div>

      {/* Crédito (haver) do cliente — oferece abater */}
      {cliente && saldoCredito > 0 && !jaUsaCredito && (
        <div className="rounded-xl p-3 border border-cyan-700 bg-cyan-900/30 flex items-center gap-3">
          <HandCoins size={20} className="text-cyan-300 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-cyan-200">{cliente.nome} tem {formatMoney(saldoCredito)} de crédito</div>
            <div className="text-[11px] text-cyan-400">Use pra abater esta compra.</div>
          </div>
          {saldoRestante > 0.01 && (
            <button onClick={usarCredito}
              className="px-3 py-2 rounded-lg bg-cyan-600 hover:bg-cyan-500 text-white text-sm font-bold shrink-0">
              Usar {formatMoney(Math.min(saldoCredito, saldoRestante))}
            </button>
          )}
        </div>
      )}

      {/* Sobra de pagamento → troco ou crédito */}
      {sobraLinhas > 0 && cliente && (
        <div className="rounded-xl p-3 border border-amber-600 bg-amber-900/25">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-amber-200">Sobrou {formatMoney(sobraLinhas)}</span>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setSobraDestino('credito')}
              className={`flex-1 py-2 rounded-lg text-sm font-bold border ${
                sobraDestino === 'credito'
                  ? 'bg-cyan-600 border-cyan-500 text-white'
                  : 'bg-ink-800 border-ink-700 text-ink-300 hover:bg-ink-700'}`}>
              💳 Deixar como crédito
            </button>
            {temDinheiro && (
              <button onClick={() => setSobraDestino('troco')}
                className={`flex-1 py-2 rounded-lg text-sm font-bold border ${
                  sobraDestino === 'troco'
                    ? 'bg-emerald-600 border-emerald-500 text-white'
                    : 'bg-ink-800 border-ink-700 text-ink-300 hover:bg-ink-700'}`}>
                💵 Troco em dinheiro
              </button>
            )}
          </div>
          <div className="text-[11px] text-amber-300/80 mt-1.5">
            {sobraDestino === 'credito' || !temDinheiro
              ? `Vai pro crédito de ${cliente.nome}.`
              : 'Devolver em dinheiro ao cliente.'}
          </div>
        </div>
      )}

      {/* Lista de linhas já adicionadas */}
      {pagamentos.length > 0 && (
        <div className="space-y-1.5">
          {pagamentos.map((p, idx) => {
            const Icon = metaFor(p.forma).icon
            return (
              <div key={idx} className="flex items-center gap-2 bg-ink-800/60 rounded-lg px-3 py-2">
                <Icon size={16} className="text-ink-400 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white truncate">
                    {formaLabel(p.forma, p.forma_label)}
                    {p.parcelas > 1 && <span className="text-ink-400"> · {p.parcelas}x</span>}
                  </div>
                  {p.operadora_nome && (
                    <div className="text-[10px] text-amber-300/90 truncate">
                      {p.operadora_nome}
                      {p.modalidade && <> · {p.modalidade === 'debito' ? 'Débito' : p.modalidade === 'pix' ? 'Pix' : 'Crédito'}</>}
                      {p.taxa_pct != null && p.taxa_pct > 0 && <> · taxa {p.taxa_pct}%</>}
                    </div>
                  )}
                  {p.primeiro_venc && (
                    <div className="text-[10px] text-ink-500">
                      1º venc: {new Date(p.primeiro_venc).toLocaleDateString('pt-BR')}
                    </div>
                  )}
                  {p.bike && (
                    <div className="text-[10px] text-fuchsia-300 truncate">
                      {[p.bike.marca, p.bike.modelo, p.bike.ano].filter(Boolean).join(' ')}
                      {p.bike.numero_serie && <> · #{p.bike.numero_serie}</>}
                    </div>
                  )}
                </div>
                <span className="text-sm font-semibold text-white">{formatMoney(p.valor)}</span>
                <button onClick={() => removerLinha(idx)} className="text-ink-500 hover:text-rose-400 p-1">
                  <X size={14} />
                </button>
              </div>
            )
          })}
        </div>
      )}

      {/* Seleção de nova forma (quando ainda falta saldo) — arraste para reordenar */}
      {!totalBatido && !formaAtual && (
        <>
          <div className="text-xs text-ink-400 text-center pt-1">
            Adicionar forma de pagamento
          </div>
          <div ref={gridFormasRef} tabIndex={0} className="grid grid-cols-3 gap-1.5 outline-none"
            onKeyDown={e => {
              // teclado: ←→ ±1, ↑↓ ±3 (3 colunas), Enter escolhe a forma destacada
              const n = formas.length
              if (!n) return
              if (e.key === 'ArrowRight') { e.preventDefault(); setFormaHi(h => Math.min(h + 1, n - 1)) }
              else if (e.key === 'ArrowLeft') { e.preventDefault(); setFormaHi(h => Math.max(h - 1, 0)) }
              else if (e.key === 'ArrowDown') { e.preventDefault(); setFormaHi(h => Math.min(h + 3, n - 1)) }
              else if (e.key === 'ArrowUp') { e.preventDefault(); setFormaHi(h => Math.max(h - 3, 0)) }
              else if (e.key === 'Enter') { e.preventDefault(); const f = formas[Math.min(formaHi, n - 1)]; if (f) escolherForma(f.id) }
            }}>
            {formas.map((f, idx) => {
              const Icon = f.icon
              return (
                <button key={f.id}
                  onClick={() => escolherForma(f.id)}
                  onMouseEnter={() => setFormaHi(idx)}
                  ref={el => { if (idx === formaHi) el?.scrollIntoView({ block: 'nearest' }) }}
                  disabled={finalizar.isPending}
                  draggable
                  onDragStart={() => { dragIdx.current = idx }}
                  onDragOver={e => e.preventDefault()}
                  onDrop={e => { e.preventDefault(); aoSoltar(idx) }}
                  onDragEnd={() => { dragIdx.current = null }}
                  title="Arraste para reordenar"
                  className={`py-1.5 rounded-md text-white text-[10px] font-medium flex flex-col items-center gap-0
                             ${f.color} disabled:opacity-50 transition-all cursor-grab active:cursor-grabbing select-none
                             ${idx === formaHi ? 'ring-2 ring-white ring-offset-1 ring-offset-ink-900' : ''}`}>
                  <Icon size={14} />
                  <span>{f.label}</span>
                </button>
              )
            })}
          </div>
          <p className="text-[10px] text-ink-500 text-center">↑↓←→ navega · Enter escolhe.</p>
        </>
      )}

      {/* Painel da linha em digitação */}
      {!totalBatido && formaAtual && formaConfig && (
        <div className="bg-ink-800/80 rounded-xl p-2 space-y-1.5 border border-ink-700">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-white flex items-center gap-1.5">
              <formaConfig.icon size={14} /> {formaConfig.label}
            </span>
            <button onClick={() => { setFormaAtual(''); setValorAtual('') }}
              className="text-ink-500 hover:text-ink-300 text-[11px]">trocar</button>
          </div>

          <div>
            <label className={`text-[10px] uppercase tracking-wide ${formaConfig.bike ? 'text-fuchsia-300 font-semibold' : 'text-ink-400'}`}>
              {formaConfig.bike ? 'Valor da BIKE (na troca, não o total da venda)' : 'Valor'}
            </label>
            <input
              ref={inputValorRef}
              type="number" step="0.01" min="0"
              value={valorAtual}
              onChange={e => {
                setValorAtual(e.target.value)
                if (formaAtual === 'dinheiro') setValorRecebido(e.target.value)
              }}
              onFocus={e => e.target.select()}
              onKeyDown={e => { if (e.key === 'Enter') adicionarLinha() }}
              className={`w-full px-3 rounded-lg bg-ink-950 border border-ink-700 text-white
                         font-bold text-right focus:border-emerald-500 focus:outline-none
                         ${formaConfig.bike ? 'py-1.5 text-lg' : 'py-2 text-xl'}`}
              placeholder="0,00"
            />
            {!formaConfig.bike && (
              <div className="flex gap-1 mt-1">
                <button onClick={() => { setValorAtual(saldoRestante.toFixed(2)); if (formaAtual === 'dinheiro') setValorRecebido(saldoRestante.toFixed(2)) }}
                  className="flex-1 py-1 rounded bg-emerald-800 hover:bg-emerald-700 text-emerald-300 text-[11px]">
                  Tudo ({formatMoney(saldoRestante)})
                </button>
                <button onClick={() => { setValorAtual((saldoRestante / 2).toFixed(2)) }}
                  className="flex-1 py-1 rounded bg-ink-700 hover:bg-ink-600 text-ink-200 text-[11px]">
                  ½
                </button>
              </div>
            )}
            {formaConfig.bike && (
              <p className="mt-1 text-[10px] text-fuchsia-300/80">
                Quanto a bike vale na troca. O restante o cliente paga em outra forma.
              </p>
            )}
          </div>

          {/* FEATURE 03/07: operadora (cartão: FixPay/máquinas) ou Link (FixPay) + modalidade. */}
          {usaOperadora && operadorasDaForma.length > 0 && (
            <div className="space-y-1.5">
              {/* Seletor de operadora — se só há uma, mostra como rótulo (já pré-selecionada). */}
              {operadorasDaForma.length > 1 ? (
                <div>
                  <label className="text-[10px] text-ink-400 uppercase tracking-wide">{ehLink ? 'Provedor' : 'Operadora / máquina'}</label>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {operadorasDaForma.map(o => {
                      const cor = corOperadora(o.id)
                      const sel = operadoraAtual === o.id
                      return (
                        <button key={o.id} onClick={() => setOperadoraAtual(o.id)}
                          style={sel ? { backgroundColor: cor, borderColor: cor } : { borderColor: cor }}
                          className={`px-2.5 py-1 rounded text-[11px] font-medium border flex items-center gap-1.5 ${
                            sel ? 'text-white' : 'bg-ink-700 text-ink-100 hover:bg-ink-600'
                          }`}>
                          {!sel && <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: cor }} />}
                          {o.nome}
                        </button>
                      )
                    })}
                  </div>
                </div>
              ) : (
                <div className="text-[11px] text-indigo-300 font-medium flex items-center gap-1">
                  <Link2 size={12} /> {operadorasDaForma[0]?.nome}
                </div>
              )}

              {/* Link: escolher débito / crédito / pix. */}
              {ehLink && (
                <div>
                  <label className="text-[10px] text-ink-400 uppercase tracking-wide">Modalidade</label>
                  <div className="flex gap-1 mt-1">
                    {[['debito', 'Débito'], ['credito', 'Crédito'], ['pix', 'Pix']].map(([m, lbl]) => (
                      <button key={m} onClick={() => { setModalidadeLink(m); setParcelasAtual(1) }}
                        className={`flex-1 py-1 rounded text-[11px] font-medium ${
                          modalidadeLink === m ? 'bg-brand-600 text-white' : 'bg-ink-700 text-ink-200 hover:bg-ink-600'
                        }`}>{lbl}</button>
                    ))}
                  </div>
                </div>
              )}

              {operadoraSel && modalidadeEfetiva && (
                <div className="text-[10px] text-ink-400">
                  Taxa {modalidadeEfetiva === 'debito' ? 'débito' : modalidadeEfetiva === 'pix' ? 'pix' : `crédito ${parcelasAtual}x`}:{' '}
                  <span className="text-amber-300 font-semibold">{taxaCartaoAtual}%</span>
                  {parseFloat(valorAtual || '0') > 0 && taxaCartaoAtual > 0 && (
                    <> · custo {formatMoney(parseFloat(valorAtual) * taxaCartaoAtual / 100)}</>
                  )}
                </div>
              )}
            </div>
          )}

          {mostrarParcelas && (
            <div>
              <label className="text-[10px] text-ink-400 uppercase tracking-wide">Parcelas</label>
              <div className="flex flex-wrap gap-1 mt-1">
                {Array.from({ length: 18 }, (_, i) => i + 1).map(n => (
                  <button key={n} onClick={() => setParcelasAtual(n)}
                    className={`min-w-[32px] py-1 rounded text-[11px] font-medium ${
                      parcelasAtual === n
                        ? 'bg-brand-600 text-white'
                        : 'bg-ink-700 text-ink-200 hover:bg-ink-600'
                    }`}>{n}x</button>
                ))}
              </div>
              {parcelasAtual > 1 && parseFloat(valorAtual || '0') > 0 && (
                <div className="text-[10px] text-ink-400 mt-1">
                  {parcelasAtual}x de {formatMoney(parseFloat(valorAtual) / parcelasAtual)}
                </div>
              )}
            </div>
          )}

          {formaConfig.vencimento && (
            <div>
              <label className="text-[10px] text-ink-400 uppercase tracking-wide">
                {parcelasAtual > 1 ? '1º Vencimento' : 'Vencimento'}
              </label>
              <input
                type="date" value={vencAtual}
                onChange={e => setVencAtual(e.target.value)}
                className="w-full py-1 px-2 rounded bg-ink-950 border border-ink-700 text-white text-xs
                           focus:border-amber-500 focus:outline-none"
              />
              {parcelasAtual > 1 && (
                <div className="text-[10px] text-ink-500 mt-1">
                  Parcelas a cada 30 dias a partir desta data
                </div>
              )}
            </div>
          )}

          {formaAtual === 'dinheiro' && parseFloat(valorRecebido || '0') > 0 && trocoDinheiro > 0 && (
            <div className="bg-emerald-900/30 border border-emerald-700 rounded-lg py-2 px-3 flex justify-between items-center">
              <span className="text-emerald-300 text-xs">Troco</span>
              <span className="text-emerald-300 text-lg font-bold">{formatMoney(trocoDinheiro)}</span>
            </div>
          )}

          {formaConfig.bike && (
            <div className="space-y-1 pt-1 border-t border-ink-700">
              <div className="text-[9px] text-fuchsia-300 uppercase tracking-wide flex items-center gap-1">
                <Bike size={11} /> Dados da bike recebida <span className="text-ink-500 normal-case">· * obrigatórios</span>
              </div>
              <div className="grid grid-cols-2 gap-1" onKeyDown={enterAdvance}>
                <input
                  type="text" value={bikeAtual.marca || ''}
                  onChange={e => setBikeAtual({ ...bikeAtual, marca: e.target.value.toUpperCase() })}
                  placeholder="Marca *"
                  className="py-1 px-2 rounded bg-ink-950 border border-ink-700 text-white text-[10px]
                             focus:border-fuchsia-500 focus:outline-none"
                />
                <input
                  type="text" value={bikeAtual.modelo || ''}
                  onChange={e => setBikeAtual({ ...bikeAtual, modelo: e.target.value.toUpperCase() })}
                  placeholder="Modelo *"
                  className="py-1 px-2 rounded bg-ink-950 border border-ink-700 text-white text-[10px]
                             focus:border-fuchsia-500 focus:outline-none"
                />
                <input
                  type="text" value={bikeAtual.ano || ''}
                  onChange={e => setBikeAtual({ ...bikeAtual, ano: e.target.value })}
                  placeholder="Ano"
                  className="py-1 px-2 rounded bg-ink-950 border border-ink-700 text-white text-[10px]
                             focus:border-fuchsia-500 focus:outline-none"
                />
                <input
                  type="text" value={bikeAtual.cor || ''}
                  onChange={e => setBikeAtual({ ...bikeAtual, cor: e.target.value.toUpperCase() })}
                  placeholder="Cor"
                  className="py-1 px-2 rounded bg-ink-950 border border-ink-700 text-white text-[10px]
                             focus:border-fuchsia-500 focus:outline-none"
                />
                <input
                  type="text" value={bikeAtual.tamanho || ''}
                  onChange={e => setBikeAtual({ ...bikeAtual, tamanho: e.target.value.toUpperCase() })}
                  placeholder="Tamanho (M, 17...)"
                  className="py-1 px-2 rounded bg-ink-950 border border-ink-700 text-white text-[10px]
                             focus:border-fuchsia-500 focus:outline-none"
                />
                <input
                  type="text" value={bikeAtual.numero_serie || ''}
                  onChange={e => setBikeAtual({ ...bikeAtual, numero_serie: e.target.value.toUpperCase() })}
                  placeholder="Nº série"
                  className="py-1 px-2 rounded bg-ink-950 border border-ink-700 text-white text-[10px]
                             focus:border-fuchsia-500 focus:outline-none"
                />
                <input
                  type="text" value={bikeAtual.observacoes || ''}
                  onChange={e => setBikeAtual({ ...bikeAtual, observacoes: e.target.value.toUpperCase() })}
                  placeholder="Observações (estado, acessórios)"
                  className="col-span-2 py-1 px-2 rounded bg-ink-950 border border-ink-700 text-white text-[10px]
                             focus:border-fuchsia-500 focus:outline-none"
                />
              </div>
            </div>
          )}

          <button onClick={adicionarLinha}
            disabled={!parseFloat(valorAtual || '0')}
            className="w-full py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-30
                       text-white font-medium text-xs flex items-center justify-center gap-1.5">
            <Plus size={14} /> Adicionar pagamento
          </button>
        </div>
      )}

      {/* Botão Finalizar — só aparece quando saldo = 0 */}
      {totalBatido && (
        <button onClick={finalizarAgora}
          disabled={finalizar.isPending || finalizandoMultiplas}
          className="w-full py-4 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-lg font-bold
                     flex items-center justify-center gap-2 transition-colors disabled:opacity-50 ring-2 ring-emerald-400/40">
          {(finalizar.isPending || finalizandoMultiplas)
            ? <><Loader2 size={20} className="animate-spin" /> Finalizando...</>
            : <><Check size={22} /> FINALIZAR VENDA</>}
        </button>
      )}

      <button onClick={onBack}
        className="w-full py-2.5 rounded-xl bg-ink-800 hover:bg-ink-700 text-ink-400 text-sm transition-colors flex items-center justify-center gap-2">
        <ArrowLeft size={14} /> Voltar
      </button>
    </div>
  )
}

  // Campos que o cliente precisa ter pra fechar venda (pedido do cliente 19/10).
function faltaNoCliente(c: any | null): string[] {
  if (!c) return ['cliente']
  // Cliente genérico de balcão (21/10): não representa ninguém, não tem o que validar.
  if (c.cliente_generico) return []
  const falta: string[] = []
  if (String(c.nome || '').trim().split(/\s+/).length < 2) falta.push('nome e sobrenome')
  const doc = String(c.documento || '').replace(/\D/g, '')
  if (!doc) falta.push('CPF/CNPJ')
  else if (![11, 14].includes(doc.length)) falta.push('CPF/CNPJ válido')
  if (!String(c.telefone || '').trim()) falta.push('telefone')
  if (!String(c.endereco || '').trim()) falta.push('endereço')
  if (!String(c.numero || '').trim()) falta.push('número')
  if (!String(c.bairro || '').trim()) falta.push('bairro')
  if (!String(c.cidade || '').trim()) falta.push('cidade')
  if (!String(c.uf || '').trim()) falta.push('UF')
  return falta
}


// ============ SELECT DE CLIENTE (busca por nome/doc) ============
function ClienteSelector({ cliente, setCliente, busca, setBusca, compact, incluirNoCupom, setIncluirNoCupom }: {
  cliente: any | null
  setCliente: (c: any | null) => void
  busca: string
  setBusca: (s: string) => void
  compact?: boolean
  incluirNoCupom: boolean
  setIncluirNoCupom: (v: boolean) => void
}) {
  const [open, setOpen] = useState(false)
  const [hi, setHi] = useState(0)  // item destacado no dropdown (teclado ↑/↓)
  const [showNovoCliente, setShowNovoCliente] = useState(false)

  const { data: pessoas = [], isFetching } = useQuery<any[]>({
    queryKey: ['pdv-clientes', busca],
    queryFn: async () => (await api.get('/cadastros/pessoas', {
      params: { q: busca, tipo: 'cliente', limit: 15 },
    })).data,
    // 22/10: 1 caractere basta quando é dígito — busca por código ("1" → 000001).
    // Nome continua exigindo 2, pra não varrer 29 mil cadastros a cada letra.
    enabled: open && (busca.trim().length >= 2 || /^\d+$/.test(busca.trim())),
  })

  // Crédito (haver) do cliente — gerado por devoluções. Mostra pra operadora saber.
  const { data: credito } = useQuery<any>({
    queryKey: ['cliente-credito', cliente?.id],
    queryFn: async () => (await api.get(`/financeiro/creditos/${cliente.id}`)).data,
    enabled: !!cliente?.id,
  })
  const saldoCredito = Number(credito?.saldo || 0)

  // Cliente selecionado — chip com botão remover + checkbox de impressão
  if (cliente) {
    return (
      <div className={compact ? 'space-y-1' : ''}>
        {!compact && (
          <label className="text-[9px] text-ink-500 uppercase tracking-wide flex items-center gap-1">
            <User size={10} /> Cliente
          </label>
        )}
        <div className={`flex items-center gap-2 px-2 py-1 rounded
          ${compact ? 'bg-ink-100' : 'bg-ink-900 border border-ink-700 mt-0.5'}`}>
          <User size={13} className={compact ? 'text-ink-500' : 'text-brand-400'} />
          <div className="flex-1 min-w-0">
            <div className={`text-sm font-medium truncate flex items-center gap-1.5 ${compact ? 'text-ink-900' : 'text-white'}`}>
              <span className="truncate">{cliente.nome}</span>
              {/* Genérico de balcão (21/10): sinaliza que a venda sai sem identificação,
                  pra ninguém achar que a trava de cliente obrigatório falhou. */}
              {cliente.cliente_generico && (
                <span className={`shrink-0 text-[9px] px-1.5 py-0.5 rounded uppercase tracking-wide
                  ${compact ? 'bg-ink-200 text-ink-600' : 'bg-ink-700 text-ink-300'}`}>
                  genérico
                </span>
              )}
            </div>
            {cliente.documento && (
              <div className={`text-[10px] truncate ${compact ? 'text-ink-500' : 'text-ink-400'}`}>
                {cliente.documento}
              </div>
            )}
          </div>
          <button onClick={() => { setCliente(null); setBusca('') }}
            className={`p-1 rounded hover:bg-rose-500/20 ${compact ? 'text-ink-400 hover:text-rose-500' : 'text-ink-500 hover:text-rose-400'}`}
            title="Trocar cliente">
            <X size={13} />
          </button>
        </div>
        {saldoCredito > 0 && (
          <div className="mt-1 flex items-center gap-1.5 px-2.5 py-1.5 rounded bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-medium">
            <Wallet size={13} />
            Crédito disponível: {formatMoney(saldoCredito)}
            <span className="text-emerald-600 font-normal">— use no Financeiro pra abater o título</span>
          </div>
        )}
        <label className={`flex items-center gap-1.5 cursor-pointer select-none mt-1 text-[10px]
          ${compact ? 'text-ink-600' : 'text-ink-400'}`}
          title="Quando desmarcado, o cupom/recibo sai sem o nome e CPF/CNPJ do cliente">
          <input type="checkbox" checked={incluirNoCupom}
            onChange={e => setIncluirNoCupom(e.target.checked)}
            className="accent-brand-600" />
          Imprimir dados do cliente no cupom
        </label>
      </div>
    )
  }

  return (
    <div>
      {!compact && (
        <label className="text-[9px] text-ink-500 uppercase tracking-wide flex items-center gap-1">
          <User size={10} /> Cliente (opcional — sai no cupom)
        </label>
      )}
      <div className="relative">
        <Search size={13} className={`absolute left-2 top-1/2 -translate-y-1/2 ${compact ? 'text-ink-400' : 'text-ink-500'}`} />
        <input
          value={busca}
          onChange={e => { setBusca(e.target.value); setOpen(true); setHi(0) }}
          onFocus={() => { setOpen(true); setHi(0) }}
          onBlur={() => setTimeout(() => setOpen(false), 200)}
          onKeyDown={e => {
            // ↑/↓ navega a lista (último item = "cadastrar novo"); Enter confirma o destacado.
            const max = pessoas.length  // índice pessoas.length = botão "cadastrar novo"
            if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setHi(h => Math.min(h + 1, max)) }
            else if (e.key === 'ArrowUp') { e.preventDefault(); setHi(h => Math.max(h - 1, 0)) }
            else if (e.key === 'Enter') {
              e.preventDefault()
              if (hi < pessoas.length) { const p = pessoas[hi]; if (p) { setCliente(p); setBusca(''); setOpen(false) } }
              else { setShowNovoCliente(true); setOpen(false) }
            } else if (e.key === 'Escape') { setOpen(false) }
          }}
          placeholder={compact ? 'Cliente (nome ou CPF/CNPJ)' : 'Buscar nome, CPF ou CNPJ...'}
          className={`w-full mt-0.5 pl-7 pr-2 py-1 text-xs rounded
            ${compact
              ? 'bg-ink-50 border border-ink-200 text-ink-900 placeholder:text-ink-400 focus:bg-white focus:border-brand-400'
              : 'bg-ink-900 border border-ink-700 text-white placeholder:text-ink-600 focus:border-brand-500'}
            focus:outline-none`}
        />
        {open && (busca.length >= 2 || pessoas.length === 0) && (
          <div className={`absolute z-30 left-0 right-0 mt-1 max-h-60 overflow-auto rounded shadow-xl
            ${compact ? 'bg-white border border-ink-200' : 'bg-ink-900 border border-ink-700'}`}>
            {busca.length >= 2 && isFetching && (
              <div className="px-3 py-2 text-xs text-ink-500 flex items-center gap-2">
                <Loader2 size={12} className="animate-spin" /> Buscando...
              </div>
            )}
            {busca.length >= 2 && !isFetching && pessoas.length === 0 && (
              <div className="px-3 py-2 text-xs text-ink-500">Nenhum cliente encontrado</div>
            )}
            {pessoas.map((p: any, idx: number) => (
              <button key={p.id}
                onMouseDown={e => e.preventDefault()}
                ref={el => { if (idx === hi) el?.scrollIntoView({ block: 'nearest' }) }}
                onMouseEnter={() => setHi(idx)}
                onClick={() => {
                  setCliente(p)
                  setBusca('')
                  setOpen(false)
                }}
                className={`w-full text-left px-3 py-2 text-xs border-b last:border-0
                  ${compact
                    ? `border-ink-100 text-ink-900 ${idx === hi ? 'bg-brand-100' : 'hover:bg-brand-50'}`
                    : `border-ink-800 text-white ${idx === hi ? 'bg-ink-700' : 'hover:bg-ink-800'}`}`}>
                <div className="font-medium truncate">{p.nome}</div>
                {p.documento && (
                  <div className={`text-[10px] ${compact ? 'text-ink-500' : 'text-ink-500'}`}>{p.documento}</div>
                )}
              </button>
            ))}
            {/* Sempre exibe botão de cadastrar novo cliente no rodapé do dropdown */}
            <button
              onMouseDown={e => e.preventDefault()}
              onMouseEnter={() => setHi(pessoas.length)}
              onClick={() => { setShowNovoCliente(true); setOpen(false) }}
              className={`w-full flex items-center gap-2 px-3 py-2 text-xs font-medium border-t
                ${compact
                  ? `border-ink-200 text-brand-700 ${hi === pessoas.length ? 'bg-brand-100' : 'bg-brand-50 hover:bg-brand-100'}`
                  : `border-ink-800 text-brand-300 ${hi === pessoas.length ? 'bg-brand-900/50' : 'bg-brand-900/30 hover:bg-brand-900/50'}`}`}>
              <UserPlus size={13} /> Cadastrar novo cliente{busca.trim() ? ` "${busca.trim()}"` : ''}
            </button>
          </div>
        )}
      </div>
      {showNovoCliente && (
        <ModalNovoCliente
          nomeInicial={busca.trim()}
          onClose={() => setShowNovoCliente(false)}
          onCriado={p => {
            setCliente(p)
            setBusca('')
            setShowNovoCliente(false)
          }}
        />
      )}
    </div>
  )
}

// ============ GATE DE CLIENTE (19/10 — pedido do cliente) ============
// Bloqueia a venda até o cliente estar identificado E com o cadastro completo
// (nome+sobrenome, CPF/CNPJ, endereço e telefone). Mesmo molde do gate de vendedor.
function ClienteGate({ cliente, setCliente, onClose, onOk }: {
  cliente: any | null
  setCliente: (c: any | null) => void
  onClose: () => void
  onOk: () => void
}) {
  const [busca, setBusca] = useState('')
  const [editar, setEditar] = useState(false)
  const falta = faltaNoCliente(cliente)

  const { data: pessoas = [], isFetching } = useQuery<any[]>({
    queryKey: ['pdv-gate-clientes', busca],
    queryFn: async () => (await api.get('/cadastros/pessoas', {
      params: { q: busca, tipo: 'cliente', limit: 15 },
    })).data,
    enabled: !cliente && (busca.trim().length >= 2 || /^\d+$/.test(busca.trim())),
  })

  // Cliente ficou completo (escolhido ou recém-cadastrado) → libera e segue.
  useEffect(() => {
    if (cliente && falta.length === 0) { onClose(); onOk() }
  }, [cliente, falta.length])

  return (
    <>
      <div className="fixed inset-0 bg-ink-950/85 z-[70] flex items-center justify-center p-4"
        role="dialog" aria-modal="true" aria-label="Identificar cliente" onClick={onClose}>
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-5" onClick={e => e.stopPropagation()}>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center">
              <UserPlus size={18} className="text-amber-600" />
            </div>
            <div>
              <div className="font-semibold text-ink-900">Identifique o cliente</div>
              <div className="text-xs text-ink-500">
                Obrigatório pra finalizar: nome e sobrenome, CPF/CNPJ, endereço e telefone.
              </div>
            </div>
          </div>

          {!cliente ? (
            <>
              <input autoFocus value={busca} placeholder="Nome ou CPF/CNPJ do cliente..."
                autoComplete="off" onChange={e => setBusca(e.target.value)}
                className="w-full mt-3 px-3 py-2 border border-ink-300 rounded-lg text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 text-ink-900" />
              <div className="mt-2 max-h-56 overflow-y-auto border border-ink-100 rounded-lg divide-y divide-ink-100">
                {busca.trim().length < 2 && !/^\d+$/.test(busca.trim()) ? (
                  <div className="p-4 text-center text-sm text-ink-400">Digite ao menos 2 caracteres</div>
                ) : isFetching ? (
                  <div className="p-4 text-center text-sm text-ink-400">Buscando...</div>
                ) : pessoas.length === 0 ? (
                  <div className="p-4 text-center text-sm text-ink-400">Nenhum cliente encontrado</div>
                ) : pessoas.filter((p: any) => !String(p.nome || '').toLowerCase().includes('consumidor final')).map((p: any) => {
                  const pend = faltaNoCliente(p)
                  return (
                    <button key={p.id} type="button" onClick={() => setCliente(p)}
                      className="w-full px-3 py-2 text-left text-sm text-ink-800 hover:bg-brand-50">
                      <div className="font-medium truncate">{p.nome}</div>
                      <div className="text-[10px] text-ink-500">
                        {p.documento || 'sem CPF/CNPJ'}
                        {pend.length > 0 && <span className="text-amber-600"> · falta {pend.join(', ')}</span>}
                      </div>
                    </button>
                  )
                })}
              </div>
              <button type="button" onClick={() => setEditar(true)}
                className="w-full mt-2 flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-brand-50 text-brand-700 hover:bg-brand-100">
                <UserPlus size={14} /> Cadastrar novo cliente{busca.trim() ? ` "${busca.trim()}"` : ''}
              </button>
            </>
          ) : (
            <>
              <div className="mt-3 p-3 rounded-lg bg-ink-50 border border-ink-200">
                <div className="text-sm font-medium text-ink-900">{cliente.nome}</div>
                <div className="text-[11px] text-ink-500">{cliente.documento || 'sem CPF/CNPJ'}</div>
              </div>
              <div className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                Cadastro incompleto — falta: <strong>{falta.join(', ')}</strong>
              </div>
              <div className="mt-3 flex justify-between">
                <button type="button" onClick={() => setCliente(null)}
                  className="px-3 py-1.5 rounded-lg text-sm text-ink-600 hover:bg-ink-100">← Trocar cliente</button>
                <button type="button" onClick={() => setEditar(true)}
                  className="px-4 py-1.5 rounded-lg text-sm font-medium bg-amber-600 hover:bg-amber-700 text-white">
                  Completar cadastro
                </button>
              </div>
            </>
          )}

          <div className="mt-3 flex justify-end">
            <button type="button" onClick={onClose}
              className="px-3 py-1.5 rounded-lg text-sm text-ink-600 hover:bg-ink-100">Cancelar (Esc)</button>
          </div>
        </div>
      </div>

      {editar && (
        <ModalNovoCliente
          nomeInicial={busca.trim()}
          pessoa={cliente}
          onClose={() => setEditar(false)}
          onCriado={p => { setCliente(p); setEditar(false) }}
        />
      )}
    </>
  )
}



// ============ MODAL — ADICIONAR CRÉDITO DO CLIENTE ============
// Cliente paga (dinheiro/cartão/pix) e o valor vira crédito (haver) pra usar depois.
// Dinheiro entra no caixa (suprimento); cartão/pix vai pro financeiro/banco.
const FORMAS_CARGA = [
  { id: 'dinheiro', label: 'Dinheiro' },
  { id: 'pix', label: 'PIX' },
  { id: 'cartao_debito', label: 'Cartão Débito' },
  { id: 'cartao_credito', label: 'Cartão Crédito' },
]
function ModalAddCredito({ clienteAtual, onClose, onFeito }: {
  clienteAtual: { id: number; nome: string } | null
  onClose: () => void; onFeito: () => void
}) {
  const [cliente, setCliente] = useState<{ id: number; nome: string } | null>(clienteAtual)
  const [busca, setBusca] = useState(clienteAtual?.nome || '')
  const [abrir, setAbrir] = useState(false)
  const [valor, setValor] = useState('')
  const [forma, setForma] = useState('dinheiro')
  const [descricao, setDescricao] = useState('')
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const onClick = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setAbrir(false) }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [])
  const { data: clientes = [] } = useQuery<any[]>({
    queryKey: ['addcredito-busca', busca],
    queryFn: async () => (await api.get('/cadastros/pessoas', {
      params: { tipo: 'cliente', q: busca.trim() || undefined, limit: 20 },
    })).data,
    enabled: abrir,
  })
  const carregar = useMutation({
    mutationFn: () => api.post('/pdv/carregar-credito', {
      pessoa_id: cliente!.id, valor: Number(valor), forma, descricao: descricao || undefined,
    }),
    onSuccess: (r: any) => {
      toast.success(`${formatMoney(r?.data?.carregado)} no crédito de ${cliente?.nome} (saldo ${formatMoney(r?.data?.saldo)})`, { icon: '💳', duration: 5000 })
      onFeito()
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao carregar crédito'),
  })
  const podeSalvar = cliente && Number(valor) > 0
  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md p-5" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-ink-900 flex items-center gap-2"><HandCoins size={18} className="text-cyan-600" /> Adicionar crédito</h2>
          <button onClick={onClose} className="text-ink-400 hover:text-ink-600"><X size={20} /></button>
        </div>
        <div className="space-y-3">
          <div className="relative" ref={ref}>
            <label className="text-xs font-semibold text-ink-500 uppercase">Cliente</label>
            <div className="relative mt-1">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
              <input className="w-full rounded-lg border border-ink-200 pl-9 pr-3 py-2 text-sm"
                placeholder="Digite o nome do cliente..." value={busca} autoComplete="off"
                onChange={e => { setBusca(e.target.value); setAbrir(true); if (cliente) setCliente(null) }}
                onFocus={() => setAbrir(true)} />
            </div>
            {abrir && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg z-20 max-h-56 overflow-y-auto">
                {clientes.length === 0 ? (
                  <div className="p-3 text-center text-sm text-ink-400">Nenhum cliente encontrado</div>
                ) : clientes.map((c: any) => (
                  <button key={c.id} type="button"
                    onClick={() => { setCliente({ id: c.id, nome: c.nome }); setBusca(c.nome); setAbrir(false) }}
                    className="w-full px-3 py-2 text-left border-b last:border-0 hover:bg-ink-50">
                    <p className="text-sm font-medium truncate">{c.nome}</p>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div>
            <label className="text-xs font-semibold text-ink-500 uppercase">Valor</label>
            <input type="number" step="0.01" min="0" value={valor} onChange={e => setValor(e.target.value)}
              placeholder="0,00" className="mt-1 w-full rounded-lg border border-ink-200 px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="text-xs font-semibold text-ink-500 uppercase">Forma de pagamento</label>
            <div className="grid grid-cols-2 gap-1.5 mt-1">
              {FORMAS_CARGA.map(f => (
                <button key={f.id} type="button" onClick={() => setForma(f.id)}
                  className={`py-2 rounded-lg text-sm font-medium border ${
                    forma === f.id ? 'bg-cyan-600 border-cyan-500 text-white' : 'bg-white border-ink-200 text-ink-600 hover:bg-ink-50'}`}>
                  {f.label}
                </button>
              ))}
            </div>
            <p className="text-[11px] text-ink-400 mt-1">
              {forma === 'dinheiro' ? 'Entra no caixa (suprimento).' : 'Entra no financeiro/banco da forma.'}
            </p>
          </div>
          <div>
            <label className="text-xs font-semibold text-ink-500 uppercase">Observação</label>
            <input value={descricao} onChange={e => setDescricao(e.target.value.toUpperCase())}
              placeholder="Ex.: pré-pago, deixou troco" className="mt-1 w-full rounded-lg border border-ink-200 px-3 py-2 text-sm" />
          </div>
          <button onClick={() => carregar.mutate()} disabled={!podeSalvar || carregar.isPending}
            className="w-full py-2.5 rounded-lg bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-bold disabled:opacity-50 flex items-center justify-center gap-2">
            {carregar.isPending ? <Loader2 size={16} className="animate-spin" /> : <><HandCoins size={16} /> Carregar crédito</>}
          </button>
        </div>
      </div>
    </div>
  )
}

// ============ MODAL — ESCOLHER VENDEDOR (no início da venda) ============
function ModalEscolherVendedor({ vendedores, onEscolher, onPular }: {
  vendedores: any[]; onEscolher: (v: any) => void; onPular: () => void
}) {
  const [busca, setBusca] = useState('')
  const filtrados = vendedores.filter((v: any) =>
    !busca.trim() || String(v.nome).toLowerCase().includes(busca.trim().toLowerCase()))
  return (
    <div className="fixed inset-0 z-[70] bg-black/60 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-md max-h-[85vh] flex flex-col">
        <div className="px-5 py-4 border-b border-ink-200">
          <h2 className="text-lg font-bold text-ink-900 flex items-center gap-2"><UserPlus size={18} className="text-brand-600" /> Quem está vendendo?</h2>
          <p className="text-xs text-ink-500 mt-0.5">Selecione o vendedor desta venda.</p>
        </div>
        <div className="p-4 overflow-y-auto">
          {vendedores.length > 6 && (
            <input autoFocus value={busca} onChange={e => setBusca(e.target.value)}
              placeholder="Buscar vendedor..."
              className="w-full mb-3 px-3 py-2 text-sm border border-ink-200 rounded-lg focus:border-brand-400 focus:outline-none" />
          )}
          <div className="grid grid-cols-2 gap-2">
            {filtrados.map((v: any) => (
              <button key={v.id} onClick={() => onEscolher(v)}
                className="flex flex-col items-center justify-center gap-1 p-3 rounded-xl border border-ink-200 hover:border-brand-400 hover:bg-brand-50 transition-colors text-center">
                <span className="w-10 h-10 rounded-full bg-brand-100 text-brand-700 font-bold flex items-center justify-center">
                  {String(v.nome || '?').charAt(0).toUpperCase()}
                </span>
                <span className="text-sm font-medium text-ink-900 leading-tight">{v.nome}</span>
                {v.codigo && <span className="text-[10px] text-ink-400 font-mono">{v.codigo}</span>}
              </button>
            ))}
          </div>
        </div>
        <div className="px-5 py-3 border-t border-ink-200 flex justify-end">
          <button onClick={onPular} className="px-4 py-2 rounded-lg text-sm font-medium text-ink-500 hover:bg-ink-100">
            Pular (sem vendedor)
          </button>
        </div>
      </div>
    </div>
  )
}

// ============ MODAL — SERVIÇOS CONCLUÍDOS (cobrar OS no PDV) ============
function TutorialPDV({ onClose }: { onClose: () => void }) {
  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])
  const Tecla = ({ children }: { children: any }) => (
    <kbd className="px-2 py-0.5 rounded border border-ink-300 bg-ink-50 text-ink-700 text-xs font-mono shadow-sm">{children}</kbd>
  )
  const Linha = ({ teclas, txt }: { teclas: any; txt: string }) => (
    <div className="flex items-start gap-3 py-2 border-b border-ink-100 last:border-0">
      <div className="flex items-center gap-1 shrink-0 w-36 flex-wrap">{teclas}</div>
      <div className="text-sm text-ink-700">{txt}</div>
    </div>
  )
  return (
    <div className="fixed inset-0 z-[70] bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[88vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-ink-200">
          <h2 className="text-lg font-bold text-ink-900 flex items-center gap-2"><Keyboard size={18} className="text-brand-600" /> Tutorial — atalhos do PDV</h2>
          <button onClick={onClose} className="text-ink-400 hover:text-ink-600"><X size={20} /></button>
        </div>
        <div className="p-5 overflow-y-auto">
          <p className="text-xs font-semibold text-ink-500 uppercase mb-1">Buscar e adicionar produto</p>
          <Linha teclas={<><Tecla>↑</Tecla><Tecla>↓</Tecla></>} txt="Navega pela lista de produtos encontrados" />
          <Linha teclas={<Tecla>Enter</Tecla>} txt="Adiciona o produto destacado ao carrinho (ou o código bipado)" />
          <Linha teclas={<Tecla>Esc</Tecla>} txt="Limpa a busca" />
          <Linha teclas={<><Tecla>N</Tecla><span className="text-ink-400">*</span><Tecla>cód</Tecla></>} txt="Adiciona N unidades de uma vez (ex.: 3*7891234567890)" />
          <Linha teclas={<span className="text-xs text-ink-500">bipar</span>} txt="Leitor de código de barras adiciona direto" />

          <p className="text-xs font-semibold text-ink-500 uppercase mt-4 mb-1">Navegar sem mouse</p>
          <Linha teclas={<><Tecla>↑</Tecla><Tecla>↓</Tecla><Tecla>←</Tecla><Tecla>→</Tecla></>} txt="Com a busca vazia, navega pelos filtros e pela vitrine de produtos" />
          <Linha teclas={<Tecla>Enter</Tecla>} txt="Escolhe o filtro / adiciona o produto destacado" />

          <p className="text-xs font-semibold text-ink-500 uppercase mt-4 mb-1">Atalhos gerais</p>
          <Linha teclas={<Tecla>F2</Tecla>} txt="Ir para o pagamento" />
          <Linha teclas={<Tecla>F3</Tecla>} txt="Buscar OS / cobrar serviço (↑↓ navega, Enter passa o serviço, Esc fecha)" />
          <Linha teclas={<Tecla>F4</Tecla>} txt="Voltar o cursor para a busca de produto" />
          <Linha teclas={<Tecla>F6</Tecla>} txt="Ir para o campo Cliente" />
          <Linha teclas={<Tecla>F7</Tecla>} txt="Ir para o campo Vendedor" />
          <Linha teclas={<Tecla>F8</Tecla>} txt="Ir para a Observação" />
          <Linha teclas={<Tecla>F9</Tecla>} txt="Ir para o Desconto" />
          <Linha teclas={<Tecla>Esc</Tecla>} txt="Fecha pagamento / modal aberto" />
        </div>
        <div className="px-5 py-3 border-t border-ink-200 flex justify-end">
          <button onClick={onClose} className="btn-primary">Entendi</button>
        </div>
      </div>
    </div>
  )
}

function ModalServicosConcluidos({ onClose, onEscolher, onEscolherMultiplas }: {
  onClose: () => void; onEscolher: (os: any) => void; onEscolherMultiplas: (osArray: any[]) => void;
}) {
  const [q, setQ] = useState('')
  const [idx, setIdx] = useState(0)
  const [acaoBusy, setAcaoBusy] = useState<number | null>(null)
  // FEATURE (item #16, "juntar mais de uma OS do mesmo cliente no PDV pra passar
  // junto"): seleção múltipla, sem mexer no banco — cada OS ainda vira sua própria
  // venda/nota; só o pagamento é feito uma vez só (dividido proporcionalmente na
  // hora de finalizar, ver finalizarMultiplasOS no PDVPage).
  const [selecionadas, setSelecionadas] = useState<Record<number, any>>({})
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const qc = useQueryClient()
  const { data: lista = [], isLoading } = useQuery<any[]>({
    queryKey: ['os-para-faturar', q],
    queryFn: async () => (await api.get('/servicos/pdv/para-faturar', { params: q.trim() ? { q: q.trim() } : {} })).data,
  })

  function toggleSelecao(os: any) {
    setSelecionadas(prev => {
      if (prev[os.id]) {
        const { [os.id]: _rm, ...resto } = prev
        return resto
      }
      const outrasIds = Object.keys(prev)
      if (outrasIds.length > 0 && prev[Number(outrasIds[0])].cliente_id !== os.cliente_id) {
        toast.error('Só dá pra juntar OS do MESMO cliente numa cobrança só.')
        return prev
      }
      return { ...prev, [os.id]: os }
    })
  }
  const listaSelecionadas = Object.values(selecionadas)
  const totalSelecionado = listaSelecionadas.reduce((s: number, os: any) => s + Number(os.valor_total || 0), 0)

  async function cancelarOS(os: any) {
    if (!window.confirm(`Cancelar a OS #${os.numero}? Os itens têm o estoque estornado e a OS não poderá mais ser cobrada. Não dá pra desfazer.`)) return
    setAcaoBusy(os.id)
    try {
      await api.post(`/servicos/${os.id}/cancelar`)
      toast.success(`OS #${os.numero} cancelada`)
      qc.invalidateQueries({ queryKey: ['os-para-faturar'] })
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Não foi possível cancelar a OS')
    } finally { setAcaoBusy(null) }
  }

  async function excluirOS(os: any) {
    setAcaoBusy(os.id)
    try {
      await api.delete(`/servicos/${os.id}`)
      toast.success(`OS #${os.numero} excluída`)
      qc.invalidateQueries({ queryKey: ['os-para-faturar'] })
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Não foi possível excluir a OS')
    } finally { setAcaoBusy(null) }
  }

  useEffect(() => { inputRef.current?.focus() }, [])
  useEffect(() => { setIdx(0) }, [q, lista.length])
  // mantém o item destacado visível ao navegar com as setas
  useEffect(() => {
    listRef.current?.querySelector(`[data-idx="${idx}"]`)?.scrollIntoView({ block: 'nearest' })
  }, [idx])

  function onKey(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') { e.preventDefault(); setIdx(i => Math.min(i + 1, lista.length - 1)) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setIdx(i => Math.max(i - 1, 0)) }
    else if (e.key === 'Enter') { e.preventDefault(); if (lista[idx]?.faturavel !== false) onEscolher(lista[idx]) }
    // ESC fecha SÓ este modal e para de propagar — evita cair no handler global do
    // PDV e acabar saindo da tela (relatado 06/07). Com o input focado, este é o
    // único handler que roda; sem foco no input, o handler do PDV fecha via showServicosOS.
    else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onClose() }
  }

  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-ink-200">
          <h2 className="text-lg font-bold text-ink-900 flex items-center gap-2"><Wrench size={18} className="text-amber-600" /> Buscar OS / serviço</h2>
          <button onClick={onClose} className="text-ink-400 hover:text-ink-600"><X size={20} /></button>
        </div>
        <div className="px-4 pt-3">
          <div className="relative">
            <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-400" />
            <input ref={inputRef} value={q} onChange={e => setQ(e.target.value)} onKeyDown={onKey}
              placeholder="Nº da OS ou nome do cliente..."
              className="input pl-8 w-full" data-no-upper />
          </div>
          <div className="text-[10px] text-ink-400 mt-1">↑↓ navegar · Enter passar o serviço · Esc fechar</div>
        </div>
        <div className="p-4 overflow-y-auto" ref={listRef}>
          {isLoading ? (
            <div className="flex items-center justify-center py-10 text-ink-400"><Loader2 size={20} className="animate-spin mr-2" /> Carregando...</div>
          ) : lista.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-ink-400">
              <Wrench size={36} className="opacity-30 mb-2" />
              <p className="text-sm">{q.trim() ? 'Nenhuma OS encontrada' : 'Nenhuma OS concluída pra cobrar'}</p>
            </div>
          ) : (
            <div className="space-y-2">
              {lista.map((os: any, i: number) => {
                const faturavel = os.faturavel !== false  // backend manda false p/ não-cobráveis
                const podeCancelar = !['faturada', 'encerrada', 'cancelada'].includes(os.status)
                const busy = acaoBusy === os.id
                return (
                <div key={os.id} data-idx={i} onMouseEnter={() => setIdx(i)}
                  className={`flex items-center gap-2 rounded-xl p-3 transition-colors border ${
                    !faturavel ? 'bg-ink-50/60 border-transparent'
                    : selecionadas[os.id] ? 'bg-amber-100 border-amber-400 ring-1 ring-amber-400'
                    : i === idx ? 'bg-amber-50 border-amber-300 ring-1 ring-amber-300' : 'bg-ink-50 border-transparent hover:bg-amber-50'}`}>
                  {/* Checkbox — junta essa OS com outras do mesmo cliente numa cobrança só */}
                  {faturavel && (
                    <button onClick={(e) => { e.stopPropagation(); toggleSelecao(os) }}
                      title="Marcar pra cobrar junto com outras OS deste cliente"
                      className={`shrink-0 w-5 h-5 rounded border flex items-center justify-center ${
                        selecionadas[os.id] ? 'bg-amber-500 border-amber-500 text-white' : 'border-ink-300 hover:border-amber-400'}`}>
                      {selecionadas[os.id] && <Check size={13} />}
                    </button>
                  )}
                  {/* Área clicável — passa a OS pro carrinho sozinha (só se faturável) */}
                  <button disabled={!faturavel || busy} onClick={() => faturavel && onEscolher(os)}
                    className={`flex-1 flex items-center gap-3 text-left min-w-0 ${!faturavel ? 'opacity-70 cursor-not-allowed' : ''}`}>
                    <div className={`w-12 h-12 rounded-full font-bold flex items-center justify-center shrink-0 ${
                      faturavel ? 'bg-amber-100 text-amber-700' : 'bg-ink-200 text-ink-500'}`}>
                      #{os.numero}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-ink-900 truncate">{os.cliente_nome}</div>
                      <div className="text-xs text-ink-500 mt-0.5">
                        {os.itens?.length || 0} item(ns) · {os.status === 'entregue' ? 'Entregue' : os.status === 'concluida' ? 'Concluída' : os.status}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className={`font-bold ${faturavel ? 'text-emerald-700' : 'text-ink-400'}`}>{formatMoney(os.valor_total)}</div>
                      {faturavel
                        ? <div className="text-[10px] text-amber-600 font-medium">Cobrar →</div>
                        : <div className="text-[10px] text-rose-500 font-medium max-w-[120px] whitespace-normal leading-tight">{os.motivo}</div>}
                    </div>
                  </button>
                  {/* Ações — cancelar / excluir a OS */}
                  <div className="flex flex-col gap-1 shrink-0 border-l border-ink-200 pl-2">
                    {busy ? (
                      <div className="p-1.5"><Loader2 size={15} className="animate-spin text-ink-400" /></div>
                    ) : (<>
                      {podeCancelar && (
                        <button onClick={() => cancelarOS(os)} title="Cancelar OS (estorna estoque)"
                          className="p-1.5 rounded-lg text-amber-600 hover:bg-amber-100 transition-colors">
                          <Ban size={15} />
                        </button>
                      )}
                      <button onClick={() => pedirExclusao({ descricao: `a OS #${os.numero}`, onConfirm: () => excluirOS(os) })} title="Excluir OS (arquivar)"
                        className="p-1.5 rounded-lg text-rose-500 hover:bg-rose-100 transition-colors">
                        <Trash2 size={15} />
                      </button>
                    </>)}
                  </div>
                </div>
                )
              })}
                        </div>
          )}
        </div>
        {/* Barra de confirmação — aparece quando há OS marcadas pra cobrar juntas */}
        {listaSelecionadas.length > 0 && (
          <div className="border-t border-ink-200 p-3 flex items-center justify-between gap-3 bg-amber-50">
            <div className="text-sm">
              <span className="font-semibold text-amber-900">{listaSelecionadas.length} OS selecionada(s)</span>
              <span className="text-amber-700 ml-2">Total {formatMoney(totalSelecionado)}</span>
            </div>
            <button onClick={() => onEscolherMultiplas(listaSelecionadas)}
              className="px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold">
              Cobrar {listaSelecionadas.length > 1 ? 'as ' + listaSelecionadas.length + ' juntas' : 'esta OS'}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}


// ============ MODAL — VENDAS DO DIA / REIMPRIMIR CUPOM ============
function ModalVendasDoDia({ onClose, emissor, sessaoId }: { onClose: () => void; emissor?: any; sessaoId?: number | null }) {
  const filialDiaId = useFilial(s => s.filialAtual?.id ?? null)
  const qc = useQueryClient()
  const [cancelandoId, setCancelandoId] = useState<number | null>(null)
  // FEATURE 03/07: editar/histórico da venda (observação livre; cliente/vendedor c/ senha).
  const [editarVenda, setEditarVenda] = useState<{ id: number; aba: 'editar' | 'historico' } | null>(null)
  const [busca, setBusca] = useState('')
  const [filtro, setFiltro] = useState<'ativas' | 'canceladas' | 'todas'>('ativas')
  // FEATURE 30/06: modal único de confirmação (motivo + senha de cargo) reusado
  // pra cancelar venda E estornar movimento do caixa, direto no PDV.
  const [confirmar, setConfirmar] = useState<null | { titulo: string; aviso?: string; onOk: (motivo: string, senha: string) => Promise<void> }>(null)
  const { data: vendas = [], isLoading, refetch } = useQuery<any[]>({
    queryKey: ['pdv-vendas-do-dia', filialDiaId],
    queryFn: async () => (await api.get('/pdv/vendas-do-dia')).data,
  })
  // Movimentos do caixa da sessão aberta (entrada/saída/sangria/suprimento) p/ estornar.
  const { data: sessaoDet, refetch: refetchSessao } = useQuery<any>({
    queryKey: ['pdv-sessao-movimentos', sessaoId],
    queryFn: async () => (await api.get(`/caixa/sessoes/${sessaoId}`)).data,
    enabled: !!sessaoId,
  })
  const movimentos: any[] = sessaoDet?.movimentos ?? []

  function cancelarVenda(v: any) {
    setConfirmar({
      titulo: `Cancelar venda #${v.numero} (${formatMoney(v.total_liquido ?? v.total)})`,
      aviso: 'Estorna estoque e financeiro — inclusive o valor recebido (volta do caixa/banco). Não dá pra desfazer.',
      onOk: async (motivo, senha) => {
        setCancelandoId(v.pedido_id)
        try {
          await api.post(`/pdv/cancelar/${v.pedido_id}`, { motivo, senha })
          toast.success(`Venda #${v.numero} cancelada e estornada`)
          await refetch(); await refetchSessao()
          qc.invalidateQueries()  // saldos/caixa/estoque/vitrine refrescam
        } catch (e: any) {
          const detail = e?.response?.data?.detail
          if (typeof detail === 'string' && /cupom fiscal|NFC-e|NF-e/i.test(detail)) {
            toast(detail, { icon: '🧾', duration: 8000 })
            window.open(`/vendas?busca=${encodeURIComponent(String(v.numero))}`, '_blank')
          } else {
            toast.error(detail || 'Erro ao cancelar venda')
          }
          throw e  // mantém o modal aberto se falhou (ex.: senha errada)
        } finally {
          setCancelandoId(null)
        }
      },
    })
  }

  function estornarMovimento(m: any) {
    const tipoLbl = { sangria: 'Sangria', suprimento: 'Suprimento', saida: 'Saída', entrada: 'Entrada' }[m.tipo as string] || m.tipo
    setConfirmar({
      titulo: `Estornar ${tipoLbl} de ${formatMoney(m.valor)}`,
      aviso: 'O movimento sai do caixa (e do financeiro, se for entrada/saída).',
      onOk: async (motivo, senha) => {
        try {
          await api.post(`/caixa/sessoes/${sessaoId}/movimentos/${m.id}/estornar`, { motivo, senha })
          toast.success('Movimento estornado')
          await refetchSessao()
          qc.invalidateQueries()
        } catch (e: any) {
          toast.error(e?.response?.data?.detail || 'Falha ao estornar')
          throw e
        }
      },
    })
  }

  // FIX 30/06: reimpressão agora usa o MESMO PDF 80mm do backend (antes reconstruía
  // o cupom em HTML e saía em A4). Mesmo padrão do finalizar/Vendas/OS.
  async function reimprimir(pedidoId: number, tipo: 'cupom' | 'recibo') {
    await imprimirCupomPdf(pedidoId, tipo)
  }

  // FEATURE 09/07: comprovante 80mm (altura dinâmica) de um movimento de caixa
  // (sangria/suprimento/saída/entrada). Mesmo caminho de impressão do cupom
  // (GET .../comprovante.pdf → blob → imprimirPdfBlob). Serve p/ o cliente/gerente
  // assinar a sangria/saída (linha de "Assinatura / Conferência" no rodapé).
  async function imprimirComprovanteMov(m: any) {
    const t = toast.loading('Gerando comprovante...')
    try {
      const r = await api.get(`/caixa/sessoes/${sessaoId}/movimentos/${m.id}/comprovante.pdf`, { responseType: 'blob' })
      toast.dismiss(t)
      imprimirPdfBlob(r.data)
    } catch (e: any) {
      toast.dismiss(t)
      toast.error(e?.response?.data?.detail || 'Erro ao gerar comprovante')
    }
  }

  // Soma o LÍQUIDO (total − bike na troca). Fallback p/ total se o backend não mandar. (06/07)
  const totalDoDia = vendas.reduce((s, v) => s + (v.total_liquido ?? v.total ?? 0), 0)
  // FEATURE 30/06: busca + filtro pra caber mais e achar rápido.
  const q = busca.trim().toLowerCase()
  const vendasFiltradas = vendas.filter((v: any) => {
    const cancelada = v.status === 'cancelado'
    if (filtro === 'ativas' && cancelada) return false
    if (filtro === 'canceladas' && !cancelada) return false
    if (!q) return true
    return (v.cliente_nome || '').toLowerCase().includes(q) || String(v.numero).includes(q)
  })

  return (
    <div className="fixed inset-0 bg-ink-950/85 z-[60] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[88vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b border-ink-200">
          <div>
            <h3 className="text-base font-bold text-ink-900 flex items-center gap-2">
              <Receipt size={17} /> Vendas e movimentos do caixa
            </h3>
            <p className="text-[11px] text-ink-500">
              {vendas.length} venda{vendas.length !== 1 ? 's' : ''} · Total {formatMoney(totalDoDia)}
            </p>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={() => { refetch(); refetchSessao() }} className="p-2 rounded-lg hover:bg-ink-100 text-ink-500" title="Atualizar">
              <RotateCcw size={16} />
            </button>
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-ink-100 text-ink-500">
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Busca + filtro */}
        <div className="px-3 py-2 border-b border-ink-100 flex items-center gap-2 shrink-0">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-400" />
            <input value={busca} onChange={e => setBusca(e.target.value)} autoComplete="off"
              placeholder="Buscar por cliente ou nº da venda…"
              className="w-full pl-8 pr-2 py-1.5 text-sm rounded-lg border border-ink-200 focus:border-brand-500 focus:outline-none" />
          </div>
          <div className="flex items-center gap-0.5 bg-ink-100 rounded-lg p-0.5 text-xs">
            {([['ativas', 'Ativas'], ['canceladas', 'Canceladas'], ['todas', 'Todas']] as const).map(([k, lbl]) => (
              <button key={k} onClick={() => setFiltro(k)}
                className={`px-2.5 py-1 rounded-md font-medium ${filtro === k ? 'bg-white shadow text-ink-900' : 'text-ink-500'}`}>
                {lbl}
              </button>
            ))}
          </div>
        </div>

        <div className="overflow-auto px-3 py-2 flex-1">
          {isLoading ? (
            <div className="flex items-center justify-center py-10 text-ink-400">
              <Loader2 size={20} className="animate-spin mr-2" /> Carregando...
            </div>
          ) : vendasFiltradas.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-ink-400">
              <Receipt size={32} className="opacity-30 mb-2" />
              <p className="text-sm">{vendas.length === 0 ? 'Nenhuma venda registrada hoje' : 'Nenhuma venda nesse filtro/busca'}</p>
            </div>
          ) : (
            <div className="space-y-1">
              {vendasFiltradas.map((v: any) => {
                const cancelada = v.status === 'cancelado'
                return (
                <div key={v.pedido_id}
                  className={`rounded-lg transition-colors ${cancelada ? 'bg-rose-50' : 'bg-ink-50 hover:bg-ink-100'}`}>
                 <div className="flex items-center gap-2.5 px-2.5 py-1.5">
                  <span className={`text-[11px] font-bold px-1.5 py-0.5 rounded shrink-0 tabular-nums ${
                    cancelada ? 'bg-rose-100 text-rose-600' : 'bg-brand-50 text-brand-700'}`}>
                    #{v.numero}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm font-medium truncate ${cancelada ? 'text-rose-700' : 'text-ink-900'}`} title={v.cliente_nome}>
                      {v.cliente_nome}
                      {cancelada && <span className="ml-1.5 text-[9px] font-bold bg-rose-600 text-white px-1 py-0.5 rounded align-middle">CANCELADA</span>}
                    </div>
                    <div className="text-[11px] text-ink-500 leading-tight">
                      {new Date(v.criado_em).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                      {' · '}{v.qtd_itens}{v.qtd_itens === 1 ? ' item' : ' itens'}
                      {' · '}{(v.forma_pagamento || '').replace(/_/g, ' ')}
                      {v.bike > 0 && <span className="text-fuchsia-600"> · bike −{formatMoney(v.bike)}</span>}
                    </div>
                  </div>
                  {/* Mostra o LÍQUIDO (total − bike na troca); descontos de item/venda já vêm no total. (06/07) */}
                  <div className={`text-sm font-bold shrink-0 tabular-nums ${cancelada ? 'text-rose-500 line-through' : 'text-ink-900'}`}>
                    {formatMoney(v.total_liquido ?? v.total)}
                  </div>
                  {!cancelada && (
                    <div className="flex items-center gap-1 shrink-0">
                      {/* 03/07: removido "Reimprimir cupom" (era ~igual ao recibo) — só o recibo. */}
                      <button onClick={() => reimprimir(v.pedido_id, 'recibo')} title="Reimprimir recibo"
                        className="p-1.5 rounded-md bg-brand-600 hover:bg-brand-700 text-white"><Printer size={14} /></button>
                      <button onClick={() => setEditarVenda({ id: v.pedido_id, aba: 'editar' })} title="Editar venda (observação, cliente, vendedor)"
                        className="p-1.5 rounded-md bg-ink-200 hover:bg-ink-300 text-ink-700"><Pencil size={14} /></button>
                      <button onClick={() => setEditarVenda({ id: v.pedido_id, aba: 'historico' })} title="Histórico de edições"
                        className="p-1.5 rounded-md bg-ink-200 hover:bg-ink-300 text-ink-700"><History size={14} /></button>
                      {/* 03/07: botão "trocar cliente" removido — a troca de cliente (com senha)
                          agora é pelo lápis (Editar venda). */}
                      <button onClick={() => cancelarVenda(v)} disabled={cancelandoId === v.pedido_id} title="Cancelar venda (estorna estoque e financeiro)"
                        className="p-1.5 rounded-md bg-rose-100 hover:bg-rose-200 text-rose-700 disabled:opacity-50">
                        {cancelandoId === v.pedido_id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}</button>
                    </div>
                  )}
                 </div>
                </div>
                )
              })}
            </div>
          )}

          {/* FEATURE 30/06: movimentos do caixa (entrada/saída/sangria/suprimento) com estorno */}
          {sessaoId && movimentos.length > 0 && (
            <div className="mt-4">
              <div className="text-xs font-semibold text-ink-500 uppercase mb-2 px-1">Movimentos do caixa</div>
              <div className="space-y-1.5">
                {movimentos.map((m: any) => {
                  const saiu = m.tipo === 'sangria' || m.tipo === 'saida'
                  const tipoLbl = { sangria: 'Sangria', suprimento: 'Suprimento', saida: 'Saída', entrada: 'Entrada' }[m.tipo as string] || m.tipo
                  const estornado = !!m.estornado_em
                  return (
                    <div key={m.id} className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm ${estornado ? 'bg-rose-50 opacity-70' : 'bg-ink-50'}`}>
                      <span className="flex-1 min-w-0 truncate">
                        <span className="font-medium">{tipoLbl}</span>
                        <span className="text-ink-500"> · {m.descricao || '—'}</span>
                        {estornado && <span className="ml-1 text-[10px] font-bold bg-rose-600 text-white px-1 rounded">ESTORNADO</span>}
                      </span>
                      <span className={`font-semibold tabular-nums ${estornado ? 'line-through text-ink-400' : saiu ? 'text-amber-700' : 'text-sky-700'}`}>
                        {saiu ? '−' : '+'} {formatMoney(m.valor)}
                      </span>
                      {/* Comprovante 80mm p/ assinatura (impresso mesmo se estornado) */}
                      <button onClick={() => imprimirComprovanteMov(m)} title="Imprimir comprovante"
                        className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded border border-ink-200 text-ink-700 hover:bg-ink-100 shrink-0">
                        <Printer size={11} /> Comprovante
                      </button>
                      {!estornado && (
                        <button onClick={() => estornarMovimento(m)}
                          className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded border border-ink-200 text-rose-700 hover:bg-rose-50 shrink-0">
                          <RotateCcw size={11} /> Estornar
                        </button>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-ink-200 flex justify-end">
          <button onClick={onClose} className="px-4 py-2 rounded-lg bg-ink-200 hover:bg-ink-300 text-ink-800 text-sm font-medium">
            Fechar
          </button>
        </div>
      </div>

      {/* FEATURE 30/06: confirmação com MOTIVO + SENHA (de usuário com cargo) */}
      {confirmar && (
        <ConfirmEstornoModal
          titulo={confirmar.titulo}
          aviso={confirmar.aviso}
          onConfirm={confirmar.onOk}
          onClose={() => setConfirmar(null)}
        />
      )}

      {/* FEATURE 03/07: editar venda / histórico */}
      {editarVenda && (
        <EditarVendaModal
          pedidoId={editarVenda.id}
          abaInicial={editarVenda.aba}
          onClose={() => setEditarVenda(null)}
          onSaved={() => qc.invalidateQueries({ queryKey: ['pdv-vendas-do-dia'] })}
        />
      )}
    </div>
  )
}

// ============ MODAL — CONFIRMAÇÃO COM MOTIVO + SENHA (cancelar/estornar no PDV) ============
function ConfirmEstornoModal({ titulo, aviso, onConfirm, onClose }: {
  titulo: string; aviso?: string; onConfirm: (motivo: string, senha: string) => Promise<void>; onClose: () => void
}) {
  const [motivo, setMotivo] = useState('')
  const [senha, setSenha] = useState('')
  const [loading, setLoading] = useState(false)
  async function ok() {
    if (!motivo.trim() || !senha) return
    setLoading(true)
    try { await onConfirm(motivo.trim(), senha); onClose() }
    catch { /* erro já tratado no caller; mantém modal aberto */ }
    finally { setLoading(false) }
  }
  return (
    <div className="fixed inset-0 bg-ink-950/80 z-[80] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-ink-200 flex items-center justify-between">
          <h3 className="font-bold text-ink-900 flex items-center gap-2"><RotateCcw size={18} className="text-rose-600" /> {titulo}</h3>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-ink-100"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          {aviso && <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">{aviso}</div>}
          <div>
            <label className="text-xs text-ink-600">Motivo *</label>
            <textarea className="input w-full mt-1 text-sm" rows={2} autoFocus value={motivo}
              onChange={e => setMotivo(e.target.value)} placeholder="Ex.: lançado errado" />
          </div>
          <div>
            <label className="text-xs text-ink-600">Senha *</label>
            {/* type=text + text-security: não autofill da senha de login salva */}
            <input type="text" className="input w-full mt-1 text-sm" name="estorno-pdv-confirm"
              autoComplete="off" data-lpignore="true" data-1p-ignore data-form-type="other"
              style={{ WebkitTextSecurity: 'disc', textSecurity: 'disc' } as any}
              value={senha} onChange={e => setSenha(e.target.value)} placeholder="Senha de um usuário com cargo" />
            <p className="text-[11px] text-ink-500 mt-1">Exige cargo. Sem cargo, use a senha de um supervisor (admin, gerente ou financeiro).</p>
          </div>
        </div>
        <div className="px-5 py-3 border-t border-ink-200 flex justify-end gap-2">
          <button onClick={onClose} className="btn-secondary text-sm">Cancelar</button>
          <button onClick={ok} disabled={loading || !motivo.trim() || !senha}
            className="px-4 py-2 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold flex items-center gap-1.5 disabled:opacity-50">
            {loading ? <Loader2 size={15} className="animate-spin" /> : <RotateCcw size={15} />} Confirmar
          </button>
        </div>
      </div>
    </div>
  )
}

// ============ RESUMO DE VENDAS DA SESSAO (no modal de fechar caixa) ============
// UI 02/07: cores por forma agora do módulo compartilhado formaVisual (IDÊNTICAS às do
// Fechamento de Caixa, com par claro/ESCURO). `escuro` = tema do PDV; o modal que
// renderiza isto adiciona a classe app-dark quando escuro, então os tokens
// (bg-white/bg-ink-*/text-ink-*) remapeiam sozinhos e aqui só as cores INLINE por forma
// + o overlay da zebra dependem do tema.
function ResumoVendasSessao({ sessaoId, escuro }: { sessaoId: number; escuro: boolean }) {
  const { data, isLoading } = useQuery<any>({
    queryKey: ['sessao-detalhe', sessaoId],
    queryFn: async () => (await api.get(`/caixa/sessoes/${sessaoId}`)).data,
  })

  if (isLoading) {
    return (
      <div className="bg-ink-50 rounded-xl p-3 mb-3 text-xs text-ink-500 flex items-center gap-2">
        <Loader2 size={12} className="animate-spin" /> Carregando vendas da sessão...
      </div>
    )
  }

  const qtd = data?.vendas?.length ?? 0
  const total = Number(data?.vendas_total ?? data?.receitas_total ?? 0)
  const porForma: Record<string, number> = data?.vendas_por_forma ?? data?.receitas_por_forma ?? {}
  const vendas: any[] = data?.vendas ?? []
  // FEATURE 02/07 (pedido do usuário): bloco em destaque no topo do fechamento com o
  // valor TOTAL das vendas somado + composição do caixa. Tudo já vem do GET da sessão;
  // é só exibição — NÃO mexe na conferência da gaveta ("Esperado em caixa").
  //   Venda Principal = total das vendas (à vista no caixa + a prazo)
  //   Receb. Clientes = a_prazo_total (vendido a prazo/crediário → título do cliente)
  //   Mov. entrada/saída = entradas/saídas de valor da sessão
  const aVista = Number(data?.recebido_em_caixa ?? 0)   // vendas recebidas agora (dinheiro+pix+cartão)
  const aPrazo = Number(data?.a_prazo_total ?? 0)       // vendas a prazo (viram receb. de cliente)
  const movEntrada = Number(data?.entradas ?? 0)
  const movSaida = Number(data?.saidas ?? 0)

  // FEATURE 02/07: destaque do total das vendas + composição do caixa (Venda Principal,
  // Receb. Clientes, Mov. entrada/saída). Só informativo — não altera a conferência.
  // Fica numa const p/ renderizar TAMBÉM quando não há vendas (a sessão pode ter só
  // entradas/saídas). Antes o early-return de "nenhuma venda" escondia o bloco todo.
  const destaque = (
    <div className="rounded-xl border border-brand-200 bg-brand-50/50 p-4 mb-3">
      <div className="flex items-end justify-between gap-3">
        <span className="text-xs uppercase tracking-wide font-semibold text-brand-700/80 flex items-center gap-1.5">
          <Receipt size={14} /> Venda Principal
        </span>
        <span className="text-3xl font-extrabold text-brand-700 tabular-nums leading-none">{formatMoney(total)}</span>
      </div>
      <div className="text-[11px] text-ink-500 mt-0.5 text-right">
        {qtd} venda{qtd !== 1 ? 's' : ''} · à vista {formatMoney(aVista)} · a prazo {formatMoney(aPrazo)}
      </div>
      <div className="mt-3 pt-3 border-t border-brand-200/60 space-y-1.5 text-sm">
        <div className="flex justify-between">
          <span className="text-ink-600">Receb. Clientes <span className="text-[11px] text-ink-400">(a prazo)</span></span>
          <span className="font-semibold text-ink-900 tabular-nums">{formatMoney(aPrazo)}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-ink-600">Movimentação entrada</span>
          <span className="font-semibold text-emerald-600 tabular-nums">+ {formatMoney(movEntrada)}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-ink-600">Movimentação saída</span>
          <span className="font-semibold text-rose-600 tabular-nums">− {formatMoney(movSaida)}</span>
        </div>
      </div>
    </div>
  )

  if (qtd === 0) {
    return (
      <>
      {destaque}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-3 text-xs text-amber-800">
        Nenhuma venda registrada nesta sessão até agora.
      </div>
      </>
    )
  }

  return (
    <>
    {destaque}

    <div className="bg-ink-50 rounded-xl p-2.5 mb-2.5 text-sm">
      <div className="flex items-center justify-between mb-1.5">
        <span className="font-semibold text-ink-900 flex items-center gap-1.5">
          <Receipt size={14} /> Vendas da sessão
        </span>
        <span className="text-ink-500 text-xs">{qtd} venda{qtd !== 1 ? 's' : ''} · {formatMoney(total)}</span>
      </div>

      {Object.keys(porForma).length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-1.5">
          {Object.entries(porForma).map(([forma, valor]) => (
            <span key={forma} className="px-2 py-0.5 bg-white border border-ink-200 rounded-full text-[11px]">
              <span className="text-ink-600">{forma.replace(/_/g, ' ')}</span>{' '}
              <span className="font-medium text-ink-900">{formatMoney(Number(valor))}</span>
            </span>
          ))}
        </div>
      )}

      <details className="text-xs">
        <summary className="cursor-pointer text-brand-600 hover:text-brand-700">
          Ver detalhe por venda e forma ({qtd})
        </summary>
        {/* UI 02/07: matriz venda × forma (mesma do Fechamento de Caixa, versão enxuta)
            p/ o operador conferir DE ONDE veio cada real na hora de fechar — resolve o
            caso da venda dividida em que o dinheiro ficava invisível. Zebra + cor por
            forma pra traçar cliente→valor sem se perder. Scroll lateral se faltar largura. */}
        <div className="mt-2 max-h-56 overflow-auto bg-white rounded border border-ink-200">
          {(() => {
            const formasCols: string[] = Object.entries(porForma)
              .sort((a: any, b: any) => Number(b[1]) - Number(a[1]))
              .map(([f]) => f)
            const qtdForma: Record<string, number> = data?.receitas_qtd_por_forma ?? {}
            return (
              <table className="w-full text-[11px] whitespace-nowrap tabular-nums">
                <thead className="text-ink-500 border-b sticky top-0 bg-white">
                  <tr>
                    <th className="text-left py-1 px-1.5">Venda</th>
                    <th className="text-left py-1 pr-2">Cliente</th>
                    {formasCols.map((f) => {
                      const c = escuro ? formaVisual(f).dark : formaVisual(f).light
                      return (
                        <th key={f} className="text-right py-1 px-1.5 capitalize" style={{ color: c.text, backgroundColor: c.bg }}>
                          {f.replace(/_/g, ' ')}
                        </th>
                      )
                    })}
                    <th className="text-right py-1 pl-1.5">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-ink-100">
                  {vendas.map((v: any, idx: number) => {
                    // zebra: neutras via token bg; colunas de forma via overlay por cima da cor
                    const zebra = idx % 2 === 1
                    const neutra = zebra ? 'bg-ink-100' : 'bg-white'
                    // FIX 02/07: overlay SEMPRE escurece (preto) — nas colunas neutras a linha
                    // alternada (bg-ink-100) já é mais escura em ambos os temas; então a coluna
                    // de forma tem que escurecer também, senão no dark ficava invertido (nome
                    // escuro, valor claro na mesma linha). No dark precisa de mais alpha (a cor
                    // da forma já é escura) p/ o degrau ficar visível igual ao das neutras.
                    const overlay = zebra
                      ? (escuro ? 'linear-gradient(rgba(0,0,0,0.28),rgba(0,0,0,0.28))'
                                : 'linear-gradient(rgba(0,0,0,0.09),rgba(0,0,0,0.09))')
                      : undefined
                    return (
                      <tr key={v.pedido_id}>
                        <td className={`py-1 px-1.5 font-medium ${neutra}`}>
                          #{v.numero}
                          {v.dividida && <span className="ml-1 text-[9px] font-semibold text-amber-700 bg-amber-100 px-1 rounded">div.</span>}
                        </td>
                        <td className={`py-1 pr-2 text-ink-600 max-w-[120px] truncate ${neutra}`} title={v.cliente_nome}>{v.cliente_nome}</td>
                        {formasCols.map((f) => {
                          const val = v.formas_valores?.[f]
                          const c = escuro ? formaVisual(f).dark : formaVisual(f).light
                          return (
                            <td key={f} className={`py-1 px-1.5 text-right ${val ? 'font-medium' : 'text-ink-300'}`} style={{ backgroundColor: c.bg, backgroundImage: overlay }}>
                              {val ? formatMoney(val) : '—'}
                            </td>
                          )
                        })}
                        <td className={`py-1 pl-1.5 text-right font-semibold ${neutra}`}>{formatMoney(v.total)}</td>
                      </tr>
                    )
                  })}
                </tbody>
                <tfoot className="border-t border-ink-200">
                  <tr>
                    <td className="py-1 px-1.5 font-semibold bg-white">TOTAL</td>
                    <td className="py-1 pr-2 text-ink-500 bg-white">{qtd} venda(s)</td>
                    {formasCols.map((f) => {
                      const c = escuro ? formaVisual(f).dark : formaVisual(f).light
                      return (
                        <td key={f} className="py-1 px-1.5 text-right align-bottom" style={{ backgroundColor: c.bg }}>
                          <div className="font-bold text-brand-700">{formatMoney(Number(porForma[f] || 0))}</div>
                          <div className="text-[9px] text-ink-400 font-normal">{qtdForma[f] ?? 0}×</div>
                        </td>
                      )
                    })}
                    <td className="py-1 pl-1.5 text-right font-bold text-brand-700 bg-white">{formatMoney(total)}</td>
                  </tr>
                </tfoot>
              </table>
            )
          })()}
        </div>
      </details>
    </div>
    </>
  )
}

// ============ MODAL — SELETOR DE FILIAL / ESTOQUE ============
function ModalSeletorFilial({ filiais, depositos, itensCarrinho, filialAtual, depositoAtual, onConfirmar, onClose, confirmLabel }: {
  filiais: any[]
  depositos: any[]
  itensCarrinho: { produto_id: number; quantidade: number }[]
  filialAtual: number | null
  depositoAtual: number | null
  onConfirmar: (filialId: number, depositoId: number) => void
  onClose: () => void
  confirmLabel?: string
}) {
  const [filialId, setFilialId] = useState<number | null>(filialAtual)
  const [depositoId, setDepositoId] = useState<number | null>(depositoAtual)

  // Estoque dos itens do carrinho por depósito (só busca quando há carrinho)
  const { data: estoqueData } = useQuery<any>({
    queryKey: ['saldos-multi', itensCarrinho.map(i => `${i.produto_id}:${i.quantidade}`).join(',')],
    queryFn: async () => (await api.post('/estoque/saldos-multi', { itens: itensCarrinho })).data,
    enabled: itensCarrinho.length > 0,
  })
  const estoquePorDeposito: Record<number, any> = {}
  for (const d of (estoqueData?.por_deposito || [])) {
    estoquePorDeposito[d.deposito_id] = d
  }

  // Depósito de saída pode ser de QUALQUER filial (a mercadoria sai de onde tiver
  // estoque). Mostra todos os depósitos de venda, independente da filial escolhida.
  const depositosDaFilial = filialId == null ? [] : depositos

  // Resumo de estoque por filial (agrega todos os depósitos da filial)
  function resumoFilial(fid: number) {
    if (itensCarrinho.length === 0) return null
    const deps = depositos.filter(d => d.filial_id === fid || d.filial_id == null)
    if (deps.length === 0) return null
    // Pra cada produto, soma estoque de todos os depósitos da filial
    let okCount = 0
    let faltandoCount = 0
    for (const it of itensCarrinho) {
      let totalDisp = 0
      for (const d of deps) {
        const dEst = estoquePorDeposito[d.id]
        const item = dEst?.itens?.find((x: any) => x.produto_id === it.produto_id)
        if (item) totalDisp += item.qtd_disponivel
      }
      if (totalDisp >= it.quantidade) okCount++
      else faltandoCount++
    }
    return { okCount, faltandoCount, total: itensCarrinho.length }
  }

  // Auto-seleciona o 1º depósito quando troca de filial (ou se atual sumiu)
  useEffect(() => {
    if (filialId == null) return
    const aindaValido = depositoId != null && depositosDaFilial.some(d => d.id === depositoId)
    if (!aindaValido && depositosDaFilial.length > 0) {
      setDepositoId(depositosDaFilial[0].id)
    }
  }, [filialId, depositosDaFilial.length])

  const podeConfirmar = filialId != null && depositoId != null

  return (
    <div className="fixed inset-0 bg-black/70 z-[70] flex items-center justify-center p-4"
      onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-2 px-5 pt-4 pb-3 border-b border-ink-100 shrink-0">
          <div>
            <h3 className="text-base font-bold text-ink-900 flex items-center gap-2">
              <Warehouse size={18} className="text-brand-600" /> De qual filial sai a venda?
            </h3>
            <p className="text-[11px] text-ink-500 mt-0.5">
              Cupom = dados da filial; estoque baixa do depósito escolhido.
            </p>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-ink-100 text-ink-500 shrink-0">
            <X size={18} />
          </button>
        </div>

        {/* Corpo rolável (filiais + depósito) — cabe na tela com rodapé fixo */}
        <div className="flex-1 overflow-y-auto px-5 py-3 min-h-0">
        {/* Cards de filiais */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
          {filiais.map(f => {
            const selecionado = filialId === f.id
            const soFiscal = !!f.somente_fiscal  // 30/06: filial só emite nota (sem estoque)
            const resumo = soFiscal ? null : resumoFilial(f.id)
            const todoOk = resumo && resumo.faltandoCount === 0
            const algumFaltando = resumo && resumo.faltandoCount > 0
            return (
              <button key={f.id}
                onClick={() => setFilialId(f.id)}
                className={`text-left p-2 rounded-lg border-2 transition-all
                  ${selecionado
                    ? 'border-brand-500 bg-brand-50'
                    : 'border-ink-200 hover:border-ink-300 hover:bg-ink-50'}`}>
                <div className="flex items-start justify-between mb-0.5 gap-2">
                  <span className="font-semibold text-ink-900 text-sm">{f.nome_fantasia || f.razao_social}</span>
                  <div className="flex items-center gap-1 shrink-0">
                    {f.is_matriz && <span className="text-[9px] font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">MATRIZ</span>}
                    {soFiscal && <span className="text-[9px] font-bold text-brand-700 bg-brand-100 px-1.5 py-0.5 rounded">SÓ FISCAL</span>}
                    {resumo && (
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                        todoOk ? 'bg-emerald-100 text-emerald-700' :
                        algumFaltando && resumo.okCount === 0 ? 'bg-rose-100 text-rose-700' :
                        'bg-amber-100 text-amber-700'
                      }`}>
                        {resumo.okCount}/{resumo.total}
                      </span>
                    )}
                  </div>
                </div>
                {f.cnpj && <div className="text-[11px] text-ink-500 font-mono">CNPJ: {f.cnpj}</div>}
                {f.cidade && <div className="text-[11px] text-ink-600">{f.cidade}/{f.uf}</div>}
                {soFiscal && <div className="text-[11px] text-brand-600 mt-1">Só emite nota — estoque sai do depósito escolhido</div>}
                {resumo && resumo.faltandoCount > 0 && (
                  <div className="text-[11px] text-rose-600 mt-1 flex items-center gap-1">
                    <AlertTriangle size={10} /> {resumo.faltandoCount} item{resumo.faltandoCount !== 1 ? 's' : ''} sem estoque
                  </div>
                )}
              </button>
            )
          })}
        </div>

        {/* Seletor de depósito */}
        {filialId != null && (
          <div className="mb-4">
            <label className="text-xs font-medium text-ink-700 uppercase tracking-wide flex items-center gap-1 mb-1.5">
              <Package size={12} /> Depósito de saída
            </label>
            {depositosDaFilial.length === 0 ? (
              <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                Nenhum depósito de venda configurado pra esta filial. Cadastre um em Estoque &gt; Depósitos.
              </div>
            ) : (
              <div className="space-y-1.5">
                {depositosDaFilial.map(d => {
                  const est = estoquePorDeposito[d.id]
                  const todoOk = est && est.qtd_faltando === 0
                  const nadaOk = est && est.qtd_ok === 0
                  const selecionado = depositoId === d.id
                  return (
                    <label key={d.id}
                      className={`flex items-start gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors
                        ${selecionado ? 'border-brand-500 bg-brand-50' : 'border-ink-200 hover:bg-ink-50'}`}>
                      <input type="radio" checked={selecionado}
                        onChange={() => setDepositoId(d.id)} className="accent-brand-600 mt-1" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-medium">{d.nome}</span>
                          <span className="text-xs text-ink-500">({d.codigo})</span>
                          {d.tipo === 'loja' && <span className="text-[9px] font-bold text-emerald-700 bg-emerald-100 px-1 rounded">LOJA</span>}
                          {est && (
                            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ml-auto ${
                              todoOk ? 'bg-emerald-100 text-emerald-700' :
                              nadaOk ? 'bg-rose-100 text-rose-700' :
                              'bg-amber-100 text-amber-700'
                            }`}>
                              {est.qtd_ok}/{est.total_itens} com estoque
                            </span>
                          )}
                        </div>
                        {est && est.itens && est.itens.length > 0 && (
                          <div className="mt-1.5 space-y-0.5">
                            {est.itens.map((it: any) => (
                              <div key={it.produto_id}
                                className={`text-[11px] flex items-center justify-between ${it.ok ? 'text-ink-600' : 'text-rose-600'}`}>
                                <span className="truncate flex-1 mr-2">{it.descricao}</span>
                                <span className="font-mono shrink-0">
                                  {it.qtd_disponivel}/{it.qtd_pedida}
                                  {!it.ok && ' ⚠'}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </label>
                  )
                })}
              </div>
            )}
          </div>
        )}
        </div>{/* fim corpo rolável */}

        <div className="grid grid-cols-2 gap-2 px-5 py-3 border-t border-ink-200 shrink-0">
          <button onClick={onClose}
            className="py-2.5 rounded-lg bg-ink-100 hover:bg-ink-200 text-ink-700 font-medium">
            Cancelar
          </button>
          <button onClick={() => podeConfirmar && onConfirmar(filialId!, depositoId!)}
            disabled={!podeConfirmar}
            className={`py-2.5 rounded-lg font-semibold flex items-center justify-center gap-2
              ${podeConfirmar ? 'bg-brand-600 hover:bg-brand-700 text-white' : 'bg-ink-100 text-ink-400 cursor-not-allowed'}`}>
            <Check size={16} /> {confirmLabel || 'Confirmar'}
          </button>
        </div>
      </div>
    </div>
  )
}
